import { MAIL_PLACEHOLDERS, addDays, datevEurAmounts, checkSmallBusinessLimit, csvCellGuard, ownInvoiceRevenueAll, smallBusinessBasis, smallBusinessRule, smallBusinessTurnover, dealModelName, isQrIban, isSwissIban, isValidBic, isValidIban, isValidQrReferencePrefix, parseAmountToCents, renderMailText, type MailPlaceholder, type OwnInvoiceRow, type OwnRevenue, type SelfBillingEInvoiceSuggestion } from "@talentkontor/domain";
import { normalizeHex } from "../../../lib/color";
import { decimalSeparator, money } from "../../../lib/format";
import { taxIdWord, taxModeText } from "../../../model/steuerbegriffe";
import type { SeiteKey } from "./baum";

/**
 * Reine Funktionen der Seitengruppe Einstellungen, Einrichtung und Konto: Beschriftungen, Umrechnungen,
 * Prüfungen der Eingaben, Farbkontrast, CSV. Keine Datenzugriffe, damit alles ohne Browser testbar bleibt.
 */

// ── Navigation und Schritte ────────────────────────────────────────────────────

export const SETTINGS_SECTIONS = [
  { key: "firma", get label() { return t("Firma", "Company"); } },
  { key: "branding", label: "Branding" },
  { key: "konditionen", get label() { return t("Konditionen", "Terms"); } },
  { key: "mahnleiter", get label() { return t("Mahnleiter", "Dunning levels"); } },
  { key: "vorlagen", get label() { return t("Vorlagen", "Templates"); } },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "team", get label() { return t("Team & Rechte", "Team & permissions"); } },
  { key: "abo", get label() { return t("Abo & Tarif", "Subscription & plan"); } },
  { key: "export", label: "Export" },
] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number]["key"];

export function sectionLabel(key: SettingsSection): string {
  return SETTINGS_SECTIONS.find((s) => s.key === key)?.label ?? key;
}

/** Wege der persönlichen Seiten (IA-1: Baum und Karten in baum.ts) */
export const SICHERHEIT_PFAD = "/agentur/einstellungen/sicherheit";
export const PROFIL_PFAD = "/agentur/profil";

export const ONBOARDING_STEPS = [
  { key: "registrierung", get label() { return t("Konto anlegen", "Create account"); } },
  { key: "firma", get label() { return t("Firmendaten", "Company details"); } },
  { key: "steuer", get label() { return t("Bank und Steuer", "Bank and tax"); } },
  { key: "branding", label: "Branding" },
  { key: "konditionen", get label() { return t("Konditionen", "Terms"); } },
  { key: "vorlagen", get label() { return t("Vorlagen", "Templates"); } },
  { key: "creator", get label() { return t("Creator einladen", "Invite creator"); } },
] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number]["key"];

export function isOnboardingStep(v: string | undefined): v is OnboardingStep {
  return ONBOARDING_STEPS.some((s) => s.key === v);
}
export function stepIndex(step: OnboardingStep): number {
  return ONBOARDING_STEPS.findIndex((s) => s.key === step);
}
export function nextStep(step: OnboardingStep, creator = false): OnboardingStep | null {
  const list = onboardingSteps(creator);
  const i = list.findIndex((s) => s.key === step);
  return i < 0 ? null : list[i + 1]?.key ?? null;
}
/** Creator-Konto (0178): Konto, Firmendaten, Bank und Branding; Konditionen, Vorlagen und Creator-Einladung entfallen */
const CREATOR_STEP_KEYS: readonly OnboardingStep[] = ["registrierung", "firma", "steuer", "branding"];
export function onboardingSteps(creator = false): readonly (typeof ONBOARDING_STEPS)[number][] {
  return creator ? ONBOARDING_STEPS.filter((s) => CREATOR_STEP_KEYS.includes(s.key)) : ONBOARDING_STEPS;
}

// ── Einrichtungsstand (org_setup_status) ──────────────────────────────────────

export interface SetupItem {
  step: string;
  key: string;
}
export interface SetupStatus {
  complete: boolean;
  missing: SetupItem[];
}

/** Klartext je Lücke aus org_setup_status, dazu die Einstellungsseite und der Einrichtungsschritt, die sie schließen. */
export const SETUP_ITEMS: Record<string, { label: string; section: SettingsSection; step: OnboardingStep; seite?: SeiteKey }> = {
  "org_settings.legal_name": { get label() { return t("Firmenname", "Company name"); }, section: "firma", step: "firma" },
  "org_settings.address": { get label() { return t("Anschrift", "Address"); }, section: "firma", step: "firma" },
  "org_settings.country": { get label() { return t("Land", "Country"); }, section: "firma", step: "firma" },
  "org_legal.legal_form": { get label() { return t("Rechtsform", "Legal form"); }, section: "firma", step: "firma" },
  "org_legal.representative_name": { get label() { return t("Vertretungsberechtigte Person", "Authorised representative"); }, section: "firma", step: "firma" },
  "org_legal.representative_position": { get label() { return t("Funktion der vertretungsberechtigten Person", "Position of the authorised representative"); }, section: "firma", step: "firma" },
  "org_legal.phone": { get label() { return t("Telefon", "Phone"); }, section: "firma", step: "firma" },
  "org_legal.register": { get label() { return t("Registernummer und Registergericht", "Register number and register court"); }, section: "firma", step: "firma" },
  "org_legal.governing_law": { get label() { return t("Anwendbares Recht", "Governing law"); }, section: "firma", step: "firma" },
  "org_legal.jurisdiction": { get label() { return t("Gerichtsstand", "Place of jurisdiction"); }, section: "firma", step: "firma" },
  "org_settings.vat_id_or_tax_number": { get label() { return t("USt-IdNr. oder Steuernummer", "VAT ID or tax number"); }, section: "firma", step: "firma" },
  // K-302: Schweiz/Liechtenstein, MWST-pflichtig gewählt: die MWST-Nummer (sonst „Nicht mehrwertsteuerpflichtig“ wählen)
  "org_settings.vat_id_ch": { get label() { return t("MWST-Nummer (CHE-… MWST) oder „Nicht mehrwertsteuerpflichtig“", "VAT number (CHE-… MWST) or “Not subject to VAT”"); }, section: "firma", step: "firma" },
  // 0262 (K-249): Österreich, regelbesteuert: die UID ist Pflicht (ab 400 € je Rechnung), die Steuernummer ersetzt sie nicht
  "org_settings.vat_id_at": { get label() { return t("UID-Nummer (Pflicht auf Rechnungen über 400 €)", "UID number (required on invoices above €400)"); }, section: "firma", step: "firma" },
  "org_bank_accounts.default": { get label() { return t("Standard-Bankkonto in der Hauswährung", "Default bank account in the home currency"); }, section: "firma", step: "steuer" },
  "org_settings.default_document_language": { get label() { return t("Sprache der Belege", "Document language"); }, section: "branding", step: "branding", seite: "region" },
  "org_settings.sender_name": { get label() { return t("Absendername", "Sender name"); }, section: "branding", step: "branding", seite: "mailversand" },
  "org_settings.reply_to_email": { get label() { return t("Antwortadresse", "Reply address"); }, section: "branding", step: "branding", seite: "mailversand" },
  "org_settings.default_commission_bp": { get label() { return t("Standardprovision", "Default commission"); }, section: "konditionen", step: "konditionen", seite: "provision" },
  "org_settings.agency_fee_mode": { get label() { return t("Agenturgebühr", "Agency fee"); }, section: "konditionen", step: "konditionen", seite: "provision" },
  "org_settings.payment_terms_days": { get label() { return t("Zahlungsziel", "Payment term"); }, section: "konditionen", step: "konditionen", seite: "zahlungsziele" },
  "org_settings.invoice_route": { get label() { return t("Abrechnungsweg", "Invoicing route"); }, section: "konditionen", step: "konditionen", seite: "abrechnungsweg" },
  "org_settings.invoice_number_prefix": { get label() { return t("Präfix der Rechnungsnummer", "Invoice number prefix"); }, section: "konditionen", step: "konditionen", seite: "nummernkreise" },
  dunning_steps: { get label() { return t("Mahnleiter mit mindestens einer Stufe", "Dunning levels with at least one level"); }, section: "mahnleiter", step: "konditionen" },
  dunning_base_rates: { get label() { return t("Basiszins für Verzugszinsen", "Base rate for default interest"); }, section: "mahnleiter", step: "konditionen" },
};

export function setupLabel(key: string): string {
  return SETUP_ITEMS[key]?.label ?? key;
}
export function missingForSection(status: SetupStatus | null | undefined, section: SettingsSection): string[] {
  return (status?.missing ?? []).filter((m) => SETUP_ITEMS[m.key]?.section === section).map((m) => setupLabel(m.key));
}
/** IA-1: offene Pflichtangaben je Seite von „Alle Einstellungen“ (ohne eigene Zuordnung: Seite = Bereich) */
export function missingForSeite(status: SetupStatus | null | undefined, seite: SeiteKey): string[] {
  return (status?.missing ?? []).filter((m) => {
    const item = SETUP_ITEMS[m.key];
    return !!item && (item.seite ?? item.section) === seite;
  }).map((m) => setupLabel(m.key));
}
export function missingForStep(status: SetupStatus | null | undefined, step: OnboardingStep): string[] {
  return (status?.missing ?? []).filter((m) => SETUP_ITEMS[m.key]?.step === step).map((m) => setupLabel(m.key));
}
/** Erster Einrichtungsschritt mit offener Pflichtangabe; alles erledigt: null. */
export function firstOpenStep(status: SetupStatus | null | undefined, creator = false): OnboardingStep | null {
  for (const s of onboardingSteps(creator)) if (missingForStep(status, s.key).length) return s.key;
  return null;
}
export function listText(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} ${t("und", "and")} ${items[items.length - 1]}`;
}

// ── Zahlen, Prozente, Beträge ──────────────────────────────────────────────────

/** Basispunkte → Eingabetext in Prozent („3000“ → „30“, „1250“ → „12,5“). */
export function bpToPercentText(bp: number | null | undefined): string {
  if (bp === null || bp === undefined) return "";
  return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2, useGrouping: false }).format(bp / 100);
}
/** Cent → Eingabetext („4000“ → „40,00“). */
export function centsToText(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "";
  const neg = cents < 0;
  const abs = Math.abs(Math.round(cents));
  return `${neg ? "-" : ""}${Math.floor(abs / 100)},${String(abs % 100).padStart(2, "0")}`;
}
export function intToText(v: number | null | undefined): string {
  return v === null || v === undefined ? "" : String(v);
}
/**
 * Betrag für CSV ohne Tausendertrenner mit dem Dezimalzeichen der Oberfläche (1234,56; in der Schweiz 1234.56), wie die
 * übrigen CSV-Exporte (SAAS-R3-3); sonst liest die Tabellenkalkulation den Betrag als Text.
 */
export function centsToDecimal(cents: number | null | undefined): string {
  return cents === null || cents === undefined ? "" : centsToText(cents).replace(",", decimalSeparator());
}

/**
 * Sammelt Prüfergebnisse eines Formulars. Jede Methode liefert den umgewandelten Wert oder null und merkt sich
 * die Fehlermeldung je Feld. Leere optionale Felder ergeben null (in der Datenbank: Plattform-Standard bzw. leer).
 */
/** Häufige Vertipper bei Mail-Anbietern → richtige Domain (Registrierung, K-185) */
export const MAIL_VERTIPPER: Record<string, string> = {
  "gmial.com": "gmail.com", "gmal.com": "gmail.com", "gmai.com": "gmail.com", "gmail.con": "gmail.com", "gmail.co": "gmail.com", "gmail.cm": "gmail.com",
  "gamil.com": "gmail.com", "gnail.com": "gmail.com", "googlemail.con": "googlemail.com",
  "yahool.de": "yahoo.de", "yaho.de": "yahoo.de", "yahoo.dee": "yahoo.de", "yahoo.con": "yahoo.com", "yaho.com": "yahoo.com",
  "hotmial.com": "hotmail.com", "hotmail.con": "hotmail.com", "hotmai.com": "hotmail.com", "outlok.com": "outlook.com", "outlook.con": "outlook.com",
  "gmx.dee": "gmx.de", "gmx.con": "gmx.com", "web.dee": "web.de", "wbe.de": "web.de", "t-onlin.de": "t-online.de", "icloud.con": "icloud.com", "iclod.com": "icloud.com",
};

export class Check {
  readonly errors: Record<string, string> = {};
  get ok(): boolean {
    return Object.keys(this.errors).length === 0;
  }
  fail(field: string, message: string): null {
    if (!this.errors[field]) this.errors[field] = message;
    return null;
  }
  text(field: string, raw: string | null | undefined, o: { label: string; required?: boolean; max?: number; min?: number; pattern?: RegExp; patternText?: string }): string | null {
    const v = (raw ?? "").trim();
    if (!v) return o.required ? this.fail(field, t(`Bitte ${o.label} angeben.`, `Please enter ${o.label}.`)) : null;
    if (o.min !== undefined && v.length < o.min) return this.fail(field, t(`${o.label}: mindestens ${o.min} Zeichen.`, `${o.label}: at least ${o.min} characters.`));
    if (o.max !== undefined && v.length > o.max) return this.fail(field, t(`${o.label}: höchstens ${o.max} Zeichen.`, `${o.label}: at most ${o.max} characters.`));
    if (o.pattern && !o.pattern.test(v)) return this.fail(field, o.patternText ?? t(`${o.label} hat ein ungültiges Format.`, `${o.label} has an invalid format.`));
    return v;
  }
  email(field: string, raw: string | null | undefined, o: { label: string; required?: boolean }): string | null {
    // 14.10.2026 (Registrierung abgelehnt mit „invalid format“): unsichtbare Zeichen (geschütztes oder schmales Leerzeichen,
    // Null-Breite) und ähnlich aussehende Zeichen (Vollbreiten-„＋“, „＠“) aus Kopieren oder Autokorrektur bereinigen
    // Praxistest K-185: ein Punkt am Ende („gmail.com.“) wird still entfernt
    const bereinigt = typeof raw === "string" ? raw.normalize("NFKC").replace(/[\u00a0\u2000-\u200f\u2028\u2029\u202f\u205f\u2060\ufeff]/g, "").trim().replace(/\.+$/, "") : raw;
    const v = this.text(field, bereinigt, { label: o.label, required: o.required ?? false, max: 254 });
    if (v === null) return null;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) return this.fail(field, t(`${o.label}: bitte eine gültige E-Mail-Adresse eingeben.`, `${o.label}: please enter a valid email address.`));
    // Mailserver und der Anmeldedienst nehmen nur Adressen ohne Umlaute und Sonderzeichen an
    if (!/^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/.test(v)) {
      return this.fail(field, t(`${o.label}: bitte ohne Umlaute, Leer- oder Sonderzeichen eingeben (am besten von Hand tippen statt einfügen).`,
        `${o.label}: please enter it without umlauts, spaces or special characters (best typed by hand rather than pasted).`));
    }
    // Häufige Vertipper bei großen Anbietern (Lucy 12.10.2026: „yahool.de“): mit Vorschlag statt stiller Annahme
    const domain = v.toLowerCase().split("@")[1] ?? "";
    const richtig = MAIL_VERTIPPER[domain];
    if (richtig) {
      const vorschlag = `${v.toLowerCase().split("@")[0]}@${richtig}`;
      return this.fail(field, t(`${o.label}: meintest du ${vorschlag}? Bitte die Adresse prüfen.`, `${o.label}: did you mean ${vorschlag}? Please check the address.`));
    }
    return v.toLowerCase();
  }
  int(field: string, raw: string | null | undefined, o: { label: string; required?: boolean; min: number; max: number }): number | null {
    const v = (raw ?? "").trim();
    if (!v) return o.required ? this.fail(field, t(`Bitte ${o.label} angeben.`, `Please enter ${o.label}.`)) : null;
    if (!/^-?\d+$/.test(v)) return this.fail(field, t(`${o.label}: bitte eine ganze Zahl eingeben.`, `${o.label}: please enter a whole number.`));
    const n = Number(v);
    if (n < o.min || n > o.max) return this.fail(field, t(`${o.label}: bitte einen Wert zwischen ${o.min} und ${o.max} eingeben.`, `${o.label}: please enter a value between ${o.min} and ${o.max}.`));
    return n;
  }
  /** Prozent mit höchstens zwei Nachkommastellen → Basispunkte (30 → 3000). */
  percentBp(field: string, raw: string | null | undefined, o: { label: string; required?: boolean; max?: number }): number | null {
    const v = (raw ?? "").trim().replace(/\s|%/g, "");
    if (!v) return o.required ? this.fail(field, t(`Bitte ${o.label} angeben.`, `Please enter ${o.label}.`)) : null;
    if (!/^\d+([.,]\d+)?$/.test(v)) return this.fail(field, t(`${o.label}: bitte eine Zahl wie 30 oder 12,5 eingeben.`, `${o.label}: please enter a number such as 30 or 12.5.`));
    const n = Number(v.replace(",", "."));
    const bp = Math.round(n * 100);
    if (Math.abs(n * 100 - bp) > 1e-6) return this.fail(field, t(`${o.label}: höchstens zwei Nachkommastellen.`, `${o.label}: at most two decimal places.`));
    const max = o.max ?? 100;
    if (bp < 0 || bp > max * 100) return this.fail(field, t(`${o.label}: bitte einen Wert zwischen 0 und ${max} eingeben.`, `${o.label}: please enter a value between 0 and ${max}.`));
    return bp;
  }
  /** Dezimalzahl mit höchstens zwei Nachkommastellen (numeric(5,2)). */
  decimal(field: string, raw: string | null | undefined, o: { label: string; required?: boolean; min: number; max: number }): number | null {
    const v = (raw ?? "").trim().replace(/\s|%/g, "");
    if (!v) return o.required ? this.fail(field, t(`Bitte ${o.label} angeben.`, `Please enter ${o.label}.`)) : null;
    if (!/^\d+([.,]\d{1,2})?$/.test(v)) return this.fail(field, t(`${o.label}: bitte eine Zahl mit höchstens zwei Nachkommastellen eingeben.`, `${o.label}: please enter a number with at most two decimal places.`));
    const n = Number(v.replace(",", "."));
    if (n < o.min || n > o.max) return this.fail(field, t(`${o.label}: bitte einen Wert zwischen ${o.min} und ${o.max} eingeben.`, `${o.label}: please enter a value between ${o.min} and ${o.max}.`));
    return n;
  }
  /** Geldbetrag → Cent (versteht 1.234,50 und 1234.5), nie negativ. */
  cents(field: string, raw: string | null | undefined, o: { label: string; required?: boolean }): number | null {
    const v = (raw ?? "").trim();
    if (!v) return o.required ? this.fail(field, t(`Bitte ${o.label} angeben.`, `Please enter ${o.label}.`)) : null;
    let c: number;
    try {
      c = parseAmountToCents(v);
    } catch {
      return this.fail(field, t(`${o.label}: bitte einen Betrag wie 40,00 eingeben.`, `${o.label}: please enter an amount such as 40.00.`));
    }
    if (c < 0) return this.fail(field, t(`${o.label}: der Betrag darf nicht negativ sein.`, `${o.label}: the amount must not be negative.`));
    return c;
  }
  date(field: string, raw: string | null | undefined, o: { label: string; required?: boolean }): string | null {
    const v = (raw ?? "").trim();
    if (!v) return o.required ? this.fail(field, t(`Bitte ${o.label} angeben.`, `Please enter ${o.label}.`)) : null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v) || Number.isNaN(Date.parse(`${v}T00:00:00Z`))) return this.fail(field, t(`${o.label}: bitte ein Datum wählen.`, `${o.label}: please choose a date.`));
    return v;
  }
}

// ── Postgres-Listen ─────────────────────────────────────────────────────────────

/** Liste aus der Datenbank: PostgREST liefert JSON-Listen, der Demo-Anschluss bei Enum-Listen den Text „{a,b}“. */
export function parsePgArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((x) => x !== null && x !== undefined).map((x) => String(x));
  if (typeof value !== "string") return [];
  const s = value.trim();
  if (!s.startsWith("{") || !s.endsWith("}")) return [];
  const inner = s.slice(1, -1);
  if (!inner) return [];
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  let wasQuoted = false;
  const push = () => {
    const v = wasQuoted ? cur : cur.trim();
    if (wasQuoted || v !== "NULL") out.push(v);
    cur = "";
    wasQuoted = false;
  };
  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i]!;
    if (quoted) {
      if (ch === "\\") {
        cur += inner[i + 1] ?? "";
        i++;
      } else if (ch === '"') quoted = false;
      else cur += ch;
    } else if (ch === '"') {
      quoted = true;
      wasQuoted = true;
    } else if (ch === ",") push();
    else cur += ch;
  }
  push();
  return out;
}

/** Liste als Postgres-Listentext („{"a","b"}“). So nimmt auch der Demo-Anschluss Enum-Listen an; PostgREST ebenso. */
export function pgArrayLiteral(values: readonly string[]): string {
  return `{${values.map((v) => `"${v.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`).join(",")}}`;
}

// ── Firma, Land, Steuer ─────────────────────────────────────────────────────────

/**
 * Länderbegriffe (SAAS-12, PRAXIS-9): Steuerwort, Bezeichnung der Steuernummer, Registerstelle; die Schweiz führt das
 * Handelsregister beim kantonalen Handelsregisteramt (kein Gericht, keine Pflichtangabe auf Belegen).
 */
export const AGENCY_COUNTRIES = [
  { code: "DE", get name() { return t("Deutschland", "Germany"); }, timezone: "Europe/Berlin", locale: "de-DE", currency: "EUR", law: "deutsches Recht", get vatLabel() { return t("USt-IdNr.", "VAT ID"); },
    get vatWord() { return t("Umsatzsteuer", "VAT"); }, get registerCourtLabel() { return t("Registergericht", "Register court"); }, registerCourtRequired: true },
  { code: "AT", get name() { return t("Österreich", "Austria"); }, timezone: "Europe/Vienna", locale: "de-AT", currency: "EUR", law: "österreichisches Recht", get vatLabel() { return t("UID-Nummer", "UID number"); },
    get vatWord() { return t("Umsatzsteuer", "VAT"); }, get registerCourtLabel() { return t("Firmenbuchgericht", "Company register court"); }, registerCourtRequired: true },
  { code: "CH", get name() { return t("Schweiz", "Switzerland"); }, timezone: "Europe/Zurich", locale: "de-CH", currency: "CHF", law: "schweizerisches Recht", get vatLabel() { return t("MWST-Nummer (UID)", "VAT number (UID)"); },
    vatWord: "MWST", get registerCourtLabel() { return t("Handelsregisteramt", "Commercial register office"); }, registerCourtRequired: false },
  // K-304: Liechtenstein (mit der Schweiz ein Mehrwertsteuergebiet, Franken, eigenes Recht und Handelsregister beim Amt für Justiz)
  { code: "LI", get name() { return t("Liechtenstein", "Liechtenstein"); }, timezone: "Europe/Vaduz", locale: "de-CH", currency: "CHF", law: "liechtensteinisches Recht", get vatLabel() { return t("MWST-Nummer", "VAT number"); },
    vatWord: "MWST", get registerCourtLabel() { return t("Handelsregister (Amt für Justiz)", "Commercial register (Office of Justice)"); }, registerCourtRequired: false },
] as const;
export type AgencyCountry = (typeof AGENCY_COUNTRIES)[number]["code"];

export function countryDefaults(code: string | null | undefined) {
  return AGENCY_COUNTRIES.find((c) => c.code === code) ?? AGENCY_COUNTRIES[0];
}

export const TIMEZONES = ["Europe/Berlin", "Europe/Vienna", "Europe/Zurich", "Europe/Vaduz"] as const;

/** SAAS-12: Steuerwort im Sitzland der Agentur („Umsatzsteuer“, in der Schweiz „MWST“) */
export function vatWord(country: string | null | undefined): string {
  return country === "CH" || country === "LI" ? "MWST" : t("Umsatzsteuer", "VAT");
}

/** PRAXIS-9: Buchhaltungsexport des Sitzlands (DATEV in Deutschland, BMD in Österreich, sonst CSV) */
export function bookkeepingExportName(country: string | null | undefined): string {
  return country === "AT" ? t("BMD-Export", "BMD export") : country === "DE" ? t("DATEV-Export", "DATEV export") : t("Buchhaltungsexport (CSV)", "Accounting export (CSV)");
}

/** PRAXIS-9: Meldung ohne Steuernummer in den Begriffen des Sitzlands */
export function vatIdMissingText(country: string | null | undefined): string {
  switch (country) {
    // K-314: bei Regelbesteuerung gehört in Österreich die UID auf jede Rechnung über 400 € (§ 11 UStG)
    case "AT": return t("Bitte die UID-Nummer (ATU…) angeben oder die Kleinunternehmerregelung wählen. Die Steuernummer allein genügt nur für Rechnungen bis 400 €.", "Please enter the UID number (ATU…) or choose the small business scheme. The tax number alone is only sufficient for invoices up to €400.");
    case "CH": case "LI": return t("Bitte die MWST-Nummer (UID) angeben oder „Nicht mehrwertsteuerpflichtig“ wählen.", "Please enter the VAT number (UID) or choose “Not subject to VAT”.");
    default: return t("Bitte USt-IdNr. oder Steuernummer angeben oder die Kleinunternehmerregelung wählen.", "Please enter a VAT ID or tax number or choose the small business scheme.");
  }
}

/**
 * K-314: Österreich mit Regelbesteuerung und nur Steuernummer: Hinweis (keine Sperre der Maske), dass Rechnungen über 400 € die
 * UID-Nummer des Ausstellers brauchen (§ 11 Abs. 1 UStG; bis 400 € Kleinbetragsrechnung). Sonst null.
 */
export function vatIdHint(d: Pick<FirmaDraft, "country" | "small_business" | "vat_id" | "tax_number">): string | null {
  // 0444 (Steuerprüfung L5, wie org_setup_status): Kleinunternehmer in Deutschland nennen Steuernummer oder USt-IdNr. (§ 34a UStDV)
  if (d.country === "DE" && d.small_business && !d.vat_id.trim() && !d.tax_number.trim()) {
    return t("Auch als Kleinunternehmer gehört die Steuernummer oder die USt-IdNr. auf jede Rechnung über 250 € brutto (§ 34a UStDV). Bitte eine davon eintragen; ohne Nummer fragt das Portal beim endgültigen Erstellen nach.",
      "Even as a small business, your tax number or VAT ID must appear on every invoice above €250 gross (section 34a German VAT Implementing Regulation). Please enter one of them; without a number the portal asks when you issue an invoice.");
  }
  if (d.country !== "AT" || d.small_business || d.vat_id.trim() || !d.tax_number.trim()) return null;
  return t("Ohne UID-Nummer nur Rechnungen bis 400 € (Kleinbetragsrechnung): ab 400 € muss die UID auf der Rechnung stehen. Bitte die UID-Nummer (ATU…) nachtragen.",
    "Without a UID number only invoices up to €400 (small-amount invoices): above €400 the UID must appear on the invoice. Please add the UID number (ATU…).");
}

/**
 * Steuerangaben im Sitzland in Kurzform (SAAS-R2-8): „Kleinunternehmer, USt-IdNr.“, in Österreich „Kleinunternehmer, UID-Nummer“,
 * in der Schweiz „MWST-Pflicht, MWST-Nummer (UID)“.
 */
export function taxDetailsWords(country: string | null | undefined): string {
  const c = countryDefaults(country);
  return country === "CH" || country === "LI" ? t(`MWST-Pflicht, ${c.vatLabel}`, `VAT registration, ${c.vatLabel}`) : t(`Kleinunternehmer, ${c.vatLabel}`, `small business, ${c.vatLabel}`);
}

/** Pauschal versteuerte Produkte (§ 37b EStG) gibt es nur nach deutschem Recht: eigene Wahl, sonst nach Sitzland (wie im Creator-Profil) */
export function flatTaxActive(enabled: boolean | null | undefined, country: string | null | undefined): boolean {
  return enabled ?? country === "DE";
}

/** Beschriftung der Kleinunternehmerregelung im Sitzland (Wortlaut wie die Steuerhinweise in 0008). */
/** Zielgruppen eines Creator-Kontos (0178, org_settings.creator_segment): steuern nur die Startbelegung */
export const CREATOR_SEGMENTS = [
  { key: "influencer", get label() { return t("Influencer, auch Einsteiger und Micro", "Influencer, including beginners and micro"); } },
  { key: "ugc", get label() { return t("UGC-Creator", "UGC creator"); } },
  { key: "business", get label() { return t("Creator als Unternehmen (Vollzeit, Team, eigene Produkte)", "Creator as a business (full-time, team, own products)"); } },
  { key: "model", get label() { return t("Model", "Model"); } },
  { key: "freelancer", get label() { return t("Foto, Video, Schnitt oder Social Media (Freelancer)", "Photo, video, editing or social media (freelancer)"); } },
  { key: "video", get label() { return t("YouTuber, Streamer, Podcaster", "YouTuber, streamer, podcaster"); } },
] as const;
export type CreatorSegment = (typeof CREATOR_SEGMENTS)[number]["key"];
export function isCreatorSegment(v: string | null | undefined): v is CreatorSegment {
  return CREATOR_SEGMENTS.some((s) => s.key === v);
}

export function smallBusinessLabel(country: string | null | undefined): string {
  switch (country) {
    case "AT": return t("Kleinunternehmer gemäß § 6 Abs. 1 Z 27 UStG", "Small business under § 6 (1) no. 27 UStG");
    case "CH": return t("Nicht mehrwertsteuerpflichtig", "Not subject to VAT");
    default: return t("Kleinunternehmer gemäß § 19 UStG", "Small business under § 19 UStG");
  }
}

export const LEGAL_FORMS: Record<AgencyCountry, string[]> = {
  DE: ["Einzelunternehmen", "GbR", "e. K.", "OHG", "KG", "GmbH & Co. KG", "UG (haftungsbeschränkt)", "GmbH", "AG"],
  AT: ["Einzelunternehmen", "GesbR", "e. U.", "OG", "KG", "GmbH", "AG"],
  CH: ["Einzelunternehmen", "Einzelfirma", "Einfache Gesellschaft", "Kollektivgesellschaft", "GmbH", "AG"],
  LI: ["Einzelunternehmen", "Einzelfirma", "Kollektivgesellschaft", "GmbH", "AG", "Anstalt"],
};

/**
 * Rechtsformen ohne Registerpflicht; alles andere verlangt Registernummer und -gericht (Katalog Schritt 1). SAAS-R2-3: auch die
 * österreichische GesbR (nicht eintragungsfähig), die Schweizer Einzelfirma (unter der Umsatzschwelle nicht eintragungspflichtig;
 * eine vorhandene Nummer lässt sich trotzdem eintragen) und die einfache Gesellschaft. Gleiche Liste in org_setup_status (0087).
 */
const NO_REGISTER = ["einzelunternehmen", "einzelfirma", "gbr", "gesbr", "gesellschaft bürgerlichen rechts", "gesellschaft nach bürgerlichem recht", "einfache gesellschaft", "freiberuf"];
export function registerRequired(legalForm: string | null | undefined): boolean {
  const f = (legalForm ?? "").trim().toLowerCase();
  if (!f) return false;
  return !NO_REGISTER.some((x) => f.startsWith(x));
}
export function suggestRegisterType(country: string | null | undefined, legalForm: string | null | undefined): string {
  if (!registerRequired(legalForm)) return "keines";
  if (country === "AT") return "Firmenbuch";
  if (country === "CH") return t("Handelsregister", "Commercial register");
  const f = (legalForm ?? "").toLowerCase();
  return /gmbh|ug|ag\b|^ag/.test(f) && !f.includes("co. kg") ? "Handelsregister B" : "Handelsregister A";
}

export function addressLine(s: { street?: string | null; postal_code?: string | null; city?: string | null }): string {
  return [s.street, [s.postal_code, s.city].filter(Boolean).join(" ")].filter((x) => x && String(x).trim()).join(", ");
}

// ── Adresse der Agentur im Web: Kurzname (Slug) ─────────────────────────────────

/** Kurzname für organizations.slug: klein, ohne Umlaute, 3–40 Zeichen (Regel aus 0001). */
export function slugify(name: string): string {
  let s = name
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .normalize("NFKD").replace(/[̀-ͯ]/g, "")
    .replace(/&/g, "-und-")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
  if (s.length > 40) s = s.slice(0, 40).replace(/-+$/, "");
  if (s.length < 3) s = s ? `${s}-agentur` : "agentur";
  return s;
}
export function slugCandidate(base: string, attempt: number): string {
  if (attempt <= 0) return base;
  const suffix = `-${attempt + 1}`;
  return `${base.slice(0, 40 - suffix.length).replace(/-+$/, "")}${suffix}`;
}
export const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{2,39}$/;

// ── Farben (05_farben.md: austauschbare Agenturfarbe) ───────────────────────────

export const BRAND_PRESETS = [
  { get name() { return t("Petrolgrün", "Petrol green"); }, hex: "#176B55" },
  { get name() { return t("Beere", "Berry"); }, hex: "#9B325E" },
  { get name() { return t("Ocker", "Ochre"); }, hex: "#805400" },
] as const;

export {
  ON_ACCENT_DARK, normalizeHex, luminance, contrastRatio, formatRatio, functionalTone, checkAccent,
  type ToneCheck, type AccentCheck,
} from "../../../lib/color";

// ── Sprachen, Währungen, Vergütungsmodelle ─────────────────────────────────────

/** Oberflächensprache im eigenen Profil (PARITAET-8): jede Sprache in ihrem eigenen Namen */
export const UI_LANGUAGES = [
  { key: "de", label: "Deutsch" },
  { key: "en", label: "English" },
] as const;
export const LANGUAGES = [
  { key: "de", get label() { return t("Deutsch", "German"); } },
  { key: "en", get label() { return t("Englisch", "English"); } },
] as const;
export const LOCALES = [
  { key: "de-DE", get label() { return t("Deutsch (Deutschland) · 1.234,56 €", "German (Germany) · 1.234,56 €"); } },
  { key: "de-AT", get label() { return t("Deutsch (Österreich) · € 1.234,56", "German (Austria) · € 1.234,56"); } },
  { key: "de-CH", get label() { return t("Deutsch (Schweiz) · CHF 1'234.56", "German (Switzerland) · CHF 1'234.56"); } },
  { key: "en-GB", get label() { return t("Englisch (Vereinigtes Königreich) · €1,234.56", "English (United Kingdom) · €1,234.56"); } },
] as const;
/** Währungen der Agentur (org_settings.currencies, Domäne currency_code). */
export const CURRENCIES = ["EUR", "CHF", "USD", "GBP", "SEK", "DKK"] as const;
/** Kontowährungen: dieselben wie die Rechnungswährungen (0042; vorher ohne SEK und DKK). */
export const BANK_CURRENCIES = CURRENCIES;

/** Namen aus dem einen Katalog der Fachlogik (dieselben wie im Kooperationsformular, Befund 25 der Kooperationen) */
export const DEAL_MODELS = (["fixed", "fixed_plus_commission", "commission_only", "monthly", "installments", "custom", "barter", "event", "gifting"] as const)
  .map((key) => ({ key, get label() { return dealModelName(key, currentLang()); } }));

export const INVOICE_ROUTES = [
  { key: "via_agency", get label() { return t("Über die Agentur", "Via the agency"); } },
  { key: "direct_to_brand", get label() { return t("Direkt (Creator stellt die Rechnung)", "Direct (creator invoices)"); } },
] as const;

/** Auszahlung an Creator (org_settings.talent_payout_mode; leer: Plattform-Standard „feste Frist“). */
export const PAYOUT_MODES = [
  { key: "fixed_terms", get label() { return t("Nach fester Frist ab Rechnungseingang", "After a fixed term from invoice receipt"); } },
  { key: "after_brand_payment", get label() { return t("Erst nach Zahlungseingang der Marke, dann mit Frist", "Only after the brand's payment, then with a term"); } },
] as const;

export const FEE_MODES = [
  { key: "none", get label() { return t("Keine Agenturgebühr", "No agency fee"); } },
  { key: "percent", get label() { return t("Prozent vom Kooperationsbudget", "Percent of the deal budget"); } },
  { key: "fixed", get label() { return t("Festbetrag je Kooperation", "Fixed amount per deal"); } },
] as const;

/**
 * Feiertagsregionen für Werktagsfristen (holiday_region; Kalender der Fachlogik). Die Liste lebt in der Fachlogik
 * (packages/domain/src/holidays.ts): alle 16 Länder, Österreich und alle 26 Kantone, jede mit vollständigen Tagen (0056).
 */
import { HOLIDAY_REGIONS } from "@talentkontor/domain";
import { t, ta, currentLang } from "../../../lib/sprache";
export { HOLIDAY_REGIONS };

/**
 * Nach einem Wechsel des Sitzlands: welche Einstellungen hängen am Land und sollten geprüft werden (Befund 30)?
 * Leer, wenn das Land gleich bleibt.
 */
export function countryChangeChecks(from: string | null | undefined, to: string, s: {
  holiday_region?: string | null; currency?: string | null; governing_law?: string | null; jurisdiction?: string | null; small_business?: boolean;
}): { key: string; text: string; to: string; auto?: boolean }[] {
  if (!from || from === to) return [];
  const out: { key: string; text: string; to: string; auto?: boolean }[] = [];
  const land = countryDefaults(to);
  // 0265 (Inhaber 14.10.2026): Die Datenbank stellt beim Landwechsel alles Länderabhängige selbst um, was nicht von Hand
  // abweichend gesetzt war. Die Liste nennt, was angepasst wurde, und nur den Steuerstatus als echte Rückfrage.
  out.push({ key: "auto", auto: true, to: "/agentur/einstellungen/region",
    text: t(`Automatisch auf ${land.name} umgestellt: Zeitzone, Zahlen- und Datumsformat, Hauswährung (${land.currency}), anwendbares Recht (${land.law}), Gerichtsstand am neuen Ort, Feiertage, Verzugszins und Basiszinssätze, Mahnpauschale, Steuerregeln, Begriffe und Buchhaltungsexport (${bookkeepingExportName(to)}). Eigene abweichende Werte bleiben erhalten.`,
      `Switched to ${land.name} automatically: time zone, number and date format, home currency (${land.currency}), governing law (${land.law}), place of jurisdiction at the new location, public holidays, default interest and base rates, dunning flat fee, tax rules, terms and accounting export (${bookkeepingExportName(to)}). Your own deviating values are kept.`) });
  if (s.small_business) out.push({ key: "steuer", text: t(`Steuerstatus bestätigen: ${smallBusinessLabel(to)} gilt in ${land.name} mit eigenen Grenzen`, `Confirm tax status: ${smallBusinessLabel(to)} applies in ${land.name} with its own thresholds`), to: "/agentur/einstellungen/firma" });
  return out;
}

/**
 * Umsatzgrenzen der Kleinunternehmerregelung je Sitzland (Praxisfall Kleinunternehmer-Agentur). Gesetzliche Werte, keine
 * Einstellung: DE § 19 UStG (seit 2025: Vorjahr 25.000 €, laufendes Jahr 100.000 €, jeweils netto), AT § 6 Abs. 1 Z 27 UStG
 * (55.000 € brutto, einmal 10 % Toleranz bis 60.500 €), CH Art. 10 MWSTG (befreit unter 100.000 CHF).
 */
export const SMALL_BUSINESS_LIMITS: Record<string, { currency: string; limits: { cents: number; text: string }[] }> = {
  DE: { currency: "EUR", limits: [
    { cents: 2_500_000, get text() { return t("Über 25.000 € netto im Jahr: ab dem nächsten Jahr gilt die Regelbesteuerung", "Over €25,000 net per year: standard taxation applies from next year"); } },
    { cents: 10_000_000, get text() { return t("Über 100.000 € netto im laufenden Jahr: die Regelbesteuerung gilt ab dem Umsatz, der die Grenze überschreitet", "Over €100,000 net in the current year: standard taxation applies from the revenue that exceeds the threshold"); } },
  ] },
  AT: { currency: "EUR", limits: [{ cents: 5_500_000, get text() { return t("Grenze 55.000 € brutto im Jahr; einmal bis 60.500 € brutto (10 % Toleranz) bleibt die Befreiung in diesem Jahr und endet zum Jahreswechsel, darüber gilt die Regelbesteuerung sofort", "Threshold €55,000 gross per year; once up to €60,500 gross (10 % tolerance) the exemption remains this year and ends at the turn of the year, above that standard taxation applies immediately"); } }] },
  CH: { currency: "CHF", limits: [{ cents: 10_000_000, get text() { return t("Ab CHF 100'000 Umsatz im Jahr: MWST-Pflicht, Anmeldung bei der ESTV", "From CHF 100,000 revenue per year: VAT liability, registration with the ESTV"); } }] },
  // K-315: Liechtenstein wie die Schweiz (ein Mehrwertsteuergebiet), Anmeldung bei der Steuerverwaltung
  LI: { currency: "CHF", limits: [{ cents: 10_000_000, get text() { return t("Ab CHF 100'000 Umsatz im Jahr: MWST-Pflicht, Anmeldung bei der Steuerverwaltung", "From CHF 100,000 revenue per year: VAT liability, registration with the tax administration"); } }] },
};

/** K-315: Überschrift der Umsatzgrenze in Einstellungen · Firma (CH/LI: MWST-Pflicht statt Kleinunternehmer) */
export function smallBusinessLimitTitle(country: string | null | undefined): string {
  return country === "CH" || country === "LI" ? t("MWST-Pflichtgrenze", "VAT registration threshold") : t("Kleinunternehmergrenze", "Small business threshold");
}
/**
 * Stand gegen die Grenzen: ab 80 % ein Hinweis, darüber eine Warnung. Österreich mit der einmaligen Toleranz aus der Grenzregel
 * des Domain-Pakets, dieselbe wie im Creator-Portal (SAAS-R3-5): über 55.000 € bis 60.500 € „tolerance“, erst darüber „over“.
 * Schweiz: befreit ist ein Umsatz von weniger als 100.000 CHF, genau 100.000 CHF sind „over“. yearCents ist der Umsatz, der für
 * die Grenze zählt (smallBusinessYear).
 */
export function smallBusinessStatus(country: string | null | undefined, yearCents: number): { percent: number; level: "ok" | "hint" | "tolerance" | "over"; text: string }[] {
  const c = SMALL_BUSINESS_LIMITS[country ?? ""];
  if (!c) return [];
  const rule = smallBusinessRule(country);
  return c.limits.map((l) => {
    const percent = Math.round((yearCents / l.cents) * 100);
    if (rule && l.cents === rule.currentCents && (rule.toleranceCents !== null || rule.country === "CH" || rule.country === "LI")) {
      const r = checkSmallBusinessLimit(rule, yearCents, "current");
      return { percent, level: r.level === "near" ? "hint" : r.level, text: l.text };
    }
    return { percent, level: yearCents > l.cents ? "over" : percent >= 80 ? "hint" : "ok", text: l.text };
  });
}

/**
 * Umsatz des Kalenderjahres für Einstellungen · Firma (Praxistest K-254): dieselbe Regel wie die Übersicht des Creator-Kontos
 * (ownInvoiceRevenueAll), daraus der Umsatz, der für die Grenze zählt (AT brutto, DE netto, jeweils nur Leistungsort Inland;
 * CH/LI netto im In- und Ausland). K-315: Rechnungen in anderen Währungen zählen mit dem beim Versand eingefrorenen Kurs
 * (fx_rate_to_org); ohne Kurs zählt `unconverted` sie. null ohne hinterlegte Grenze.
 */
export function smallBusinessYear(country: string | null | undefined, rows: readonly OwnInvoiceRow[], year: number): {
  currency: string; revenue: OwnRevenue; turnoverCents: number; basis: "gross" | "net"; abroadCents: number; unconverted: number;
  status: ReturnType<typeof smallBusinessStatus>;
} | null {
  const limits = SMALL_BUSINESS_LIMITS[country ?? ""];
  const rule = smallBusinessRule(country);
  if (!limits || !rule) return null;
  const { unconverted, ...revenue } = ownInvoiceRevenueAll(rows, year, limits.currency);
  const turnoverCents = smallBusinessTurnover(rule, revenue);
  const basis = smallBusinessBasis(rule);
  const swiss = rule.country === "CH" || rule.country === "LI";
  const abroadCents = swiss ? 0 : basis === "gross" ? revenue.grossCents - revenue.domesticGrossCents : revenue.netCents - revenue.domesticNetCents;
  return { currency: limits.currency, revenue, turnoverCents, basis, abroadCents, unconverted, status: smallBusinessStatus(country, turnoverCents) };
}

/** Pflichtangaben im Creator-Profil vor der ersten Rechnung (talent_profile_requirements, Katalog Schritt 10). */
export const TALENT_PROFILE_FIELDS = [
  { key: "street", get label() { return t("Straße und Hausnummer", "Street and house number"); } },
  { key: "postal_code", label: "PLZ" },
  { key: "city", get label() { return t("Ort", "City"); } },
  { key: "country", get label() { return t("Land (immer Pflicht)", "Country (always required)"); } },
  { key: "iban", label: "IBAN" },
  { key: "bank_account_holder", get label() { return t("Kontoinhaber", "Account holder"); } },
  { key: "bic", label: "BIC" },
  { key: "bank_name", label: "Bank" },
] as const;

/**
 * Beispielnummer zum Nummernmuster wie app.format_invoice_number (0037): {P} Präfix, {YYYY} Jahr, {YY} Jahr zweistellig, {MM} Monat,
 * {N} laufende Nummer mit mindestens drei Stellen oder {N:k} mit mindestens k Stellen. Standard {P}-{YYYY}-{N}.
 */
export const DEFAULT_NUMBER_TEMPLATE = "{P}-{YYYY}-{N}";
export const NUMBER_TOKENS = /\{(P|YYYY|YY|MM|N|N:[1-8])\}/g;
export function invoiceNumberExample(template: string | null | undefined, prefix: string | null | undefined, isoDate: string, n = 1): string {
  const t = (template ?? "").trim() || DEFAULT_NUMBER_TEMPLATE;
  const digits = Number(/\{N:([1-8])\}/.exec(t)?.[1] ?? 3);
  const num = String(n).padStart(digits, "0");
  return t
    .replace(/\{P\}/g, (prefix ?? "").trim() || "RE")
    .replace(/\{YYYY\}/g, isoDate.slice(0, 4))
    .replace(/\{YY\}/g, isoDate.slice(2, 4))
    .replace(/\{MM\}/g, isoDate.slice(5, 7))
    .replace(/\{N(:[1-8])?\}/g, num);
}

/** Stichworte aus einem Textfeld: je Zeile oder durch Komma getrennt. */
export function splitKeywords(text: string): string[] {
  return [...new Set(text.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean))];
}

// ── Mahnleiter ─────────────────────────────────────────────────────────────────

/** Stufen mit Plattformtext (dunning.* in 0008). Eigene Schlüssel hätten keinen Mahntext (org_text kennt nur Plattformschlüssel). */
export const DUNNING_STAGES = [
  { key: "reminder", get label() { return t("Zahlungserinnerung", "Payment reminder"); } },
  { key: "first_notice", get label() { return t("1. Mahnung", "1st dunning notice"); } },
  { key: "second_notice", get label() { return t("2. Mahnung", "2nd dunning notice"); } },
  { key: "final_notice", get label() { return t("Letzte Mahnung", "Final dunning notice"); } },
] as const;
export function stageLabel(key: string): string {
  return DUNNING_STAGES.find((s) => s.key === key)?.label ?? key;
}

export interface StepDraft {
  key: string;
  days: string;
  day_type: string;
  fee: string;
  interest: boolean;
  is_reminder: boolean;
}
export interface StepRow {
  step: number;
  key: string;
  days: number;
  day_type: "business" | "calendar";
  fee_cents: number;
  interest: boolean;
  is_reminder: boolean;
}

/** Muster-Leiter des Assistenten: vier Stufen ohne Fristen und Beträge (Katalog Schritt 5). */
export function sampleLadder(): StepDraft[] {
  return DUNNING_STAGES.map((s, i) => ({ key: s.key, days: "", day_type: "calendar", fee: "", interest: i >= 2, is_reminder: i === 0 }));
}

export function stepToDraft(s: StepRow): StepDraft {
  return { key: s.key, days: String(s.days), day_type: s.day_type, fee: s.fee_cents ? centsToText(s.fee_cents) : "", interest: s.interest, is_reminder: s.is_reminder };
}

/** Obergrenze einer Mahngebühr je Stufe (K-143) */
export const MAHNGEBUEHR_MAX_CENTS = 50_000;

const RESERVED_STAGE_KEYS = ["none", "collection"];

export function validateLadder(drafts: readonly StepDraft[]): { rows: StepRow[]; errors: Record<string, string> } {
  const c = new Check();
  if (drafts.length === 0) c.fail("steps", t("Die Mahnleiter braucht mindestens eine Stufe.", "The dunning levels need at least one level."));
  if (drafts.length > 10) c.fail("steps", t("Höchstens zehn Stufen.", "At most ten levels."));
  const seen = new Set<string>();
  const rows: StepRow[] = [];
  drafts.forEach((d, i) => {
    const key = d.key.trim();
    if (!/^[a-z0-9_]{2,30}$/.test(key) || RESERVED_STAGE_KEYS.includes(key)) c.fail(`key-${i}`, t(`Stufe ${i + 1}: bitte eine Stufe wählen.`, `Level ${i + 1}: please choose a level.`));
    else if (seen.has(key)) c.fail(`key-${i}`, t(`Stufe ${i + 1}: „${stageLabel(key)}“ kommt doppelt vor.`, `Level ${i + 1}: “${stageLabel(key)}” appears twice.`));
    seen.add(key);
    const days = c.int(`days-${i}`, d.days, { label: t(`Frist der Stufe ${i + 1}`, `Deadline of level ${i + 1}`), required: true, min: 0, max: 90 });
    const fee = c.cents(`fee-${i}`, d.fee, { label: t(`Gebühr der Stufe ${i + 1}`, `Fee of level ${i + 1}`) }) ?? 0;
    // K-143: Mahngebühr mit Obergrenze (999.999 € gingen durch); übliche Gebühren liegen bei wenigen Euro, die Pauschale bei 40 €
    if (fee > MAHNGEBUEHR_MAX_CENTS) c.fail(`fee-${i}`, t(`Gebühr der Stufe ${i + 1}: höchstens ${(MAHNGEBUEHR_MAX_CENTS / 100).toLocaleString("de-DE")} €.`, `Fee of level ${i + 1}: at most ${(MAHNGEBUEHR_MAX_CENTS / 100).toLocaleString("en-GB")} €.`));
    const dayType = d.day_type === "business" ? "business" : d.day_type === "calendar" ? "calendar" : null;
    if (!dayType) c.fail(`day_type-${i}`, t(`Stufe ${i + 1}: bitte Kalender- oder Werktage wählen.`, `Level ${i + 1}: please choose calendar or working days.`));
    if (days !== null && dayType) rows.push({ step: i + 1, key, days, day_type: dayType, fee_cents: fee, interest: d.interest, is_reminder: d.is_reminder });
  });
  // Zahlungserinnerungen stehen vor den Mahnungen (erst erinnern, dann mahnen)
  const firstNotice = drafts.findIndex((d) => !d.is_reminder);
  const lateReminder = drafts.findIndex((d, i) => d.is_reminder && firstNotice >= 0 && i > firstNotice);
  if (lateReminder >= 0) c.fail(`key-${lateReminder}`, t(`Stufe ${lateReminder + 1}: eine Zahlungserinnerung steht vor den Mahnungen. Bitte nach oben schieben.`, `Level ${lateReminder + 1}: a payment reminder comes before the dunning notices. Please move it up.`));
  return { rows, errors: c.errors };
}

/**
 * Steuerprüfung 23.10.2026, L15: Ersatzfähig sind nur tatsächliche Rechtsverfolgungskosten. Die erste, verzugsbegründende Mahnung
 * ist kein Verzugsschaden (§ 286 Abs. 1 BGB); über die angerechnete Pauschale von 40 € (§ 288 Abs. 5 BGB) hinaus braucht ein höherer
 * Betrag einen Nachweis. Nur ein Hinweis in der Mahnleiter, keine Sperre. Unlesbare Beträge zählen nicht (die Prüfung meldet sie).
 */
export const STUFENGEBUEHR_HINWEIS_CENTS = 4_000;
export function stufengebuehrenHinweis(drafts: readonly Pick<StepDraft, "fee">[]): string | null {
  const fees = drafts.map((d) => {
    try { return d.fee.trim() ? Math.max(0, parseAmountToCents(d.fee.trim())) : 0; } catch { return 0; }
  });
  const sum = fees.reduce((a, b) => a + b, 0);
  const first = (fees[0] ?? 0) > 0;
  if (!first && sum <= STUFENGEBUEHR_HINWEIS_CENTS) return null;
  const teile = [
    first ? t("Die erste Stufe trägt eine Gebühr. Die erste Mahnung begründet meist erst den Verzug; ihre Kosten sind kein Verzugsschaden und lassen sich in der Regel nicht verlangen.", "The first level carries a fee. The first notice usually only puts the customer in default; its costs are not damages for default and generally cannot be claimed.") : null,
    sum > STUFENGEBUEHR_HINWEIS_CENTS ? t("Die Stufengebühren ergeben zusammen mehr als 40 €. Ersatzfähig sind nur tatsächlich entstandene Kosten; über die Verzugspauschale von 40 € hinaus brauchst du dafür einen Nachweis (§ 288 Abs. 5 BGB).", "The level fees add up to more than €40. Only costs actually incurred can be claimed; above the flat late-payment fee of €40 you need proof of them (§ 288(5) German Civil Code).") : null,
  ];
  return teile.filter(Boolean).join(" ");
}

/** Verzugszinsen an, aber keine Stufe weist sie aus: dann stehen sie auf keinem Schreiben. */
export function interestWithoutStep(interestEnabled: boolean, drafts: readonly Pick<StepDraft, "interest">[]): boolean {
  return interestEnabled && drafts.length > 0 && !drafts.some((d) => d.interest);
}

/**
 * PRAXIS-1: Zinsen einschalten geht erst, wenn für das Land ein Satz gepflegt ist (Basiszins bzw. fester Satz, 0055). Sonst
 * rechnet keine Mahnung Zinsen; die Meldung sagt, wer ihn pflegt.
 */
export function interestNeedsRate(interestEnabled: boolean, rates: readonly unknown[], country: string | null | undefined): string | null {
  if (!interestEnabled || rates.length > 0) return null;
  const land = country === "AT" ? t("Österreich", "Austria") : country === "CH" ? t("die Schweiz", "Switzerland") : country === "DE" ? t("Deutschland", "Germany") : t("das Land der Agentur", "the agency's country");
  return t(`Für ${land} ist noch kein Basiszins hinterlegt. Der Betrieb pflegt ihn; bis dahin lassen sich Verzugszinsen nicht einschalten.`, `No base rate is on file for ${land} yet. Operations maintain it; until then default interest cannot be switched on.`);
}

/** K-392: Zinsbeginn in der Schweiz nach der Einstellung „Zinsen ab“ (Vorgabe: ab der ersten Mahnung, Art. 102 Abs. 1 OR) */
function chZinsbeginn(interestStart: "due" | "reminder" | null | undefined): string {
  return interestStart === "due"
    ? t("Eingestellt ist: Verzugszins ab der Fälligkeit laut Beleg. Das setzt einen vereinbarten Verfalltag voraus (Art. 102 Abs. 2 OR); ob er vereinbart ist, prüft CreatorOffice nicht.", "Set: default interest from the due date on the document. This requires an agreed expiry date (Art. 102 (2) CO); CreatorOffice does not check whether one was agreed.")
    : t("Der Verzugszins läuft ab der ersten Mahnung (Art. 102 Abs. 1 OR); nach einem Zurücksetzen der Mahnstufe ab der nächsten Mahnung. Ist ein Verfalltag vereinbart, lässt sich oben unter „Zinsen ab“ der Beginn ab Fälligkeit wählen.", "Default interest runs from the first dunning notice (Art. 102 (1) CO); after the dunning level is reset, from the next notice. If an expiry date was agreed, you can choose interest from the due date under “Interest from” above.");
}

/**
 * SAAS-8: Verzugspauschale und Verbraucherfall je Sitzland. Die gesetzliche Pauschale (40 €) gibt es nur in Deutschland und
 * Österreich und nur zwischen Unternehmen; die Schweiz kennt keine, dort nur vertraglich vereinbarte Mahngebühren.
 */
export function flatFeeHint(country: string | null | undefined, interestStart?: "due" | "reminder" | null): string {
  const privat = t("Gegenüber Privatpersonen rechnet das Mahnschreiben ohne Pauschale und mit dem Verbraucherzins (Deutschland Basiszins plus 5 Prozentpunkte, Österreich 4 %, Schweiz 5 %).", "For private individuals the dunning letter calculates without a flat fee and with the consumer interest rate (Germany base rate plus 5 percentage points, Austria 4 %, Switzerland 5 %).");
  switch (country) {
    case "DE": return t(`Bei Geschäften zwischen Unternehmen darf eine Verzugspauschale von 40 € verlangt werden (§ 288 Abs. 5 BGB). Sie lässt sich oben als „Pauschale (einmalig)“ eintragen; Mahngebühren der Stufen werden darauf angerechnet, berechnet wird also der höhere Betrag, nicht beides. ${privat}`, `In business-to-business transactions, a flat late-payment fee of €40 may be charged (§ 288 (5) BGB). It can be entered above as the “default fee”; level fees are credited against it, so the higher amount is charged, not both. ${privat}`);
    case "AT": return t(`Bei Geschäften zwischen Unternehmen darf eine Pauschale für Betreibungskosten von 40 € verlangt werden (§ 458 UGB). Sie lässt sich oben als „Pauschale (einmalig)“ eintragen; Mahngebühren der Stufen werden darauf angerechnet, berechnet wird also der höhere Betrag, nicht beides. ${privat}`, `In business-to-business transactions, a flat compensation for recovery costs of €40 may be charged (§ 458 UGB). It can be entered above as the “default fee”; level fees are credited against it, so the higher amount is charged, not both. ${privat}`);
    case "CH": case "LI": return t(`Das Schweizer Recht kennt keine gesetzliche Verzugspauschale. Mahngebühren dürfen nur verlangt werden, wenn sie vertraglich vereinbart sind (etwa in den AGB); dann lassen sie sich oben als „Pauschale (einmalig)“ oder je Stufe eintragen. Eine Pauschale oder einen Aufschlag auf den Basiszins berechnet CreatorOffice für die Schweiz nicht automatisch. ${chZinsbeginn(interestStart)} ${privat}`, `Swiss law has no statutory flat late-payment fee. Dunning fees may only be charged if agreed by contract (e.g. in the terms); they can then be entered above as the “default fee” or per level. CreatorOffice does not automatically calculate a flat fee or a surcharge on the base rate for Switzerland. ${chZinsbeginn(interestStart)} ${privat}`);
    default: return t(`Eine Verzugspauschale gibt es nur, wenn das Recht des Landes oder der Vertrag sie vorsieht. ${privat}`, `A flat late-payment fee only exists if the country's law or the contract provides for it. ${privat}`);
  }
}

/**
 * 0181: Hinweis zur Mahnung im Auftrag des Creators je Sitzland des Kontos. Die Pauschale steht in Deutschland in § 288 Abs. 5 BGB,
 * in Österreich in § 458 UGB (nicht § 456, dort stehen die Zinsen); die Schweiz kennt keine. Sitzt der Creator in einem anderen
 * Land als die Agentur, rechnet CreatorOffice Pauschale und Zinsen nicht automatisch.
 */
export function creatorClaimsHint(country: string | null | undefined): string {
  const pauschale = country === "DE" ? t("Pauschale nach § 288 Abs. 5 BGB gegenüber Unternehmen", "flat fee under § 288 (5) BGB towards businesses")
    : country === "AT" ? t("Pauschale nach § 458 UGB gegenüber Unternehmen", "flat fee under § 458 UGB towards businesses")
    : country === "CH" || country === "LI" ? t("keine gesetzliche Pauschale", "no statutory flat fee")
    : t("Pauschale, soweit vereinbart oder gesetzlich vorgesehen", "flat fee where agreed or provided by law");
  return t(`Rechnet der Creator direkt mit der Marke ab, mahnt die Agentur in seinem Auftrag. Die Ansprüche (Mahngebühren dieser Leiter, ${pauschale}, Verzugszinsen) stehen dem Creator zu und stehen dann im Mahnschreiben. Sitzt der Creator in einem anderen Land als die Agentur, berechnet CreatorOffice Pauschale und Verzugszinsen nicht automatisch. Aus: ohne Gebühr und Zinsen.`,
    `If the creator invoices the brand directly, the agency sends reminders on their behalf. The claims (dunning fees of this ladder, ${pauschale}, default interest) belong to the creator and then appear in the dunning letter. If the creator is based in a different country than the agency, CreatorOffice does not calculate the flat fee and default interest automatically. Off: without fee and interest.`);
}

// ── Texte (org_texts gegen platform_texts) ─────────────────────────────────────

export const TEXT_GROUPS = [
  { key: "dunning", get label() { return t("Mahnschreiben an Marken", "Dunning letters to brands"); } },
  { key: "email", get label() { return t("E-Mails", "Emails"); } },
  { key: "invoice", get label() { return t("Rechnungshinweise (Steuer)", "Invoice notes (tax)"); } },
] as const;

/**
 * Texte, die die Agentur nicht selbst fasst: Mitteilungen der Plattform an die Agentur (Kündigung, Löschung) und
 * Begrüßungstexte, die noch nirgends erscheinen. Die Auswahl zeigt sie nicht.
 */
export const HIDDEN_TEXT_KEYS = [
  "email.org_cancel_confirmed", "email.org_trial_cancelled", "email.org_trial_payment_removed", "email.org_winback", "email.org_purge_notice", "email.org_reduce_notice", "email.org_access_ended_team", "email.guardian_decided", "email.subscription_activated",
  "email.platform_subscription_request", "email.support_access_opened", "email.support_session_started", "email.chef_role_changed",
  "email.org_readonly_notice", "email.mfa_reset", "email.member_mfa_reset",
  "email.invite_chef", "email.org_subscription_expiring", "email.org_subscription_expired", "email.org_subscription_payment_failed",
  // 0326: Aktivierungs-Mails der Plattform an neue Agenturen (T+1, T+3, T+7)
  "email.org_activation_t1", "email.org_activation_t3", "email.org_activation_t7",
  // 0572: Willkommens-Mail mit 15-Minuten-Einrichtung (Plattform an die Agentur)
  "email.org_welcome",
  // 0414/0415: Pause und Kündigungsweg (Plattform an die Agentur)
  "email.org_pause_reminder", "email.org_pause_ended", "email.org_cancel_confirmed_v2", "email.org_winback_before_end",
  // 0416: Kündigungsbestätigung bei PayPal (ohne „zurücknehmen“)
  "email.org_cancel_confirmed_paypal",
  // 0421: Kündigung zurückgenommen
  "email.org_cancel_withdrawn",
  // 0101: Betriebsbericht und Sofortwarnung gehen nur an den Plattform-Betrieb
  "email.platform_ops_report", "email.platform_ops_alert",
  // 0243: offene Registrierung (an den Betrieb) und Erinnerung an die Person schreibt die Plattform
  "email.platform_signup_open", "email.signup_reminder", "email.platform_signup_confirmed",
  // 0176: Support und gemeldete Fehler schreibt die Plattform
  "email.support_reply", "email.support_ticket_closed", "email.error_report_resolved", "email.platform_support_ticket", "email.platform_support_digest",
  // 0102: Stand der eigenen Absenderdomain schreibt die Plattform
  "email.sender_domain_status",
  // 0182: Ankündigung von Änderungen der Rechtstexte schreibt die Plattform
  "email.platform_legal_change",
  "email.platform_account_deleted",
  "email.platform_event",
  "email.platform_trial_scope",
  "email.platform_retention",
  // 0390: Demo-Termine (an die buchende Person und an den Betrieb) schreibt die Plattform
  "email.platform_demo", "email.platform_demo_ops",
  // 0459: Einladung ins Team der Betreiber-Konsole schreibt die Plattform
  "email.platform_staff_invite",
  "portal.welcome_agency", "portal.welcome_talent",
];
/**
 * Texte, die die Auswahl nicht zeigt: HIDDEN_TEXT_KEYS und (0431) die Fassungen für Creator-Konten („<Schlüssel>.creator“), die der
 * Versand selbst wählt (outbox.ts).
 */
export function isHiddenText(key: string): boolean {
  return HIDDEN_TEXT_KEYS.includes(key) || key.endsWith(".creator");
}

const TEXT_LABELS: Record<string, string> = {
  get "dunning.reminder"() { return t("Zahlungserinnerung", "Payment reminder"); },
  get "dunning.first_notice"() { return t("1. Mahnung", "1st dunning notice"); },
  get "dunning.second_notice"() { return t("2. Mahnung", "2nd dunning notice"); },
  get "dunning.final_notice"() { return t("Letzte Mahnung", "Final dunning notice"); },
  get "dunning.collection_notice"() { return t("Übergabe an Inkasso", "Handover to debt collection"); },
  get "email.invite_member"() { return t("Einladung ins Team", "Team invitation"); },
  get "email.invite_talent"() { return t("Einladung für Creator", "Creator invitation"); },
  get "email.contract_to_sign"() { return t("Vertrag zur Unterschrift", "Contract awaiting signature"); },
  get "email.contract_signed"() { return t("Vertrag unterschrieben", "Contract signed"); },
  get "email.contract_reminder"() { return t("Vertrag: Erinnerung an die Unterschrift", "Contract: signature reminder"); },
  get "email.contract_copy"() { return t("Vertrag: Ausfertigung (Leselink)", "Contract: executed copy (read link)"); },
  get "email.contract_stopped"() { return t("Vertrag angehalten", "Contract on hold"); },
  get "email.contract_voided"() { return t("Vertrag aufgehoben", "Contract voided"); },
  get "email.contract_terminated"() { return t("Vertrag gekündigt", "Contract terminated"); },
  get "email.contract_recipient_changed"() { return t("Vertrag: Empfänger geändert (Info an die alte Adresse)", "Contract: recipient changed (info to the old address)"); },
  get "email.invoice_to_brand"() { return t("Rechnung an Marke", "Invoice to brand"); },
  get "email.offer_to_brand"() { return t("Angebot an Marke (mit PDF)", "Offer to brand (with PDF)"); },
  get "email.credit_note_to_brand"() { return t("Stornorechnung an Marke", "Cancellation invoice to brand"); },
  get "email.agency_invoice_issued"() { return t("Rechnung der Agentur an den Creator", "Agency invoice to the creator"); },
  get "email.self_billing_issued"() { return t("Gutschrift an den Creator", "Self-billing invoice to the creator"); },
  get "email.self_billing_cancelled"() { return t("Gutschrift storniert (Stornobeleg an den Creator)", "Self-billing invoice cancelled (cancellation document to the creator)"); },
  get "email.invoice_cancelled"() { return t("Rechnung storniert (an den Creator)", "Invoice cancelled (to the creator)"); },
  get "email.invoice_collection"() { return t("Rechnung beim Inkasso (an den Creator)", "Invoice with debt collection (to the creator)"); },
  get "email.invoice_collection_withdrawn"() { return t("Inkasso zurückgenommen (an den Creator)", "Debt collection withdrawn (to the creator)"); },
  get "email.invoice_request"() { return t("Rechnung anfordern", "Request invoice"); },
  get "email.invoice_revision"() { return t("Rechnung korrigieren", "Correct invoice"); },
  get "email.payment_check"() { return t("Zahlungsnachfrage beim Creator", "Payment enquiry to the creator"); },
  get "email.payment_confirmed"() { return t("Zahlung bestätigt", "Payment confirmed"); },
  get "email.payment_reset"() { return t("Zahlung zurückgesetzt", "Payment reset"); },
  get "email.payout_revoked"() { return t("Auszahlung zurückgenommen", "Payout withdrawn"); },
  get "email.calendar_event"() { return t("Neuer Termin", "New appointment"); },
  get "email.calendar_event_changed"() { return t("Termin geändert", "Appointment changed"); },
  get "email.calendar_event_cancelled"() { return t("Termin abgesagt", "Appointment cancelled"); },
  get "email.content_deadline_soon"() { return t("Erinnerung: Content-Deadline naht", "Reminder: content deadline approaching"); },
  get "email.deliverable_draft_due"() { return t("Erinnerung: Entwurf morgen fällig (Beiträge)", "Reminder: draft due tomorrow (posts)"); },
  get "email.deliverable_publish_due"() { return t("Erinnerung: Veröffentlichung morgen (Beiträge)", "Reminder: posting due tomorrow (posts)"); },
  get "email.deliverable_overdue_talent"() { return t("Beitrag überfällig (an den Creator)", "Content overdue (to the creator)"); },
  // 0073 Freigabe durch die Marke, Bericht, Kennzahlen
  get "email.brand_review_request"() { return t("Freigabe-Link an die Marke", "Approval link to the brand"); },
  get "email.brand_report"() { return t("Bericht an die Marke", "Report to the brand"); },
  get "email.brand_approved"() { return t("Marke hat freigegeben (an den Creator)", "Brand approved (to the creator)"); },
  get "email.brand_changes"() { return t("Änderungswunsch der Marke (an den Creator)", "Brand's change request (to the creator)"); },
  get "email.content_changes"() { return t("Korrektur am Entwurf (an den Creator)", "Changes to the draft (to the creator)"); },
  get "email.deliverable_metrics_due"() { return t("Kennzahlen hochladen (an den Creator)", "Upload metrics (to the creator)"); },
  get "email.chat_unread"() { return t("Ungelesene Nachrichten (Sammelmail)", "Unread messages (digest email)"); },
  get "email.staff_task"() { return ta("Aufgabe für das Team", "Task for the team", "Aufgabe für dich", "Task for you"); },
  get "email.insights_share"() { return t("Insights-Freigabe an eine Marke", "Insights share with a brand"); },
  get "email.media_kit"() { return t("Media-Kit bzw. Pitch-Liste an eine Marke", "Media kit or pitch list to a brand"); },
  get "email.guardian_consent"() { return t("Hinweis an Erziehungsberechtigte (Einwilligung beim Konto-Anlegen)", "Notice to parents or guardians (consent when creating the account)"); },
  get "email.guardian_confirm"() { return t("Bestätigungslink an Erziehungsberechtigte (Einwilligung vor dem Zugang)", "Confirmation link to parents or guardians (consent before access)"); },
  get "email.guardian_decided"() { return t("Rückmeldung der Erziehungsberechtigten (an den Creator)", "Reply from parents or guardians (to the creator)"); },
  get "email.guardian_consent_later"() { return t("Hinweis an Erziehungsberechtigte (Einwilligung im Creator-Portal nachgereicht)", "Notice to parents or guardians (consent given later in the creator portal)"); },
  get "email.accounting_forward"() { return t("Beleg an die Buchhaltung (automatisch, mit PDF)", "Document to accounting (automatic, with PDF)"); },
  get "email.accounting_forward_test"() { return t("Testmail an die Beleg-Adresse der Buchhaltung", "Test email to the accounting document address"); },
  get "email.bank_details_changed"() { return t("Bankdaten geändert (an den Creator)", "Bank details changed (to the creator)"); },
  get "email.org_cancel_confirmed"() { return t("Kündigung vorgemerkt (Plattform an die Agentur)", "Termination scheduled (platform to the agency)"); },
  get "email.org_trial_cancelled"() { return t("Testphase beendet (Plattform an die Agentur)", "Trial ended (platform to the agency)"); },
  get "email.org_trial_payment_removed"() { return t("Test beendet, Zahlungsart entfernt (Plattform an die Agentur)", "Trial ended, payment method removed (platform to the agency)"); },
  get "email.org_winback"() { return t("Rückgewinnung nach der Kündigung (Plattform an die Agentur)", "Win-back after termination (platform to the agency)"); },
  get "email.org_pause_reminder"() { return t("Pause endet in 7 Tagen (Plattform an die Agentur)", "Pause ends in 7 days (platform to the agency)"); },
  get "email.org_pause_ended"() { return t("Pause beendet (Plattform an die Agentur)", "Pause ended (platform to the agency)"); },
  get "email.org_cancel_confirmed_v2"() { return t("Kündigung bestätigt, mit „Kündigung zurücknehmen“ (Plattform an die Agentur)", "Cancellation confirmed, with “Withdraw cancellation” (platform to the agency)"); },
  get "email.org_cancel_withdrawn"() { return t("Kündigung zurückgenommen (Plattform an die Agentur)", "Cancellation withdrawn (platform to the agency)"); },
  get "email.org_cancel_confirmed_paypal"() { return t("Kündigung vorgemerkt, Abo über PayPal (Plattform an die Agentur)", "Cancellation scheduled, PayPal subscription (platform to the agency)"); },
  get "email.org_winback_before_end"() { return t("Angebot 7 Tage vor Wirksamkeit der Kündigung (Plattform an die Agentur)", "Offer 7 days before the cancellation takes effect (platform to the agency)"); },
  get "email.org_welcome"() { return t("Willkommen mit kostenloser 15-Minuten-Einrichtung (Plattform an die Agentur)", "Welcome with a free 15-minute setup (platform to the agency)"); },
  get "email.org_activation_t1"() { return t("Aktivierung Tag 1: erster Creator (Plattform an die Agentur)", "Activation day 1: first creator (platform to the agency)"); },
  get "email.org_activation_t3"() { return t("Aktivierung Tag 3: erste Kooperation (Plattform an die Agentur)", "Activation day 3: first collaboration (platform to the agency)"); },
  get "email.org_activation_t7"() { return t("Aktivierung Tag 7: Zwischenstand (Plattform an die Agentur)", "Activation day 7: progress (platform to the agency)"); },
  get "email.org_purge_notice"() { return t("Ankündigung der Löschung (Plattform an die Agentur)", "Deletion announcement (platform to the agency)"); },
  get "email.org_access_ended_team"() { return t("Zugang endet: Kündigung wirksam oder Testende (Plattform an die Manager)", "Access ending: termination effective or trial ended (platform to managers)"); },
  get "email.org_reduce_notice"() { return t("Ankündigung: Reduktion auf die Belege (Plattform an die Agentur)", "Announcement: reduction to documents (platform to the agency)"); },
  get "email.subscription_activated"() { return t("Abo freigeschaltet (Plattform an die Agentur)", "Subscription activated (platform to the agency)"); },
  get "email.platform_subscription_request"() { return t("Tarifanfrage (Agentur an die Plattform)", "Plan request (agency to the platform)"); },
  get "email.support_access_opened"() { return t("Support-Zugang geöffnet (Plattform an die Agentur)", "Support access opened (platform to the agency)"); },
  get "email.support_session_started"() { return t("Support-Sitzung begonnen (Plattform an die Agentur)", "Support session started (platform to the agency)"); },
  get "email.chef_role_changed"() { return t("Admin-Rolle geändert (an die Admins)", "Admin role changed (to the admins)"); },
  // 0077 Korrekturrunde 1
  get "email.org_readonly_notice"() { return t("Agentur-Portal eingeschränkt (Plattform an die Creator)", "Agency portal restricted (platform to the creators)"); },
  get "email.mfa_reset"() { return t("Zweiter Faktor zurückgesetzt (Plattform an die Person)", "Second factor reset (platform to the person)"); },
  get "email.member_mfa_reset"() { return t("Zweiter Faktor zurückgesetzt (Plattform an die Admins)", "Second factor reset (platform to the admins)"); },
  // 0092 Korrekturrunde 3
  get "email.invite_chef"() { return t("Einladung als Admin (Plattform an die neue Person)", "Invitation as admin (platform to the new person)"); },
  get "email.org_subscription_expiring"() { return t("Abo-Laufzeit endet bald (Plattform an die Agentur)", "Subscription term ending soon (platform to the agency)"); },
  get "email.org_subscription_expired"() { return t("Abo-Laufzeit abgelaufen (Plattform an die Agentur)", "Subscription term expired (platform to the agency)"); },
  get "email.org_subscription_payment_failed"() { return t("Abo-Zahlung fehlgeschlagen (Plattform an die Admins)", "Subscription payment failed (platform to the admins)"); },
  // 0101 Betrieb
  get "email.platform_ops_report"() { return t("Täglicher Betriebsbericht (an den Plattform-Betrieb)", "Daily operations report (to platform operations)"); },
  get "email.platform_ops_alert"() { return t("Sofortwarnung (an den Plattform-Betrieb)", "Instant warning (to platform operations)"); },
  // 0243
  get "email.platform_signup_open"() { return t("Neue Registrierung, noch nicht bestätigt (an den Plattform-Betrieb)", "New sign-up, not confirmed yet (to platform operations)"); },
  get "email.platform_signup_confirmed"() { return t("Registrierung über die Erinnerung bestätigt (an den Plattform-Betrieb)", "Sign-up confirmed via the reminder (to platform operations)"); },
  // 0390
  get "email.platform_demo"() { return t("Demo-Termin (Plattform an die buchende Person)", "Demo booking (platform to the booker)"); },
  get "email.platform_demo_ops"() { return t("Demo-Termin (an den Plattform-Betrieb)", "Demo booking (to platform operations)"); },
  // 0459
  get "email.platform_staff_invite"() { return t("Einladung ins Team der Betreiber-Konsole (Plattform an die eingeladene Person)", "Invitation to the operator console team (platform to the invited person)"); },
  get "email.signup_reminder"() { return t("Erinnerung: Adresse bestätigen (Plattform an die registrierte Person)", "Reminder: confirm address (platform to the person who signed up)"); },
  // 0176 Support
  get "email.support_ticket_closed"() { return t("Ticket nach 3 Tagen ohne Antwort geschlossen (Plattform an die Agentur)", "Ticket closed after 3 days without reply (platform to the agency)"); },
  get "email.support_reply"() { return t("Antwort des Supports auf ein Ticket (Plattform an die Agentur)", "Support reply to a ticket (platform to the agency)"); },
  get "email.error_report_resolved"() { return t("Gemeldeter Fehler behoben (Plattform an die meldende Person)", "Reported problem fixed (platform to the reporter)"); },
  get "email.platform_support_ticket"() { return t("Neues Ticket oder neue Antwort (an den Plattform-Betrieb)", "New ticket or reply (to platform operations)"); },
  get "email.platform_support_digest"() { return t("Tägliche Support-Übersicht (an den Plattform-Betrieb)", "Daily support summary (to platform operations)"); },
  get "email.platform_legal_change"() { return t("Ankündigung einer Änderung der Rechtstexte (Plattform an die Admins)", "Notice of a change to the legal texts (platform to the admins)"); },
  get "email.platform_account_deleted"() { return t("Bestätigung: Konto gelöscht (Plattform an die Person)", "Confirmation: account deleted (platform to the person)"); },
  get "email.platform_retention"() { return t("Bestätigung des Rabatts aus dem Kündigungsweg", "Confirmation of the discount from the cancellation flow"); },
  get "email.platform_trial_scope"() { return t("Test im vollen Umfang: Hinweis vor und nach dem Testende", "Full-scope trial: notice before and after the trial ends"); },
  get "email.platform_event"() { return t("Sofortmeldung an den Betrieb (neues Konto, Zahlungsart, Kündigung)", "Instant notice to operations (new account, payment method, termination)"); },
  // 0102
  get "email.sender_domain_status"() { return t("Eigene Absenderdomain: Stand (Plattform an die Admins)", "Custom sender domain: status (platform to the admins)"); },
  get "email.contract_resumed"() { return t("Vertrag läuft weiter (an den Creator)", "Contract continues (to the creator)"); },
  get "email.tax_status_check"() { return t("Steuerstatus fürs neue Jahr bestätigen (an den Creator)", "Confirm tax status for the new year (to the creator)"); },
  get "email.deliverable_deadline_changed"() { return t("Termin eines Beitrags geändert (an den Creator)", "Post date changed (to the creator)"); },
  // 0085 Korrekturrunde 2
  get "email.deliverable_added"() { return t("Neuer Beitrag (an den Creator)", "New post (to the creator)"); },
  // 0458 Prüf-Chat CP-014
  get "email.deal_new"() { return t("Neue Kooperation (an den Creator)", "New collaboration (to the creator)"); },
  get "email.deliverable_cancelled"() { return t("Beitrag entfällt (an den Creator)", "Post cancelled (to the creator)"); },
  // 0087 Korrekturrunde 2
  get "email.invoice_rejection_reverted"() { return t("Ablehnung zurückgenommen (an den Creator)", "Rejection withdrawn (to the creator)"); },
  get "invoice.notice.DE_DOMESTIC_VAT"() { return t("Deutschland · Regelbesteuerung", "Germany · standard taxation"); },
  get "invoice.notice.DE_DAMAGES_NOT_TAXABLE"() { return t("Verzugskosten · nicht steuerbar (Schadensersatz)", "Late-payment costs · not subject to VAT (damages)"); },
  get "invoice.notice.AT_DAMAGES_NOT_TAXABLE"() { return t("Österreich · Verzugskosten nicht steuerbar (Schadenersatz)", "Austria · late-payment costs not subject to VAT (damages)"); },
  get "invoice.notice.CH_DAMAGES_NOT_TAXABLE"() { return t("Schweiz · Verzugszinsen und Betreibungskosten ohne MWST", "Switzerland · default interest and collection costs without VAT"); },
  get "invoice.notice.DE_SMALL_BUSINESS"() { return t("Deutschland · Kleinunternehmer", "Germany · small business (§ 19 UStG)"); },
  get "invoice.notice.DE_REVERSE_CHARGE_13B"() { return t("Deutschland · Reverse Charge nach § 13b", "Germany · reverse charge under § 13b UStG"); },
  get "invoice.notice.AT_DOMESTIC_VAT"() { return t("Österreich · Regelbesteuerung", "Austria · standard taxation"); },
  get "invoice.notice.AT_SMALL_BUSINESS"() { return t("Österreich · Kleinunternehmer", "Austria · small business"); },
  get "invoice.notice.CH_DOMESTIC_VAT"() { return t("Schweiz · mehrwertsteuerpflichtig", "Switzerland · subject to VAT"); },
  get "invoice.notice.CH_NOT_VAT_REGISTERED"() { return t("Schweiz · nicht registriert", "Switzerland · not registered"); },
  get "invoice.notice.CH_ACQUISITION_TAX"() { return t("Schweiz · Bezugsteuer beim Empfänger", "Switzerland · acquisition tax at the recipient"); },
  get "invoice.notice.ABROAD_CH_ACQUISITION_TAX"() { return t("Aus der EU in die Schweiz · Bezugsteuer beim Empfänger", "From the EU to Switzerland · acquisition tax at the recipient"); },
  get "invoice.notice.CH_TO_DE_REVERSE_CHARGE_13B"() { return t("Aus der Schweiz nach Deutschland · Reverse Charge nach § 13b", "From Switzerland to Germany · reverse charge under § 13b UStG"); },
  get "invoice.notice.CH_TO_EU_REVERSE_CHARGE"() { return t("Aus der Schweiz in die EU · Reverse Charge", "From Switzerland to the EU · reverse charge"); },
  get "invoice.notice.EU_REVERSE_CHARGE"() { return t("EU · Reverse Charge", "EU · reverse charge"); },
  get "invoice.notice.THIRD_COUNTRY_NOT_TAXABLE"() { return t("Drittland · nicht steuerbar", "Third country · not taxable"); },
  get "invoice.notice.MANUAL_REVIEW"() { return t("Steuerliche Prüfung erforderlich", "Tax review required"); },
  get "email.content_deadline_changed"() { return t("Deadline geändert (an den Creator)", "Deadline changed (to the creator)"); },
  get "dunning.talent.reminder"() { return t("Zahlungserinnerung an den Creator (Provisionsrechnung)", "Payment reminder to the creator (commission invoice)"); },
  get "dunning.talent.notice"() { return t("Mahnung an den Creator (Provisionsrechnung)", "Dunning notice to the creator (commission invoice)"); },
  get "email.task_digest"() { return t("Tagesübersicht der Aufgaben (ans Team)", "Daily task summary (to the team)"); },
  get "portal.welcome_agency"() { return t("Begrüßung im Agentur-Portal", "Welcome in the agency portal"); },
  get "portal.welcome_talent"() { return t("Begrüßung im Creator-Portal", "Welcome in the creator portal"); },
};
/**
 * Ältere Schlüssel aus 0008, die die Fachlogik seit 0029 nicht mehr nutzt (dort unter neuem Schlüssel).
 * Eigene Fassungen dieser Schlüssel kämen nie auf eine Rechnung; die Auswahl zeigt sie deshalb nicht.
 */
export const LEGACY_TEXT_KEYS = [
  "invoice.notice.DE_STANDARD", "invoice.notice.AT_STANDARD", "invoice.notice.CH_STANDARD", "invoice.notice.CH_NOT_REGISTERED", "invoice.notice.NON_EU_NOT_TAXABLE",
];
/** Vorlagen in der Belegsprache der Agentur zuerst, dann nach Name (Einstellungen und Einrichtung gleich, Befund 24). */
export function sortTemplates<T extends { language: string; name: string }>(templates: readonly T[], docLang: string): T[] {
  return [...templates].sort((a, b) => Number(b.language === docLang) - Number(a.language === docLang) || a.name.localeCompare(b.name, "de"));
}
export function textLabel(key: string): string {
  // 0431: Fassung für Creator-Konten (versteckt, der Versand wählt sie selbst) trägt die Bezeichnung der bisherigen Fassung
  if (key.endsWith(".creator") && TEXT_LABELS[key.slice(0, -".creator".length)]) {
    return `${TEXT_LABELS[key.slice(0, -".creator".length)]} (${t("Creator-Konto", "creator account")})`;
  }
  return TEXT_LABELS[key] ?? key;
}
/** Reihenfolge in Auswahllisten: fachlich (Erinnerung vor Mahnungen), unbekannte Schlüssel am Ende. */
export function textRank(key: string): number {
  const i = Object.keys(TEXT_LABELS).indexOf(key);
  return i < 0 ? 1000 : i;
}
export function textGroup(key: string): string {
  return key.split(".")[0] ?? key;
}
/** Welche Texte gehören zur Auswahl „E-Mail-Vorlage“ der Vorlage (Vertrag, Rechnung, Mahnung, Einladung). */
export function textKind(key: string): string {
  if (key.startsWith("dunning.")) return t("Mahnung", "Dunning");
  if (key.includes("contract")) return t("Vertrag", "Contract");
  if (key.includes("invite")) return t("Einladung", "Invitation");
  if (key.includes("invoice") || key.includes("payment")) return t("Rechnung", "Invoice");
  return t("Sonstiges", "Other");
}

export function placeholdersIn(text: string): string[] {
  return [...new Set([...text.matchAll(/\{\{\s*([a-z_.]+)\s*\}\}/g)].map((m) => m[1]!))];
}
/** Platzhalter, die es in Mailtexten nicht gibt (Liste aus packages/domain/src/mail-text.ts). */
export function unknownPlaceholders(subject: string, body: string): string[] {
  const allowed = MAIL_PLACEHOLDERS as readonly string[];
  return placeholdersIn(`${subject}\n${body}`).filter((p) => !allowed.includes(p));
}
/** Platzhalter, die der Produkttext nutzt, die eigene Fassung aber nicht mehr (Hinweis, keine Sperre). */
export function droppedPlaceholders(platform: { subject: string | null; body: string }, own: { subject: string; body: string }): string[] {
  const before = placeholdersIn(`${platform.subject ?? ""}\n${platform.body}`);
  const after = new Set(placeholdersIn(`${own.subject}\n${own.body}`));
  return before.filter((p) => !after.has(p));
}

/** Beispielwerte für die Vorschau; Beträge im Format der Agentur. */
export function sampleMailValues(agencyName: string, money: (cents: number) => string, today: string): Partial<Record<MailPlaceholder, string>> {
  const dmy = (iso: string) => iso.split("-").reverse().join(".");
  return {
    "agency.name": agencyName,
    "talent.first_name": t("Vorname", "First name"),
    "brand.name": "Beispielmarke",
    "invoice.number": "RE-2026-001",
    "invoice.amount": money(119000),
    "invoice.due_date": dmy(addDays(today, -14)),
    "invoice.days_overdue": "14",
    "dunning.fee": money(4000),
    "dunning.deadline": dmy(addDays(today, 7)),
    "contract.title": "Kooperationsvertrag",
    link: "https://example.com/link",
    "deal.title": "Beispielkooperation",
    "period.month": "September 2026",
    reason: "Leistungsdatum fehlt",
    "event.title": "Abstimmung",
    "event.date": dmy(addDays(today, 3)),
  };
}
export function renderPreview(subject: string, body: string, values: Partial<Record<MailPlaceholder, string>>) {
  return renderMailText({ subject, body }, values);
}

// ── Status und Begriffe (org_status_labels) ────────────────────────────────────

export const STATUS_KEYS: { status: string; group: string; label: string }[] = [
  { status: "planning", get group() { return t("Kooperation", "Deal"); }, get label() { return t("In Planung", "In planning"); } },
  { status: "contract", get group() { return t("Kooperation", "Deal"); }, get label() { return t("Vertrag zur Unterschrift", "Contract awaiting signature"); } },
  { status: "concept", get group() { return t("Kooperation", "Deal"); }, get label() { return t("Konzept ausstehend", "Concept pending"); } },
  { status: "production", get group() { return t("Kooperation", "Deal"); }, get label() { return t("In Produktion", "In production"); } },
  { status: "content_approval", get group() { return t("Kooperation", "Deal"); }, get label() { return t("Freigabe Content", "Content approval"); } },
  { status: "content_approved", get group() { return t("Kooperation", "Deal"); }, get label() { return t("Content freigegeben", "Content approved"); } },
  { status: "published", get group() { return t("Kooperation", "Deal"); }, get label() { return t("Veröffentlicht", "Published"); } },
  { status: "reporting", get group() { return t("Kooperation", "Deal"); }, label: "Reporting" },
  { status: "invoicing", get group() { return t("Kooperation", "Deal"); }, get label() { return t("Abrechnung vorbereiten", "Prepare invoicing"); } },
  { status: "done", get group() { return t("Kooperation", "Deal"); }, get label() { return t("Abgeschlossen", "Completed"); } },
  { status: "blocked", get group() { return t("Kooperation", "Deal"); }, get label() { return t("Blockiert", "Blocked"); } },
  { status: "open", get group() { return t("Monat", "Month"); }, get label() { return t("Offen", "Open"); } },
  { status: "invoice_requested", get group() { return t("Monat", "Month"); }, get label() { return t("Rechnung angefordert", "Invoice requested"); } },
  { status: "invoiced", get group() { return t("Monat", "Month"); }, get label() { return t("Abgerechnet", "Invoiced"); } },
  { status: "draft", get group() { return t("Rechnung", "Invoice"); }, get label() { return t("Entwurf", "Draft"); } },
  { status: "submitted", get group() { return t("Rechnung", "Invoice"); }, get label() { return t("Eingereicht", "Submitted"); } },
  { status: "revision_requested", get group() { return t("Rechnung", "Invoice"); }, get label() { return t("Korrektur angefordert", "Correction requested"); } },
  { status: "rejected", get group() { return t("Rechnung", "Invoice"); }, get label() { return t("Abgelehnt", "Declined"); } },
  { status: "approved", get group() { return t("Rechnung", "Invoice"); }, get label() { return t("Freigegeben", "Approved"); } },
  { status: "sent", get group() { return t("Rechnung", "Invoice"); }, get label() { return t("An Marke versendet", "Sent to brand"); } },
  { status: "paid", get group() { return t("Monat und Rechnung", "Month and invoice"); }, get label() { return t("Bezahlt", "Paid"); } },
  { status: "cancelled", get group() { return t("Alle", "All"); }, get label() { return t("Storniert", "Cancelled"); } },
  { status: "withdrawn", get group() { return t("Rechnung", "Invoice"); }, get label() { return t("Zurückgezogen", "Withdrawn"); } },
  { status: "discarded", get group() { return t("Rechnung", "Invoice"); }, get label() { return t("Verworfen", "Discarded"); } },
];

// ── Artikelkatalog (invoice_articles) ──────────────────────────────────────────

export const ARTICLE_CATEGORIES = ["Content", "Nutzungsrechte", "Reise/Auslagen", "Event", "Beratung", "Sonstiges"] as const;
export const ARTICLE_UNITS = ["Pauschal", "Stück", "Tag", "Monat", "Stunde"] as const;

export interface ArticleDraft {
  name: string;
  description: string;
  category: string;
  price: string;
  currency: string;
  unit: string;
  commissionable: boolean;
  active: boolean;
  sort_order: string;
}
export interface ArticleRow {
  name: string;
  description: string | null;
  category: string | null;
  default_price_cents: number | null;
  currency: string | null;
  unit: string | null;
  commissionable: boolean;
  active: boolean;
  sort_order: number;
}
export function emptyArticle(currency: string): ArticleDraft {
  return { name: "", description: "", category: "", price: "", currency, unit: "Pauschal", commissionable: true, active: true, sort_order: "100" };
}
export function validateArticle(d: ArticleDraft): { row: ArticleRow | null; errors: Record<string, string> } {
  const c = new Check();
  const name = c.text("name", d.name, { label: t("eine Bezeichnung", "a name"), required: true, max: 120 });
  const description = c.text("description", d.description, { label: t("Beschreibung", "Description"), max: 1000 });
  const category = c.text("category", d.category, { label: t("Kategorie", "Category"), max: 60 });
  const price = c.cents("price", d.price, { label: t("Standardpreis", "Default price") });
  const unit = c.text("unit", d.unit, { label: t("Einheit", "Unit"), max: 20 });
  const sort = c.int("sort_order", d.sort_order || "100", { label: t("Reihenfolge", "Order"), min: -1000, max: 100000 });
  const currency = (CURRENCIES as readonly string[]).includes(d.currency) ? d.currency : null;
  if (price !== null && !currency) c.fail("currency", t("Bitte eine Währung wählen.", "Please choose a currency."));
  if (!c.ok || name === null) return { row: null, errors: c.errors };
  return {
    row: { name, description, category, default_price_cents: price, currency, unit, commissionable: d.commissionable, active: d.active, sort_order: sort ?? 100 },
    errors: c.errors,
  };
}

// ── Team & Rechte ──────────────────────────────────────────────────────────────

export const RIGHTS = [
  { key: "can_approve_invoices", get label() { return t("Rechnungen freigeben", "Approve invoices"); } },
  { key: "can_send_invoices", get label() { return t("An Marke senden", "Send to brand"); } },
  { key: "can_dun", get label() { return t("Mahnen", "Send dunning notices"); } },
  { key: "can_confirm_payments", get label() { return t("Zahlungen bestätigen", "Confirm payments"); } },
  { key: "can_trigger_payouts", get label() { return t("Auszahlungen ausführen", "Make payouts"); } },
  { key: "can_sign_contracts", get label() { return t("Verträge gegenzeichnen", "Countersign contracts"); } },
  { key: "can_edit_templates", get label() { return t("Vorlagen bearbeiten", "Edit templates"); } },
  { key: "can_edit_contract_text", get label() { return t("Vertragstext ändern", "Change contract text"); } },
  { key: "can_view_financials", get label() { return t("Kennzahlen und Agenturumsatz sehen", "View metrics and agency revenue"); } },
  { key: "can_view_amounts", get label() { return t("Beträge sehen", "View amounts"); } },
] as const;
export type RightKey = (typeof RIGHTS)[number]["key"];

/** Was ein Recht genau freigibt (Hinweis am Schalter, Befund 9). */
export const RIGHT_HINTS: Partial<Record<RightKey, string>> = {
  get can_edit_templates() { return t("Vertragsvorlagen und Klauseln bauen, CreatorOffice-Vorlagen übernehmen, neue Versionen anlegen und veröffentlichen. Bestehende Verträge ändern sich dadurch nie.", "Build contract templates and clauses, adopt CreatorOffice templates, create and publish new versions. Existing contracts never change as a result."); },
  get can_edit_contract_text() { return t("Den Text eines einzelnen Vertragsentwurfs abweichend von der Vorlage ändern und so versenden. Die Abweichung bleibt am Vertrag markiert. Felder füllen, Unterzeichner und Laufzeit gehen auch ohne dieses Recht.", "Change the text of a single contract draft so that it differs from the template, and send it that way. The deviation stays marked on the contract. Filling in fields, signers and term work without this permission."); },
  get can_view_financials() { return t("Übersicht mit Agenturumsatz, Kennzahlen-Seite, Agentur-Einnahmen und Umsätze im Creator-Profil, in der Creator- und Markenliste und bei den Marken. Setzt „Beträge sehen“ voraus.", "Overview with agency revenue, metrics page, agency shares and revenue in the creator profile, in the creator and brand lists and for brands. Requires “View amounts”."); },
  get can_view_amounts() { return t("Honorare, Budgets, Provisionen und Umsätze in Listen, Profilen und Kooperationen. Aus: die Person sieht Stammdaten, Kooperationen und Termine ohne Geldangaben (z. B. Assistenz); Rechnungen, Mahnungen und Auszahlungen sind dann nicht zugänglich.", "Fees, budgets, commissions and revenue in lists, profiles and deals. Off: the person sees master data, deals and appointments without monetary details (e.g. assistant); invoices, dunning and payouts are then not accessible."); },
};

/**
 * Rechte-Vorlagen beim Einladen: typische Rollen in kleinen Agenturen. Der Admin kann jede Vorlage anpassen;
 * „Eigene Auswahl“ lässt die Häkchen, wie sie sind.
 */
export const RIGHTS_PRESETS: { key: string; label: string; text: string; rights: Omit<RightsState, "role"> }[] = [
  {
    key: "assistenz", get label() { return t("Assistenz", "Assistant"); }, get text() { return t("Nur zugewiesene Creator, keine Rechnungen, Auszahlungen und Finanzaktionen; Honorare und Budgets bleiben verborgen, auch in der Datenbank.", "Assigned creators only, no invoices, payouts or financial actions; fees and budgets stay hidden, in the database as well."); },
    rights: { sees_all_talents: false, can_approve_invoices: false, can_send_invoices: false, can_dun: false, can_confirm_payments: false, can_trigger_payouts: false, can_sign_contracts: false, can_edit_templates: false, can_edit_contract_text: false, can_view_financials: false, can_view_amounts: false },
  },
  {
    key: "talent", get label() { return t("Talent-Management", "Talent management"); }, get text() { return t("Zugewiesene Creator mit Beträgen; Rechnungen prüfen und freigeben, aber nicht versenden, mahnen oder auszahlen.", "Assigned creators with amounts; review and approve invoices, but not send, dun or pay out."); },
    rights: { sees_all_talents: false, can_approve_invoices: true, can_send_invoices: false, can_dun: false, can_confirm_payments: false, can_trigger_payouts: false, can_sign_contracts: false, can_edit_templates: false, can_edit_contract_text: false, can_view_financials: false, can_view_amounts: true },
  },
  {
    key: "buchhaltung", get label() { return t("Buchhaltung", "Accounting"); }, get text() { return t("Alle Creator, Rechnungen senden, mahnen, Zahlungen bestätigen, auszahlen und Kennzahlen sehen; keine Verträge.", "All creators, send invoices, send dunning notices, confirm payments, pay out and view metrics; no contracts."); },
    rights: { sees_all_talents: true, can_approve_invoices: true, can_send_invoices: true, can_dun: true, can_confirm_payments: true, can_trigger_payouts: true, can_sign_contracts: false, can_edit_templates: false, can_edit_contract_text: false, can_view_financials: true, can_view_amounts: true },
  },
];
/** Welche Vorlage passt genau zu den Rechten? Sonst null („Eigene Auswahl“). */
export function presetOf(rights: Omit<RightsState, "role">): string | null {
  return RIGHTS_PRESETS.find((p) => (Object.keys(p.rights) as (keyof typeof p.rights)[]).every((k) => p.rights[k] === rights[k]))?.key ?? null;
}
/** Rechte, die „Beträge sehen“ voraussetzen: Kennzahlen und alles rund um Rechnungen, Zahlungen und Auszahlungen (RECHTE-6). */
export const NEEDS_AMOUNTS = ["can_view_financials", "can_approve_invoices", "can_send_invoices", "can_dun", "can_confirm_payments", "can_trigger_payouts"] as const;
export const needsAmounts = (key: string): boolean => (NEEDS_AMOUNTS as readonly string[]).includes(key);
/** Wer keine Beträge sieht, sieht auch keinen Agenturumsatz und hat keine Rechnungs- und Zahlungsrechte. */
export function normalizeRights<T extends Partial<Omit<RightsState, "role">>>(r: T): T {
  if (r.can_view_amounts !== false) return r;
  const next = { ...r } as Record<string, unknown>;
  for (const k of NEEDS_AMOUNTS) if (next[k]) next[k] = false;
  return next as T;
}

/** Befugnisse der Rolle Admin in Klartext (Bestätigung bei Beförderung und Übergabe, Befund 20). */
export const CHEF_POWERS = () => t("Admins sehen alle Creator und Beträge, verwalten Einstellungen, Team und Abo, können andere Admins herabstufen, die Agentur kündigen und die Löschung aller Daten beantragen.", "Admins see all creators and amounts, manage settings, team and subscription, can demote other admins, terminate the agency and request deletion of all data.");

export interface RightsState {
  role: "chef_manager" | "manager";
  sees_all_talents: boolean;
  can_approve_invoices: boolean;
  can_send_invoices: boolean;
  can_dun: boolean;
  can_confirm_payments: boolean;
  can_trigger_payouts: boolean;
  can_sign_contracts: boolean;
  can_edit_templates: boolean;
  can_edit_contract_text: boolean;
  can_view_financials: boolean;
  can_view_amounts: boolean;
}
const RIGHTS_FIELDS: (keyof RightsState)[] = ["role", "sees_all_talents", ...RIGHTS.map((r) => r.key)];

/** Nur geänderte Felder; nichts geändert: null. */
export function rightsPatch(before: RightsState, after: RightsState): Partial<RightsState> | null {
  const patch: Partial<RightsState> = {};
  for (const f of RIGHTS_FIELDS) if (before[f] !== after[f]) (patch as Record<string, unknown>)[f] = after[f];
  return Object.keys(patch).length ? patch : null;
}

export function assignmentDiff(before: readonly string[], after: readonly string[]): { add: string[]; remove: string[] } {
  const b = new Set(before), a = new Set(after);
  return { add: [...a].filter((x) => !b.has(x)), remove: [...b].filter((x) => !a.has(x)) };
}

/** Anwesenheit eines Talents (talent_presence, AD-211). */
export function presenceLabel(lastSeen: string | null | undefined, nowMs: number, formatDate: (iso: string) => string): string {
  if (!lastSeen) return t("Noch nicht angemeldet", "Not signed in yet");
  const diff = nowMs - Date.parse(lastSeen);
  if (!Number.isFinite(diff)) return "–";
  const min = Math.floor(diff / 60000);
  if (min < 5) return t("Gerade online", "Online now");
  if (min < 60) return t(`vor ${min} Minuten`, `${min} minutes ago`);
  const h = Math.floor(min / 60);
  if (h < 24) return h === 1 ? t("vor 1 Stunde", "1 hour ago") : t(`vor ${h} Stunden`, `${h} hours ago`);
  return `am ${formatDate(lastSeen)}`;
}

// ── Abo & Tarif ────────────────────────────────────────────────────────────────

/** Grenzen und Module des Tarifs (Abo-Seite: Module und Tarifvergleich; Gruppen in model/tarif.ts TARIF_GRUPPEN). */
export const ENTITLEMENT_LABELS: Record<string, string> = {
  max_talents: "Creator",
  get max_seats() { return t("Team-Plätze", "Team seats"); },
  get max_contracts_per_month() { return t("Verträge pro Monat", "Contracts per month"); },
  get storage_gb() { return t("Speicher in GB", "Storage in GB"); },
  get module_insights() { return t("Instagram-Insights", "Instagram insights"); },
  get module_custom_domain() { return t("Eigene Domain", "Custom domain"); },
  get module_whatsapp() { return t("WhatsApp-Hinweise", "WhatsApp notices"); },
  // Tarifstaffel (0099)
  get module_dunning_custom() { return t("Eigene Mahngebühren und Mahntexte", "Custom dunning fees and texts"); },
  get module_rights() { return t("Team-Rechte und Sichtbereiche", "Team permissions and visibility"); },
  get module_payouts() { return t("Auszahlungsläufe und Gutschriften", "Payout runs and self-billing"); },
  get module_accounting_export() { return t("DATEV- und BMD-Export", "DATEV and BMD export"); },
  get module_pipeline() { return t("Anfragen und Angebote", "Inquiries and offers"); },
  get module_media_kit() { return t("Media-Kits und Pitch-Listen", "Media kits and pitch lists"); },
  get module_bank_sync() { return t("Kontoauszug-Import und Liquiditätsvorschau", "Bank statement import and liquidity forecast"); },
  get module_sender_domain() { return t("Eigene Absenderdomain", "Custom sender domain"); },
  get module_mail_unbranded() { return t("Mails ohne CreatorOffice-Hinweis", "Emails without the CreatorOffice note"); },
  get module_brand_review() { return t("Freigabe-Link an die Marke", "Approval link for the brand"); },
};
/** Was ein Modul in der Oberfläche freischaltet (Modul-Panel der Abo-Seite). */
export const MODULE_HINTS: Record<string, string> = {
  get module_insights() { return t("Instagram-Kennzahlen, Verbinden und Freigaben an Marken", "Instagram metrics, connecting and shares with brands"); },
  get module_whatsapp() { return t("Hinweise an Creator per WhatsApp", "Notices to creators via WhatsApp"); },
  get module_dunning_custom() { return t("Gebühren je Stufe, Pauschale, Verzugszinsen und eigene Mahntexte; Stufen und Fristen legst du in jedem Tarif fest", "Fees per level, flat fee, default interest and custom dunning texts; you set levels and deadlines in every plan"); },
  get module_rights() { return t("Rechte einzelner Manager einschränken, Sichtbereich und Zuweisungen; ohne das Modul haben alle volle Rechte", "Restrict individual managers' permissions, visibility and assignments; without the module everyone has full permissions"); },
  get module_payouts() { return t("Auszahlungsläufe mit Bankdatei und Gutschriftverfahren; die Einzel-Auszahlung an der Rechnung geht immer", "Payout runs with bank file and self-billing; the single payout on the invoice always works"); },
  get module_accounting_export() { return t("DATEV-Buchungsstapel bzw. BMD-Buchungen für die Kanzlei; der allgemeine Datenexport geht immer", "DATEV posting batch or BMD bookings for the accounting firm; the general data export always works"); },
  get module_pipeline() { return t("Neue Anfragen und Angebote an Marken", "New inquiries and offers to brands"); },
  get module_media_kit() { return t("Neue Media-Kit-Links, Pitch-Listen und ihr Versand", "New media kit links, pitch lists and sending them"); },
  get module_bank_sync() { return t("Kontoauszug hochladen und Liquiditätsvorschau; bereits importierte Zeilen lassen sich immer zuordnen", "Upload bank statements and liquidity forecast; already imported lines can always be matched"); },
  get module_sender_domain() { return t("Mails von der eigenen Domain der Agentur", "Emails from the agency's own domain"); },
  get module_mail_unbranded() { return t("Das Band „Erstellt mit CreatorOffice“ am Ende eurer Mails lässt sich ausblenden", "The “Made with CreatorOffice” strip at the end of your emails can be hidden"); },
  get module_brand_review() { return t("Entwürfe per Link an die Marke zur Freigabe schicken, mit Beweiskette; bestehende Links bleiben gültig", "Send drafts to the brand for approval via link, with an audit trail; existing links stay valid"); },
};

export interface Usage {
  text: string;
  percent: number;
  unlimited: boolean;
  full: boolean;
}
/** Verbrauch gegen Tarifgrenze; -1 = unbegrenzt. */
/**
 * Was ein Tarif bei der heutigen Nutzung nicht erlaubt (wie app.plan_overage, 0077): aktive Creator und Team-Plätze.
 * Solche Tarife sind in „Tarif wählen“ gesperrt (RECHTE-5).
 */
export function tarifUeberschreitung(used: { talents: number; seats: number }, entitlements: readonly { plan_key: string; key: string; value: number }[], planKey: string,
  opts: { storageBytes?: number | null; extras?: { seats?: number | null; talents?: number | null } } = {}): string[] {
  const max = (k: string) => entitlements.find((e) => e.plan_key === planKey && e.key === k)?.value;
  const out: string[] = [];
  // 0157: gebuchte Zusatzpakete zählen auf die Grenzen (wie app.billing_overage)
  const tl0 = max("max_talents");
  const tl = tl0 !== undefined && tl0 >= 0 ? tl0 + Math.max(0, opts.extras?.talents ?? 0) : tl0;
  if (tl !== undefined && tl >= 0 && used.talents > tl) out.push(t(`${used.talents} aktive Creator, der Tarif erlaubt ${tl}`, `${used.talents} active creators, the plan allows ${tl}`));
  const m0 = max("max_seats");
  const m = m0 !== undefined && m0 >= 0 ? m0 + Math.max(0, opts.extras?.seats ?? 0) : m0;
  if (m !== undefined && m >= 0 && used.seats > m) out.push(t(`${used.seats} Team-Plätze, der Tarif erlaubt ${m}`, `${used.seats} team seats, the plan allows ${m}`));
  const gb = max("storage_gb");
  const bytes = opts.storageBytes ?? null;
  if (gb !== undefined && gb >= 0 && bytes !== null && bytes > gb * 1024 ** 3) {
    const belegt = (Math.round((bytes / 1024 ** 3) * 10) / 10).toLocaleString(currentLang() === "en" ? "en-GB" : "de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    out.push(t(`${belegt} GB Speicher belegt, der Tarif erlaubt ${gb} GB`, `${belegt} GB of storage used, the plan allows ${gb} GB`));
  }
  return out;
}

// ── Abo-Selbstbedienung (0157) ──────────────────────────────────────────────────

export type AboIntervall = "month" | "year";
type KatalogTarif = { key: string; name: string; price_month_cents: number; price_year_cents: number };
type KatalogZusatz = { key: string; entitlement_key: string; price_month_cents: number; price_year_cents: number };

/** Preis eines Tarifs bzw. Zusatzpakets je Intervall (netto, Cent). */
export function intervallPreis(p: { price_month_cents: number; price_year_cents: number }, interval: AboIntervall): number {
  return interval === "year" ? p.price_year_cents : p.price_month_cents;
}

/**
 * Wann gilt ein Tarifwechsel bei laufendem Stripe-Abo (wie changeTiming in packages/server/src/billing-self-service.ts)?
 * Größerer Tarif (höherer Monatspreis): sofort und anteilig. Kleinerer Tarif und bloßer Wechsel der Abrechnung: zum Laufzeitende.
 */
export function wechselZeitpunkt(current: { price_month_cents: number } | null | undefined, target: { price_month_cents: number }): "now" | "period_end" {
  if (!current) return "now";
  return target.price_month_cents > current.price_month_cents ? "now" : "period_end";
}

export interface GrenzAenderung {
  key: "max_talents" | "max_seats" | "max_contracts_per_month" | "storage_gb";
  vorher: number | null;
  nachher: number | null;
  richtung: "mehr" | "weniger" | "gleich";
}
export interface WechselVorschau {
  /** unchanged: nichts zu tun · new: erster Abschluss · now: Upgrade sofort · period_end: Downgrade bzw. andere Abrechnung zum Laufzeitende */
  art: "unchanged" | "new" | "now" | "period_end";
  /** Ab wann (ISO); null = sofort bzw. mit der Zahlung */
  ab: string | null;
  preisVorher: number | null;
  intervallVorher: AboIntervall | null;
  preisNachher: number;
  intervallNachher: AboIntervall;
  grenzen: GrenzAenderung[];
  moduleDazu: string[];
  moduleWeg: string[];
  /** Was der neue Tarif bei der heutigen Nutzung nicht erlaubt (Creator, Plätze, Speicher) */
  ueber: string[];
  /** Grobe Schätzung des anteiligen Betrags heute (nur Upgrade mit gleicher Abrechnung); den genauen Betrag rechnet Stripe */
  anteilig: number | null;
  /** K-501: Upgrade mit anderer Abrechnung; der Tarif gilt sofort in der bisherigen Abrechnung, die neue ab diesem Tag */
  umstellungAb: string | null;
  /** Preis des Zieltarifs (mit Zusatzpaketen) in der bisherigen Abrechnung, solange die neue noch nicht gilt */
  preisSofort: number | null;
}

const GRENZ_REIHE = ["max_talents", "max_seats", "max_contracts_per_month", "storage_gb"] as const;
const groesser = (a: number | null, b: number | null) => (a === null || b === null ? 0 : a === b ? 0 : a < 0 ? 1 : b < 0 ? -1 : a > b ? 1 : -1);

/**
 * Was sich bei einem Tarifwechsel ändert: Grenzen (mit gebuchten Zusatzpaketen, die beim Wechsel mitwandern), Module, Preis
 * (Tarif plus Zusatzpakete) und ab wann. Grundlage für den Dialog „Tarif wählen“ und die Bestätigung.
 */
export function wechselVorschau(input: {
  current: { plan_key: string; billing_interval: string; current_period_end: string | null; status: string } | null;
  /** läuft ein Abo über Stripe (active/trialing)? Dann Wechsel, sonst erster Abschluss */
  stripeLaeuft: boolean;
  plans: readonly KatalogTarif[];
  entitlements: readonly { plan_key: string; key: string; value: number }[];
  addons: readonly KatalogZusatz[];
  extras: { seats: number; talents: number };
  target: string;
  interval: AboIntervall;
  used: { talents: number; seats: number };
  storageBytes: number | null;
  nowMs: number;
}): WechselVorschau | null {
  const target = input.plans.find((p) => p.key === input.target);
  if (!target) return null;
  const cur = input.current ? input.plans.find((p) => p.key === input.current!.plan_key) ?? null : null;
  const curInterval: AboIntervall | null = input.current ? (input.current.billing_interval === "year" ? "year" : "month") : null;
  const ent = (plan: string | undefined, k: string) => plan ? input.entitlements.find((e) => e.plan_key === plan && e.key === k)?.value ?? null : null;
  const plus = (k: string) => (k === "max_seats" ? input.extras.seats : k === "max_talents" ? input.extras.talents : 0);
  const mitZusatz = (v: number | null, k: string) => (v === null || v < 0 ? v : v + Math.max(0, plus(k)));
  const zusatzPreis = (interval: AboIntervall) => input.addons.reduce((sum, a) =>
    sum + intervallPreis(a, interval) * Math.max(0, a.entitlement_key === "max_seats" ? input.extras.seats : a.entitlement_key === "max_talents" ? input.extras.talents : 0), 0);
  const grenzen: GrenzAenderung[] = GRENZ_REIHE.map((k) => {
    const vorher = mitZusatz(ent(cur?.key, k), k);
    const nachher = mitZusatz(ent(target.key, k), k);
    const g = groesser(nachher, vorher);
    return { key: k, vorher, nachher, richtung: g > 0 ? "mehr" : g < 0 ? "weniger" : "gleich" };
  });
  const module = [...new Set(input.entitlements.filter((e) => e.key.startsWith("module_")).map((e) => e.key))];
  const an = (plan: string | undefined, k: string) => (ent(plan, k) ?? 0) >= 1;
  const moduleDazu = module.filter((k) => an(target.key, k) && !an(cur?.key, k));
  const moduleWeg = cur ? module.filter((k) => an(cur.key, k) && !an(target.key, k)) : [];
  const ueber = tarifUeberschreitung(input.used, input.entitlements, target.key, { storageBytes: input.storageBytes, extras: input.extras });
  const preisNachher = intervallPreis(target, input.interval) + zusatzPreis(input.interval);
  const preisVorher = cur && curInterval ? intervallPreis(cur, curInterval) + zusatzPreis(curInterval) : null;
  const gleich = !!input.current && input.current.plan_key === target.key && curInterval === input.interval;
  let art: WechselVorschau["art"] = "new";
  let ab: string | null = null;
  let anteilig: number | null = null;
  let umstellungAb: string | null = null;
  let preisSofort: number | null = null;
  if (gleich) art = "unchanged";
  else if (input.stripeLaeuft) {
    art = wechselZeitpunkt(cur, target);
    const end = input.current?.current_period_end ?? null;
    if (art === "period_end") ab = end;
    else if (curInterval && preisVorher !== null) {
      // K-501 (wie billing-change): bei anderer Abrechnung gilt der Tarif sofort in der bisherigen, die neue ab dem Laufzeitende
      if (curInterval !== input.interval) umstellungAb = end;
      preisSofort = intervallPreis(target, curInterval) + zusatzPreis(curInterval);
      const rest = end ? Date.parse(end) - input.nowMs : 0;
      const laenge = (curInterval === "year" ? 365.25 : 30.44) * 86400000;
      if (rest > 0) anteilig = Math.max(0, Math.round(((preisSofort - preisVorher) * Math.min(rest, laenge)) / laenge));
    }
  }
  return { art, ab, preisVorher, intervallVorher: curInterval, preisNachher, intervallNachher: input.interval, grenzen, moduleDazu, moduleWeg, ueber, anteilig, umstellungAb, preisSofort };
}

/** Testphase in Zahlen: Tage insgesamt (ab Anlage der Agentur), vergangen, übrig, Anteil für den Balken. */
export function testphaseStand(createdAt: string | null | undefined, endsAt: string | null | undefined, nowMs: number):
  { tageGesamt: number; tageUebrig: number; tagHeute: number; tageNoch: number; prozent: number; abgelaufen: boolean } | null {
  if (!endsAt) return null;
  const end = Date.parse(endsAt);
  if (!Number.isFinite(end)) return null;
  const start = createdAt ? Date.parse(createdAt) : NaN;
  const tag = 86400000;
  const gesamt = Number.isFinite(start) && start < end ? Math.max(1, Math.round((end - start) / tag)) : Math.max(1, Math.ceil((end - nowMs) / tag));
  const uebrig = Math.max(0, Math.ceil((end - nowMs) / tag));
  const heute = Math.min(gesamt, Math.max(1, gesamt - uebrig + 1));
  // F (K-479): „Tag n von 14 (noch 14 − n Tage)“: Resttage nach dem heutigen Tag, überall gleich gezählt (tageUebrig zählt den
  // heutigen Tag mit und bleibt für Schwellen wie „bald“)
  return { tageGesamt: gesamt, tageUebrig: uebrig, tagHeute: heute, tageNoch: Math.max(0, gesamt - heute), prozent: Math.min(100, Math.max(0, Math.round(((gesamt - uebrig) / gesamt) * 100))), abgelaufen: end <= nowMs };
}

export function usage(used: number, max: number | null | undefined): Usage {
  if (max === null || max === undefined) return { text: `${used} / –`, percent: 0, unlimited: false, full: false };
  if (max < 0) return { text: `${used} / unbegrenzt`, percent: 0, unlimited: true, full: false };
  const percent = max === 0 ? 100 : Math.min(100, Math.round((used / max) * 100));
  return { text: `${used} / ${max}`, percent, unlimited: false, full: used >= max };
}

export const SUBSCRIPTION_STATUS: Record<string, string> = {
  get trialing() { return t("Testphase", "trial"); },
  get active() { return t("Aktiv", "Active"); },
  get past_due() { return t("Zahlung offen", "Payment outstanding"); },
  get cancelled() { return t("Gekündigt", "Terminated"); },
};
export const ORG_STATUS: Record<string, string> = {
  get trial() { return t("Testphase", "trial"); },
  get active() { return t("Aktiv", "Active"); },
  get past_due() { return t("Zahlung offen", "Payment outstanding"); },
  get suspended() { return t("Gesperrt", "Suspended"); },
  get cancelled() { return t("Gekündigt", "Terminated"); },
};

// ── Export ──────────────────────────────────────────────────────────────────────

export type ExportRange = "all" | "year" | "custom";

// ── Lebenszyklus: Kündigung, Nur-Lesen-Phase, Löschplan (org_lifecycle_status, 0042) ──────────────

/**
 * Die Wahl entspricht dem laufenden Abo (gleicher Tarif, gleiches Intervall, Abo aktiv und nicht gekündigt): dann gibt es
 * nichts anzufragen (SICHT-AGENTUR-REST-5; die Datenbank weist dieselbe Anfrage seit 0081 ab). Nach einer Kündigung bleibt
 * die Anfrage möglich (Weiterführen), ebenso in der Testphase und bei offener Zahlung.
 */
export function sameAsCurrentSubscription(
  sub: { plan_key: string; status: string; billing_interval: string } | null | undefined,
  plan: string, interval: "month" | "year", cancelRequestedAt: string | null | undefined,
): boolean {
  return !!sub && sub.status === "active" && !cancelRequestedAt && sub.plan_key === plan && sub.billing_interval === interval;
}

export interface LifecycleStatus {
  status: string;
  /** 0590: Zahlungsart des Stripe-Abos (charge_automatically = Karte, send_invoice = auf Rechnung) */
  collection_method?: "charge_automatically" | "send_invoice" | null;
  read_only: boolean;
  /** 0441: Anbieterwechsel läuft (bis dahin kein Vertragsende, nicht nur lesbar wegen des Vertragsendes) */
  switching?: boolean;
  switching_until?: string | null;
  reason: "cancelled" | "trial_expired" | "suspended" | "paused" | null;
  is_chef: boolean;
  trial_ends_at: string | null;
  subscription_status: string | null;
  current_period_end: string | null;
  cancel_requested_at: string | null;
  cancel_effective_at: string | null;
  cancel_reason: string | null;
  early_purge_requested_at: string | null;
  purge_notice_sent_at: string | null;
  plan: {
    read_only_since?: string; retention_years?: number; min_days?: number; notice_days?: number; last_document?: string | null;
    retention_until?: string | null; purge_at?: string; notice_at?: string;
    /** 0057: Aufbewahrung je Sitzland und Reduktion auf die Belege */
    country?: string | null; retention_letters_years?: number; last_book?: string | null; last_letter?: string | null;
    books_until?: string | null; letters_until?: string | null; retained_invoices?: number; retained_contracts?: number;
    reduce_at?: string | null; reduced_at?: string | null; next_action?: "reduce" | "purge" | "hold"; next_at?: string;
    /** 0444 (Steuerprüfung L17): Zahlungsbuchungen als Aufzeichnungen, Verträge zu Rechnungen, Sperre „Aufbewahrung verlängert“ */
    retention_records_years?: number; records_until?: string | null; contracts_linked_until?: string | null; retained_contracts_linked?: number;
    retention_hold?: boolean; retention_hold_since?: string | null;
  } | null;
  can_cancel: boolean;
  can_withdraw: boolean;
  can_request_early_purge: boolean;
  can_withdraw_early_purge: boolean;
  /** 0057 */
  suspended_reason?: string | null;
  suspended_at?: string | null;
  plan_key?: string | null;
  billing_interval?: string | null;
  stripe_managed?: boolean;
  announced_action?: "reduce" | "purge" | null;
  reduced_at?: string | null;
  open_request?: { id: string; plan_key: string; billing_interval: "month" | "year"; note: string | null; created_at: string } | null;
  can_request_plan?: boolean;
  can_choose_trial_plan?: boolean;
  /** 0157: letzte gescheiterte Zahlung (nur Admins), Kundenportal möglich, vorgemerkter Wechsel, Kündigung im Kundenportal */
  payment_failed_at?: string | null;
  billing_portal?: boolean;
  pending_plan_key?: string | null;
  pending_interval?: "month" | "year" | null;
  pending_change_at?: string | null;
  cancel_at_period_end?: boolean;
  extra_seats?: number;
  extra_talents?: number;
  /** Abo über Stripe abgeschlossen, Stripe bucht erst zum Ende der Testphase */
  stripe_trial?: boolean;
  /** 0372: Tage Test extra beim ersten Hinterlegen der Zahlungsart (0 = schon erhalten bzw. nicht im Test) */
  payment_bonus_days?: number | null;
  /** 0372: gewährte Bonustage und ob sie gerade zum Testende zählen (aus, solange automatisch weiterlaufen ausgeschaltet ist) */
  payment_bonus_granted_days?: number | null;
  payment_bonus_active?: boolean | null;
  /** 0169: neue Agentur ohne Stripe-Abschluss: erst Zahlungsmethode hinterlegen bzw. kaufen */
  needs_payment_method?: boolean;
  /** 0205: Zahlungsmethode für die laufende Testphase überspringbar bzw. übersprungen; Testkonto sofort löschbar */
  can_skip_payment?: boolean;
  payment_skipped?: boolean;
  can_delete_trial?: boolean;
  /** 0207: Löschen geht immer (sofort beim Testkonto, sonst vorgemerkt zum Ende der Laufzeit) */
  can_delete_account?: boolean;
  delete_mode?: "sofort" | "geplant";
  /** 0173: Abo über PayPal */
  paypal_managed?: boolean;
  payment_provider?: "stripe" | "paypal";
  /** 0415: gekündigt oder gesperrt (read_only gilt auch während einer Pause) */
  status_read_only?: boolean;
  /** 0414/0415: Abo-Pause (nur Admins): laufend bzw. vorgemerkt, ob möglich und warum nicht */
  pause?: PauseStand | null;
  /** 0415: Rückhalte-Rabatt schon genutzt (einmal je Konto) */
  retention_used?: boolean | null;
}

/** Stand der Abo-Pause (app.pause_state, 0414) */
export interface PauseStand {
  active: boolean;
  scheduled: boolean;
  starts_at: string | null;
  ends_at: string | null;
  months: number | null;
  provider: "stripe" | "ohne" | null;
  can_pause: boolean;
  unavailable: "aus" | "test" | "kein_abo" | "verbund" | "gekuendigt" | "paypal" | "jahresabo" | "wechsel_vorgemerkt" | "genutzt" | "schon_pausiert" | "abo_unklar" | null;
  next_possible_at: string | null;
  /** Beginn einer neuen Pause (Ende des bezahlten Monats) */
  start_if_now: string | null;
  /** 0419: in der Pause angenommener Rückhalte-Rabatt, gilt ab dem Ende der Pause */
  retention_pending?: boolean;
}

/** 0423: Stand des Rückhalte-Rabatts für die Abo-Seite (public.org_retention_info) */
export interface RetentionInfo {
  state: string | null;
  /** Altfall 3M-Gutschein: Ende laut Stripe */
  until?: string | null;
  /** 4M-Gutschein: noch offene rabattierte Rechnungen und ihre Daten */
  left?: number | null;
  invoice_dates?: string[] | null;
  /** in der Pause vorgemerkt: Ende der Pause */
  from?: string | null;
}

/**
 * P-9/0424: in der Pause vorgemerkter Rückhalte-Rabatt. Wurde ein laufender Rabatt zum Pausenbeginn abgenommen, gilt nach der Pause
 * nur noch der Rest (left 1 oder 2), sonst drei Monatsrechnungen.
 */
export function rabattVorgemerktText(ende: string, left?: number | null): string {
  if (left === 1) return t(`50 % Rabatt vorgemerkt: gilt ab dem Ende der Pause (${ende}) für noch eine Monatsrechnung.`,
    `50% discount reserved: applies from the end of the pause (${ende}) to one more monthly invoice.`);
  if (left === 2) return t(`50 % Rabatt vorgemerkt: gilt ab dem Ende der Pause (${ende}) für noch zwei Monatsrechnungen.`,
    `50% discount reserved: applies from the end of the pause (${ende}) to two more monthly invoices.`);
  return t(`50 % Rabatt vorgemerkt: gilt ab dem Ende der Pause (${ende}) für drei Monatsrechnungen.`,
    `50% discount reserved: applies from the end of the pause (${ende}) to three monthly invoices.`);
}

/** Aufzählung „a, b und c“ (Bindewort je Sprache) */
export function aufzaehlung(xs: string[], und: string): string {
  return xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} ${und} ${xs[xs.length - 1]}`;
}

/**
 * 0423: „50 % Rabatt auf die Rechnungen am 21.11., 21.12. und 21.01.“; nach der ersten rabattierten Rechnung „… noch auf die
 * Rechnungen am …“, zuletzt „… noch auf die Rechnung am …“. null ohne Daten (dann gilt der bisherige Hinweis).
 */
export function rabattRechnungenText(info: RetentionInfo | null | undefined, timeZone: string): string | null {
  const daten = (info?.invoice_dates ?? []).filter((d) => !Number.isNaN(Date.parse(d)));
  if (info?.state !== "active" || !daten.length) return null;
  const de = daten.map((d) => new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", timeZone }).format(new Date(d)));
  const en = daten.map((d) => new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone }).format(new Date(d)));
  const alle = (info.left ?? daten.length) >= 3;
  // das deutsche Datum („21.01.“) schließt den Satz schon mit Punkt
  if (alle) return t(`50 % Rabatt auf die Rechnungen am ${aufzaehlung(de, "und")}`, `50% off the invoices on ${aufzaehlung(en, "and")}.`);
  return daten.length === 1
    ? t(`50 % Rabatt noch auf die Rechnung am ${de[0]}`, `50% off still applies to the invoice on ${en[0]}.`)
    : t(`50 % Rabatt noch auf die Rechnungen am ${aufzaehlung(de, "und")}`, `50% off still applies to the invoices on ${aufzaehlung(en, "and")}.`);
}

/** Aufbewahrung nach der Export-Phase in Klartext (Abo-Seite, Kündigungsdialog; 0425: Belege zum Abruf statt lesbar). */
export function retentionText(plan: LifecycleStatus["plan"], fmt: (iso: string) => string): string {
  const p = plan ?? {};
  const books = p.retention_years ?? 10;
  const letters = p.retention_letters_years ?? books;
  // Inhaber 14.10.2026: klare, kurze Sätze (vorher ein verschachtelter Satz mit „nach den Fristen für …“)
  const records = p.retention_records_years ?? books;
  // 0444 (Steuerprüfung L17): Zahlungsbuchungen länger als Belege (DE 10 Jahre), Verträge zu Rechnungen wie Belege
  const frist2 = records !== books
    ? t(`Rechnungen, Gutschriften und Stornos ${books} Jahre, Zahlungsbuchungen ${records} Jahre, Verträge zu Rechnungen oder Gutschriften ${books} Jahre, übrige unterschriebene Verträge ${letters} Jahre`,
      `invoices, self-billing invoices and cancellations ${books} years, payment records ${records} years, contracts relating to invoices or self-billing invoices ${books} years, other signed contracts ${letters} years`)
    : letters === books
    ? t(`${books} Jahre`, `${books} years`)
    : t(`Rechnungen, Gutschriften, Stornos und Buchungsdaten ${books} Jahre, unterschriebene Verträge ${letters} Jahre`, `invoices, self-billing invoices, cancellations and accounting data ${books} years, signed contracts ${letters} years`);
  const landIn = p.country === "AT" ? t(" in Österreich", " in Austria") : p.country === "CH" ? t(" in der Schweiz", " in Switzerland") : p.country === "DE" ? t(" in Deutschland", " in Germany") : "";
  const base = t(`Danach löschen wir alles. Nur Belege, die aufbewahrt werden müssen, halten wir so lange zum Abruf bereit, wie es das Gesetz${landIn} verlangt: ${frist2}, jeweils ab Ende des Jahres des letzten Belegs.`,
    `After that we delete everything. Only documents that must be retained stay available for download for as long as the law${landIn} requires: ${frist2}, each from the end of the year of the last document.`);
  const until = p.retention_until ? t(` Stand heute: Belege bis ${fmt(p.retention_until)}.`, ` As of today: documents until ${fmt(p.retention_until)}.`) : p.last_document === null || p.last_document === undefined ? t(" Gibt es keine Belege, löschen wir nach der Export-Phase alles.", " If there are no documents, we delete everything after the export phase.") : "";
  const halt = p.retention_hold ? t(" Die endgültige Löschung ist derzeit angehalten (Aufbewahrung verlängert).", " Final deletion is currently paused (retention extended).") : "";
  return base + until + halt + t(" Jeden Schritt kündigen wir vorher per Mail an.", " We announce every step by email beforehand.");
}

/**
 * Grund der Nur-Lesen-Phase bzw. der Sperre in Klartext (Banner, Abo-Seite). 0425: Nach dem Vertragsende (gekündigt, Test
 * abgelaufen, `gesperrt` für eine abgelaufene Laufzeit ohne Verlängerung) ist das Portal gesperrt und die Export-Phase läuft;
 * Pause, offene Zahlung und Sperre durch den Betrieb bleiben „nur lesen“.
 */
export function readOnlyText(s: Pick<LifecycleStatus, "reason" | "cancel_effective_at" | "plan"> & { subscription_status?: string | null; suspended_reason?: string | null; reduced_at?: string | null; pause?: PauseStand | null }, fmt: (iso: string) => string,
  /** K-383: nach dem Test gewählter Tarif mit Preis (Leiste nennt Tarif und Preis statt „bis ein Abo abgeschlossen ist“) */
  tarif?: { name: string; cents: number; interval: "month" | "year" } | null,
  /** 0425: Vertrag beendet (org_read_only_state.ended) */
  gesperrt = false,
  /** 0425 (Runde 3): nie bezahlt (Test beendet): „Test beendet am …“ statt „Gekündigt zum …“ */
  trial = false): string {
  const p = s.plan;
  // „Nur-Lesen statt Sperre“ (Merker des Betriebs) zeigt bei gekündigt bzw. Test abgelaufen weiter die Nur-Lesen-Sätze
  const beendet = gesperrt;
  const until = s.reduced_at
    ? t(` Nur noch die aufbewahrungspflichtigen Belege sind zum Abruf bereit${p?.purge_at ? t(`, bis ${fmt(p.purge_at)}`, `, until ${fmt(p.purge_at)}`) : ""}.`, ` Only the documents that must be retained are still available for download${p?.purge_at ? t(`, bis ${fmt(p.purge_at)}`, `, until ${fmt(p.purge_at)}`) : ""}.`)
    : p?.next_at && p.next_action === "reduce"
    ? (beendet ? t(` Export bis ${fmt(p.next_at)}; danach bleiben nach Ankündigung nur die aufbewahrungspflichtigen Belege.`, ` Export until ${fmt(p.next_at)}; afterwards, after notice, only the documents that must be retained remain.`)
      : t(` Nur lesen bis ${fmt(p.next_at)}; danach bleiben nach Ankündigung nur die aufbewahrungspflichtigen Belege.`, ` Read-only until ${fmt(p.next_at)}; afterwards, after notice, only the documents that must be retained remain.`))
    : p?.purge_at
    ? (beendet ? t(` Export bis ${fmt(p.purge_at)}; danach werden die Daten nach Ankündigung gelöscht.`, ` Export until ${fmt(p.purge_at)}; afterwards the data will be deleted after notice.`)
      : t(` Nur lesen bis ${fmt(p.purge_at)}; danach werden die Daten nach Ankündigung gelöscht.`, ` Read-only until ${fmt(p.purge_at)}; afterwards the data will be deleted after notice.`))
    : "";
  switch (s.reason) {
    // 0414: Pause statt Kündigung; die Daten sind vollständig da, danach geht es automatisch weiter
    case "paused": return s.pause?.ends_at && s.cancel_effective_at
      // P-8: in der Pause gekündigt
      ? t(`Pausiert bis ${fmt(s.pause.ends_at)}, dann endet das Abo. Lesen und Export gehen weiter.`, `Paused until ${fmt(s.pause.ends_at)}, then the subscription ends. Reading and export still work.`)
      : s.pause?.ends_at
      ? t(`Pausiert bis ${fmt(s.pause.ends_at)}. Danach geht es automatisch weiter. Bis dahin wird nichts abgebucht, und Mahnungen und Erinnerungen pausieren mit; Lesen und Export gehen weiter.`,
        `Paused until ${fmt(s.pause.ends_at)}. After that everything continues automatically. Until then nothing is charged, and dunning emails and reminders pause too; reading and export still work.`)
      : t("Pausiert. Lesen und Export gehen weiter.", "Paused. Reading and export still work.");
    case "trial_expired": return tarif
      // Inhaber 16.10.2026: keine Jahressumme, bei jährlicher Abrechnung der Monatsbetrag
      ? t(`Dein Test ist beendet. Starte ${tarif.name} für ${money(tarif.interval === "year" ? Math.round(tarif.cents / 12) : tarif.cents, "EUR")} netto im Monat${tarif.interval === "year" ? " (jährlich abgerechnet)" : ""}, dann geht es sofort weiter.${until}`,
        `Your trial has ended. Start ${tarif.name} for ${money(tarif.interval === "year" ? Math.round(tarif.cents / 12) : tarif.cents, "EUR")} net per month${tarif.interval === "year" ? " (billed annually)" : ""} to continue right away.${until}`)
      : beendet
      ? t(`Testphase abgelaufen. Das Portal ist gesperrt, bis ein Abo abgeschlossen ist; deine Daten kannst du exportieren.${until}`, `Trial expired. The portal is locked until a subscription is taken out; you can export your data.${until}`)
      : t(`Testphase abgelaufen. Das Konto ist nur lesbar, bis ein Abo abgeschlossen ist.${until}`, `Trial expired. The account is read-only until a subscription is taken out.${until}`);
    case "cancelled": return trial
      ? t(`Test beendet${s.cancel_effective_at ? ` am ${fmt(s.cancel_effective_at)}` : ""}. ${beendet ? "Das Portal ist gesperrt; deine Daten kannst du exportieren." : "Das Konto ist nur lesbar."}${until}`,
        `Trial ended${s.cancel_effective_at ? ` on ${fmt(s.cancel_effective_at)}` : ""}. ${beendet ? "The portal is locked; you can export your data." : "The account is read-only."}${until}`)
      : beendet
      ? t(`Gekündigt${s.cancel_effective_at ? ` zum ${fmt(s.cancel_effective_at)}` : ""}. Das Portal ist gesperrt; deine Daten kannst du exportieren.${until}`, `Terminated${s.cancel_effective_at ? ` as of ${fmt(s.cancel_effective_at)}` : ""}. The portal is locked; you can export your data.${until}`)
      : t(`Gekündigt${s.cancel_effective_at ? ` zum ${fmt(s.cancel_effective_at)}` : ""}. Das Konto ist nur lesbar.${until}`, `Terminated${s.cancel_effective_at ? ` as of ${fmt(s.cancel_effective_at)}` : ""}. The account is read-only.${until}`);
    // 0181 (Zusatz 38): Sperre wegen offener Abo-Zahlung (Stripe/PayPal, 0173/0180) hebt die Zahlung auf, nicht der Betrieb
    // 0425: Laufzeit eines vom Betrieb freigeschalteten Abos ohne Verlängerung abgelaufen: Vertrag beendet, Portal gesperrt
    case "suspended": return gesperrt
      ? t(`Abo beendet${s.suspended_reason ? ` (${s.suspended_reason})` : ""}. Das Portal ist gesperrt; deine Daten kannst du exportieren.${until}`, `Subscription ended${s.suspended_reason ? ` (${s.suspended_reason})` : ""}. The portal is locked; you can export your data.${until}`)
      // Nachtest CO-005: offene Abo-Zahlung auch ohne eingetragenen Grund (subscription_status past_due)
      : /zahlung/i.test(s.suspended_reason ?? "") || s.subscription_status === "past_due"
      ? t(`Gesperrt (${s.suspended_reason ?? "Abo-Zahlung offen"}). Das Konto ist nur lesbar; mit der Zahlung ist alles wieder offen. Lesen, Herunterladen und Export gehen weiter.${until}`, `Suspended (${s.suspended_reason ?? "subscription payment outstanding"}). The account is read-only; once paid, everything is unlocked again. Reading, downloading and export still work.${until}`)
      : t(`Gesperrt${s.suspended_reason ? ` (${s.suspended_reason})` : ""}. Das Konto ist nur lesbar, bis der Plattform-Betrieb es wieder freigibt.${until}`, `Suspended${s.suspended_reason ? ` (${s.suspended_reason})` : ""}. The account is read-only until platform operations reactivate it.${until}`);
    default: return "";
  }
}

/** Kündigungsgründe (Auswahl; der Text geht mit an die Plattform). */
export const CANCEL_REASONS = ["Zu teuer", "Funktionen fehlen", "Agentur hört auf", "Wechsel zu anderem Werkzeug", "Sonstiges"] as const;
/** Gründe im Halten-Dialog (Inhaber 21.10.2026); gespeichert wird der deutsche Wert (Auswertung im Betrieb) */
export const HALTE_GRUENDE = ["Zu teuer", "Eine Funktion fehlt", "Keine Zeit", "Anderes Werkzeug", "Sonstiges"] as const;
export type HalteGrund = (typeof HALTE_GRUENDE)[number];
/** Anzeige eines Grunds; „Keine Zeit“ heißt im Test „Keine Zeit zum Testen“, mit Abo „Keine Zeit für die Einrichtung“ */
export function halteGrundLabel(g: HalteGrund, imTest: boolean): string {
  switch (g) {
    case "Zu teuer": return t("Zu teuer", "Too expensive");
    case "Eine Funktion fehlt": return t("Eine Funktion fehlt", "A feature is missing");
    case "Keine Zeit": return imTest ? t("Keine Zeit zum Testen", "No time to try it") : t("Keine Zeit für die Einrichtung", "No time to set it up");
    case "Anderes Werkzeug": return t("Ich nutze ein anderes Werkzeug", "I use another tool");
    default: return t("Sonstiges", "Other");
  }
}
/** Anzeige der Kündigungsgründe: gespeichert wird der deutsche Wert (Auswertung im Betrieb), angezeigt in der Oberflächensprache und passend zur Kontoart. */
export function cancelReasonLabel(r: (typeof CANCEL_REASONS)[number]): string {
  switch (r) {
    case "Zu teuer": return t("Zu teuer", "Too expensive");
    case "Funktionen fehlen": return t("Funktionen fehlen", "Features are missing");
    case "Agentur hört auf": return ta("Agentur hört auf", "The agency is closing", "Ich beende meine Selbständigkeit", "I am ending my self-employment");
    case "Wechsel zu anderem Werkzeug": return t("Wechsel zu anderem Werkzeug", "Switching to another tool");
    default: return t("Sonstiges", "Other");
  }
}

// ── Zustimmungen (AGB, Datenschutz, AVV) ────────────────────────────────────────────────────────

/**
 * Rechtstexte der Plattform (0057): AGB schließt der Admin für die Agentur ab, Nutzungsbedingungen gelten für jede
 * eingeladene Person (Manager, Creator), Datenschutzhinweise für alle, den AVV schließt der Admin für die Agentur.
 * Die Fassungen stehen nur in platform_defaults.legal_versions.
 */
export const LEGAL_DOCS = [
  { kind: "agency_terms", label: "AGB", labelEn: "Terms and Conditions", path: "/agb.html" },
  { kind: "user_terms", label: "Nutzungsbedingungen", labelEn: "terms of use", path: "/nutzung.html" },
  { kind: "privacy", label: "Datenschutzhinweise", labelEn: "privacy notice", path: "/datenschutz.html" },
  { kind: "dpa", label: "Auftragsverarbeitungsvertrag (AVV)", labelEn: "data processing agreement", path: "/avv.html" },
] as const;
export type LegalKind = (typeof LEGAL_DOCS)[number]["kind"];
export function legalDoc(kind: string): (typeof LEGAL_DOCS)[number] | undefined {
  return LEGAL_DOCS.find((d) => d.kind === kind || (kind === "terms" && d.kind === "agency_terms"));
}

/**
 * B2B-Bestätigung (Anwaltsvorgabe 06.10.2026): eigene Pflicht-Checkbox bei Registrierung und Checkout, nie vorangekreuzt.
 * Gespeichert als Zustimmung „b2b_declaration“ mit dieser Fassung (0182, platform_defaults.b2b_declaration_version; die
 * Datenbank lehnt eine andere Fassung ab). Neuer Wortlaut: neues Datum hier und im Produkt-Standard.
 */
export const B2B_VERSION = "2026-10-06";
export const b2bDeclarationText = () => t(
  "Ich bestätige, dass ich CreatorOffice ausschließlich für meine gewerbliche oder selbständige berufliche Tätigkeit nutze und bei Abschluss dieses Vertrags als Unternehmer handle. Ein Vertragsschluss als Verbraucher ist ausgeschlossen.",
  "I confirm that I use CreatorOffice exclusively for my commercial or self-employed professional activity and act as a business (not as a consumer) when concluding this contract. Concluding the contract as a consumer is excluded.");
export const b2bMissingText = () => t("Bitte bestätige, dass du als Unternehmer handelst.", "Please confirm that you are acting as a business.");

/** Zustimmungen, die eine Vormerkung (Registrierung) tragen kann: Rechtstexte und die B2B-Bestätigung */
export type PendingKind = LegalKind | "b2b_declaration";
/** Weg der Zustimmung (0182, user_consents.method) */
export type ConsentMethod = "registration" | "google" | "setup" | "banner" | "checkout" | "invitation" | "settings";
export function consentMethodLabel(m: string | null | undefined): string {
  switch (m) {
    case "registration": return t("bei der Registrierung", "at registration");
    case "google": return t("bei der Registrierung mit Google", "at registration with Google");
    case "setup": return t("in der Einrichtung", "during setup");
    case "banner": return t("Hinweis im Portal (erneute Zustimmung)", "notice in the portal (renewed consent)");
    case "checkout": return t("beim Abschluss des Abos", "when taking out the subscription");
    case "invitation": return t("beim Annehmen der Einladung", "when accepting the invitation");
    case "settings": return t("in den Einstellungen", "in the settings");
    default: return t("nicht erfasst (vor dem 06.10.2026)", "not recorded (before 6 Oct 2026)");
  }
}

/**
 * Vertragspartner aller Kunden, wie im Impressum (apps/website/impressum.html, Stand 01.10.2026).
 */
export const ANBIETER = {
  name: "HeereHaase Partners FZCO",
  get adresse() { return t("IFZA Properties, Dubai Silicon Oasis, Vereinigte Arabische Emirate", "IFZA Properties, Dubai Silicon Oasis, United Arab Emirates"); },
  get register() { return t("Freezone Company, eingetragen bei der Dubai Integrated Economic Zones Authority (DIEZ) unter der Lizenznummer 35900", "Freezone Company, registered with the Dubai Integrated Economic Zones Authority (DIEZ) under licence number 35900"); },
  get vertreten() { return t("vertreten durch die Geschäftsführung (Angaben im Impressum)", "represented by its management (see legal notice)"); },
  kontakt: "support@creatoroffice.de",
};

/** Preis mit „netto“ direkt am Betrag: „19,00 € netto/Monat“ (Anwaltsvorgabe 06.10.2026) */
export function nettoPreis(formatted: string, interval: "month" | "year"): string {
  return interval === "year" ? t(`${formatted} netto/Jahr`, `${formatted} net/year`) : t(`${formatted} netto/Monat`, `${formatted} net/month`);
}
/** Monatsbetrag eines Tarifs; bei jährlicher Abrechnung ein Zwölftel des Jahrespreises (Inhaber 16.10.2026: nie Jahressummen zeigen) */
export function monatsCents(p: { price_month_cents: number; price_year_cents: number }, interval: "month" | "year"): number {
  return interval === "year" ? Math.round(p.price_year_cents / 12) : p.price_month_cents;
}
/** Inhaber 16.10.2026: Beim Wechsel aus einem laufenden Abo nur die Differenz zum heutigen Monatsbetrag („nur 20,00 € zusätzlich“) */
export function aufpreisText(diffCents: number): string {
  return diffCents >= 0
    ? t(`nur ${money(diffCents, "EUR")} netto zusätzlich im Monat`, `only ${money(diffCents, "EUR")} net extra per month`)
    : t(`${money(-diffCents, "EUR")} netto weniger im Monat`, `${money(-diffCents, "EUR")} net less per month`);
}
/** Kurzform für die Preiszeile der Karte: „+20,00 €“ bzw. „−20,00 €“ */
export function aufpreisKurz(diffCents: number): string {
  return `${diffCents >= 0 ? "+" : "−"}${money(Math.abs(diffCents), "EUR")}`;
}
/** Der Creator-Tarif ist nur für Unternehmer und selbständig tätige Creator (direkt beim Preis zu nennen) */
export const creatorNurUnternehmerText = () => t("ausschließlich für Unternehmer und selbständig tätige Creator.", "exclusively for businesses and self-employed creators.");
export const CREATOR_PLAN_KEY = "creator";

/**
 * Kosten beim Start mit Testphase (Anwaltsvorgabe 06.10.2026): „Heute 0 €. Ab <Datum> <Preis> netto/<Monat|Jahr>, sofern du
 * vorher nicht kündigst.“ Datum ist das Ende der Testphase (Stripe bucht dann automatisch, billing-self-service.ts trial_end).
 */
export function testphaseKostenText(endDate: string, preis: string, interval: "month" | "year"): string {
  return interval === "year"
    ? t(`Heute 0 €. Ab ${endDate} ${preis} netto/Jahr, sofern du vorher nicht kündigst.`, `Today €0. From ${endDate} ${preis} net/year, unless you cancel before.`)
    : t(`Heute 0 €. Ab ${endDate} ${preis} netto/Monat, sofern du vorher nicht kündigst.`, `Today €0. From ${endDate} ${preis} net/month, unless you cancel before.`);
}

/** Fassung eines Rechtstexts im Archiv (0182, public.legal_document_versions) */
export interface LegalVersionRow {
  kind: string; version: string; valid_from: string; valid_until: string | null; url: string; archive_url: string | null;
  summary_de: string | null; summary_en: string | null; published_at: string | null;
}
/**
 * Aktuelle, angekündigte und frühere Fassungen je Rechtstext: aktuell ist die Fassung, die die Datenbank als wirksam nennt
 * (legal_versions_current), angekündigt sind spätere, früher alle älteren (neueste zuerst).
 */
export function legalVersionsOverview(rows: readonly LegalVersionRow[], current: Record<string, string>, kinds: readonly string[]) {
  return kinds.map((kind) => {
    const own = rows.filter((r) => r.kind === kind).sort((a, b) => b.version.localeCompare(a.version));
    const cur = current[kind] ?? null;
    return {
      kind,
      current: own.find((r) => r.version === cur) ?? null,
      currentVersion: cur,
      upcoming: cur ? own.filter((r) => r.version > cur).reverse() : [],
      earlier: cur ? own.filter((r) => r.version < cur) : own,
    };
  });
}

/** Angekündigte Änderung, wie sie Admins im Portal sehen (0182, legal_change_notices_open) */
export interface LegalNotice {
  id: string; notice_type: "change" | "subprocessor"; kind: string; new_version: string | null; valid_from: string;
  objection_until: string | null; material: boolean; summary_de: string; summary_en: string; url: string | null; privacy_email: string;
}

// ── Hauswährung wechseln: Festbeträge ohne eigene Währung ──────────────────────────────────────

/**
 * Welche gespeicherten Festbeträge gelten nach einem Wechsel der Hauswährung in der neuen Währung?
 * (agency_fee_cents, revision_fee_cents, Mahngebühren ohne eigene Gebührenwährung). Leer: nichts zu bestätigen.
 */
export function amountsInHomeCurrency(s: { agency_fee_mode: string | null; agency_fee_cents: number | null },
  dunning: { fee_currency: string | null; flat_fee_cents: number | null } | null, steps: readonly { fee_cents: number }[]): string[] {
  const out: string[] = [];
  if (s.agency_fee_mode === "fixed" && s.agency_fee_cents) out.push(t("Agenturgebühr als Festbetrag", "Agency fee as a fixed amount"));
  if (dunning && !dunning.fee_currency) {
    if (dunning.flat_fee_cents) out.push(t("Pauschale der Mahnleiter", "Flat fee of the dunning levels"));
    if (steps.some((x) => x.fee_cents > 0)) out.push(t("Gebühren der Mahnstufen", "Fees of the dunning levels"));
  }
  return out;
}
export type ExportKind = "invoices" | "flat_taxed";

export function exportRange(range: ExportRange, today: string, from?: string, to?: string): { from: string; to: string } | { error: string } {
  if (range === "all") return { from: "1970-01-01", to: "9999-12-31" };
  if (range === "year") return { from: `${today.slice(0, 4)}-01-01`, to: `${today.slice(0, 4)}-12-31` };
  const ok = (v?: string) => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);
  if (!ok(from) || !ok(to)) return { error: t("Bitte Beginn und Ende des Zeitraums wählen.", "Please choose the start and end of the period.") };
  if (from! > to!) return { error: t("Der Beginn liegt nach dem Ende. Bitte den Zeitraum prüfen.", "The start is after the end. Please check the period.") };
  return { from: from!, to: to! };
}
export function exportFileName(kind: ExportKind, r: { from: string; to: string }, ext: "csv" | "json"): string {
  const name = kind === "invoices" ? "rechnungen" : "pauschal-versteuert";
  const span = r.from === "1970-01-01" ? "gesamt" : `${r.from}_${r.to}`;
  return `${name}_${span}.${ext}`;
}

export interface CsvColumn<T> {
  label: string;
  value: (row: T) => string | number | null | undefined;
  /** Zahlen und Beträge ohne Formel-Schutz (sie beginnen nie mit Rechenzeichen außer „-“) */
  numeric?: boolean;
}

/** CSV für Excel: Semikolon, CRLF, UTF-8 mit BOM, Schutz gegen Formeln in Textfeldern (AD-226, AD-227). */
export function toCsv<T>(columns: readonly CsvColumn<T>[], rows: readonly T[], bom = true): string {
  const cell = (v: string | number | null | undefined, numeric?: boolean): string => {
    let s = v === null || v === undefined ? "" : String(v);
    if (!numeric) s = csvCellGuard(s);
    return /[;"\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.map((c) => cell(c.label)).join(";"), ...rows.map((r) => columns.map((c) => cell(c.value(r), c.numeric)).join(";"))];
  return `${bom ? "﻿" : ""}${lines.join("\r\n")}\r\n`;
}

export const INVOICE_KIND_LABEL: Record<string, string> = {
  get talent_to_agency() { return t("Creator an Agentur", "Creator to agency"); },
  get agency_to_brand() { return ta("Agentur an Marke", "Agency to brand", "An Marke", "To brand"); },
  get talent_to_brand() { return t("Creator an Marke", "Creator to brand"); },
  get agency_to_talent() { return t("Agentur an Creator", "Agency to creator"); },
};
export const INVOICE_STATUS_LABEL: Record<string, string> = {
  get draft() { return t("Entwurf", "Draft"); }, get submitted() { return t("Eingereicht", "Submitted"); }, get revision_requested() { return t("Korrektur angefordert", "Correction requested"); }, get rejected() { return t("Abgelehnt", "Declined"); },
  get approved() { return t("Freigegeben", "Approved"); }, get sent() { return t("Versendet", "Sent"); }, get paid() { return t("Bezahlt", "Paid"); }, get cancelled() { return t("Storniert", "Cancelled"); }, get withdrawn() { return t("Zurückgezogen", "Withdrawn"); }, get discarded() { return t("Verworfen", "Discarded"); },
};

/**
 * Stand einer Rechnung im Export (K-147 d): Creator-Rechnungen an die Agentur werden nicht „versendet“, sondern ausgezahlt.
 */
export function exportStatusLabel(r: Pick<ExportInvoiceRow, "kind" | "status" | "payout_confirmed_at">): string {
  if (r.kind === "talent_to_agency") {
    if (r.status === "paid") return t("Ausgezahlt", "Paid out");
    if (r.status === "approved" || r.status === "sent") return r.payout_confirmed_at ? t("Ausgezahlt · Bestätigung offen", "Paid out · confirmation open") : t("Auszahlung offen", "Payout open");
  }
  return INVOICE_STATUS_LABEL[r.status] ?? r.status;
}

export interface ExportInvoiceRow {
  /** Auszahlung von der Agentur vermerkt (Creator-Rechnung, 0240) */
  payout_confirmed_at?: string | null;
  number: string | null;
  kind: string;
  talent: string | null;
  brand: string | null;
  issue_date: string | null;
  revenue_month: string | null;
  currency: string;
  net_cents: number | null;
  tax_cents: number | null;
  gross_cents: number | null;
  amount_org_cents: number | null;
  status: string;
  paid_at: string | null;
  service_period_start?: string | null;
  service_period_end?: string | null;
  due_date?: string | null;
  tax_mode?: string | null;
  tax_rate_percent?: number | null;
  notice_key?: string | null;
  issuer_name?: string | null;
  issuer_vat_id?: string | null;
  issuer_tax_number?: string | null;
  recipient_name?: string | null;
  recipient_vat_id?: string | null;
  recipient_country?: string | null;
  deal_number?: number | null;
  deal_title?: string | null;
  /** 0049: Gutschrift der Agentur, Stornorechnung zu */
  self_billed?: boolean | null;
  credit_for_number?: string | null;
  /** 0064: aus dem bisherigen System übernommen (dort schon gebucht) */
  imported?: boolean | null;
  /** 0274 (K-308): Umsatzsteuer in Euro bei fremder Währung, wie auf dem PDF (Betrag, Kurs 1 EUR = x, Tag, Quelle) */
  tax_eur_cents?: number | null;
  tax_eur_rate?: number | string | null;
  tax_eur_rate_on?: string | null;
  tax_eur_source?: string | null;
  /** 0284 (K-399): Verzugszinsen eines Belegs über Verzugskosten (Rechnungswährung), sonst null */
  late_interest_cents?: number | null;
}

/** Steuerfall der Rechnung (tax_mode der Fachlogik) in Klartext für Steuerberatung und Buchhaltung. */
export interface ExportFlatRow {
  deal_number: number | null;
  deal_title: string;
  talent: string;
  brand: string;
  month: string;
  amount_cents: number | null;
  currency: string;
  /** Warenwert (Sachleistung) und Produktpositionen des Monats (0047) */
  goods_value_cents?: number | null;
  goods_description?: string | null;
  products?: string | null;
  products_cents?: number | null;
  /** Warenwert plus Produktpositionen */
  base_cents?: number | null;
}

const dmy = (iso: string | null | undefined) => (iso ? iso.slice(0, 10).split("-").reverse().join(".") : "");

/** K-398: Brutto in EUR zum Kurs der Umsatzsteuer in EUR (PDF): Netto mit diesem Kurs plus die gedruckte USt; sonst null */
export function grossEurAtTaxRate(r: Pick<ExportInvoiceRow, "currency" | "net_cents" | "tax_cents" | "gross_cents" | "tax_eur_cents" | "tax_eur_rate">): number | null {
  if (r.currency === "EUR" || r.tax_eur_cents == null || r.tax_eur_rate == null) return null;
  const e = datevEurAmounts({ currency: r.currency, grossCents: r.gross_cents ?? 0, netCents: r.net_cents ?? 0, taxCents: r.tax_cents ?? 0,
    taxEurCents: r.tax_eur_cents, taxEurRate: Number(r.tax_eur_rate) });
  return e?.fromPdf ? e.grossEurCents : null;
}

/** Rechnungen als CSV; Steuerfall im Klartext und Spaltenköpfe in den Begriffen des Agenturlands (SAAS-R3-3) */
export function invoiceCsvColumns(orgCurrency: string, orgCountry: string | null = null): CsvColumn<ExportInvoiceRow>[] {
  const idWord = taxIdWord(orgCountry);
  return [
    { label: t("Rechnungsnummer", "Invoice number"), value: (r) => r.number },
    { label: t("Art", "Type"), value: (r) => INVOICE_KIND_LABEL[r.kind] ?? r.kind },
    { label: "Creator", value: (r) => r.talent },
    { label: t("Marke", "Brand"), value: (r) => r.brand },
    { label: t("Rechnungsdatum", "Invoice date"), value: (r) => dmy(r.issue_date), numeric: true },
    { label: t("Leistungsmonat", "Service month"), value: (r) => r.revenue_month, numeric: true },
    { label: t("Währung", "Currency"), value: (r) => r.currency },
    { label: t("Netto", "Net"), value: (r) => centsToDecimal(r.net_cents), numeric: true },
    { label: t("Steuer", "Tax"), value: (r) => centsToDecimal(r.tax_cents), numeric: true },
    { label: t("Brutto", "Gross"), value: (r) => centsToDecimal(r.gross_cents), numeric: true },
    // K-398: Kurs beim Versand; der Bruttobetrag zum Kurs der Umsatzsteuer in EUR (PDF) steht in einer eigenen Spalte
    { label: t(`Brutto in ${orgCurrency} (Versandkurs)`, `Gross in ${orgCurrency} (dispatch rate)`), value: (r) => centsToDecimal(r.amount_org_cents), numeric: true },
    { label: "Status", value: (r) => exportStatusLabel(r) },
    { label: t("Bezahlt am", "Paid on"), value: (r) => dmy(r.paid_at), numeric: true },
    { label: t("Leistung von", "Service from"), value: (r) => dmy(r.service_period_start), numeric: true },
    { label: t("Leistung bis", "Service until"), value: (r) => dmy(r.service_period_end), numeric: true },
    { label: t("Fällig am", "Due on"), value: (r) => dmy(r.due_date), numeric: true },
    // K-310: mit Empfängerland (Bezugsteuer CH/LI bzw. § 13b DE richtet sich nach dem Empfänger)
    { label: t("Steuerfall", "Tax treatment"), value: (r) => (r.tax_mode ? taxModeText(r.tax_mode, orgCountry, r.recipient_country ?? null) : "") },
    // K-310: Hinweisschlüssel des Belegs (unterscheidet z. B. ABROAD_CH_ACQUISITION_TAX und THIRD_COUNTRY_NOT_TAXABLE)
    { label: t("Steuerhinweis (Schlüssel)", "Tax notice (key)"), value: (r) => r.notice_key ?? "" },
    { label: t("Steuersatz %", "Tax rate %"), value: (r) => (r.tax_rate_percent === null || r.tax_rate_percent === undefined ? "" : String(r.tax_rate_percent).replace(".", decimalSeparator())), numeric: true },
    { label: t("Aussteller", "Issuer"), value: (r) => r.issuer_name },
    { label: t(`${idWord} Aussteller`, `Issuer ${idWord}`), value: (r) => r.issuer_vat_id },
    { label: t("Steuernummer Aussteller", "Issuer tax number"), value: (r) => r.issuer_tax_number },
    { label: t("Empfänger", "Recipient"), value: (r) => r.recipient_name },
    { label: t(`${idWord} Empfänger`, `Recipient ${idWord}`), value: (r) => r.recipient_vat_id },
    { label: t("Land Empfänger", "Recipient country"), value: (r) => r.recipient_country },
    { label: t("Kooperation Nr.", "Deal no."), value: (r) => r.deal_number, numeric: true },
    { label: t("Kooperation", "Deal"), value: (r) => r.deal_title },
    { label: t("Übernommen", "Taken over"), value: (r) => (r.imported ? t("ja", "yes") : "") },
    // K-308 (0274): Umsatzsteuer in Euro wie auf dem PDF (fremde Rechnungswährung mit DE/AT-Umsatzsteuer)
    // K-398: Netto zum Kurs der USt umgerechnet plus die gedruckte USt in EUR (wie im DATEV-/BMD-Export); EUR-Rechnungen leer
    { label: t("Brutto in EUR (Kurs der USt)", "Gross in EUR (VAT rate)"), value: (r) => centsToDecimal(grossEurAtTaxRate(r)), numeric: true },
    { label: t("Umsatzsteuer in EUR", "VAT in EUR"), value: (r) => (r.tax_eur_cents == null ? "" : centsToDecimal(r.tax_eur_cents)), numeric: true },
    { label: t("Kurs USt in EUR (1 EUR =)", "VAT in EUR rate (1 EUR =)"), value: (r) => (r.tax_eur_rate == null ? "" : String(Number(r.tax_eur_rate)).replace(".", decimalSeparator())), numeric: true },
    { label: t("Kurstag USt in EUR", "VAT in EUR rate date"), value: (r) => dmy(r.tax_eur_rate_on), numeric: true },
    { label: t("Kursquelle USt in EUR", "VAT in EUR rate source"), value: (r) => r.tax_eur_source ?? "" },
  ];
}

// ── Vollständiger Export (org_export_all): eine CSV je Tabelle, dazu JSON und Dateien ──────────────

export interface FullExport {
  format: string;
  version: number;
  generated_at: string;
  org_id: string;
  organization: Record<string, unknown>;
  accounts: { user_id: string; email: string }[];
  row_counts: Record<string, number>;
  tables: Record<string, Record<string, unknown>[]>;
  files: { bucket: string; path: string; kind: string; name: string }[];
  /** Content-Ablage (0193): fertige Dateien im Medienspeicher; die Dateien selbst kommen über die Serverfunktion medien */
  content?: { count: number; bytes: number; videos?: number };
}

/** Kopf des seitenweisen Exports (org_export_manifest, 0193): statt der Tabelleninhalte nur ihre Namen */
export interface ExportManifest extends Omit<FullExport, "tables"> {
  tables: string[];
  page_limit?: number;
}
/** Eine Seite einer Tabelle (org_export_table, 0193); next ist der Weiter-Zeiger oder null */
export interface ExportTablePage {
  table: string;
  rows: Record<string, unknown>[];
  count: number;
  next: Record<string, unknown> | null;
}

/** Datei der Content-Ablage im Vollexport (Serverfunktion medien, Aktion export, 0193) */
export interface MediaExportItem {
  media_id: string;
  file_name: string;
  mime_type: string;
  kind: string;
  size: number;
  sha256: string;
  created_at: string;
  submitted: boolean;
  deal_id: string;
  deal_number: number | null;
  deal_title: string | null;
  creator: string | null;
  month: string | null;
  platform: string | null;
  format: string | null;
  /** signierter Download-Link (7 Tage), null wenn der Schlüssel nicht stimmte */
  url: string | null;
}
export interface MediaExport {
  items: MediaExportItem[];
  /** Ablauf der Links; null, wenn keine ausgestellt wurden */
  expires_at: string | null;
  /** Medienspeicher nicht erreichbar oder nicht eingerichtet: Meldung für LIESMICH und Hinweis */
  error: string | null;
}

/**
 * Höchstens so viele Bytes der Content-Ablage kommen ins ZIP: das Paket entsteht im Browser (ohne ZIP64, also unter 4 GB
 * insgesamt). Was darüber liegt, steht mit Download-Link in medien/dateien.csv und medien/links.html.
 */
export const MEDIA_ZIP_LIMIT = 2 * 1024 ** 3;

const safeName = (v: string) => v.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^[._]+/, "").slice(0, 120);

/** Pfad einer Datei der Content-Ablage im Paket: ein Ordner je Kooperation, darin der Dateiname. */
export function mediaExportPath(f: Pick<MediaExportItem, "deal_number" | "deal_title" | "deal_id" | "file_name" | "media_id">): string {
  const deal = safeName([f.deal_number ? String(f.deal_number) : "", f.deal_title ?? ""].filter(Boolean).join("-")) || f.deal_id.slice(0, 8);
  return `medien/${deal}/${safeName(f.file_name) || f.media_id}`;
}

/** Welche Dateien ins Paket kommen: in der Reihenfolge der Liste, solange sie in die Grenze passen (auch kleinere nach einer zu großen). */
export function pickMediaForZip(items: readonly Pick<MediaExportItem, "media_id" | "size" | "url">[], limit: number): Set<string> {
  const out = new Set<string>();
  let used = 0;
  for (const f of items) {
    if (!f.url || used + f.size > limit) continue;
    used += f.size;
    out.add(f.media_id);
  }
  return out;
}

/** Liste der Content-Ablage (CSV): alle Dateien mit Kooperation, Prüfsumme, Pfad im Paket bzw. Download-Link. */
export function mediaListCsv(items: readonly MediaExportItem[], inZip: ReadonlyMap<string, string>, expiresAt: string | null): string {
  const d = (v: string | null) => (v ? v.slice(0, 10).split("-").reverse().join(".") : "");
  const cols: CsvColumn<MediaExportItem>[] = [
    { label: t("Kooperation Nr.", "Deal no."), value: (r) => r.deal_number, numeric: true },
    { label: t("Kooperation", "Deal"), value: (r) => r.deal_title },
    { label: "Creator", value: (r) => r.creator },
    { label: t("Monat", "Month"), value: (r) => (r.month ? r.month.slice(0, 7) : ""), numeric: true },
    { label: t("Plattform", "Platform"), value: (r) => [r.platform, r.format].filter(Boolean).join(" · ") },
    { label: t("Datei", "File"), value: (r) => r.file_name },
    { label: t("Art", "Type"), value: (r) => r.mime_type },
    { label: t("Größe (Byte)", "Size (bytes)"), value: (r) => r.size, numeric: true },
    { label: "SHA-256", value: (r) => r.sha256 },
    { label: t("Hochgeladen", "Uploaded"), value: (r) => d(r.created_at), numeric: true },
    { label: t("Eingereicht", "Submitted"), value: (r) => (r.submitted ? t("ja", "yes") : t("nein", "no")) },
    { label: t("Im Paket", "In package"), value: (r) => inZip.get(r.media_id) ?? "" },
    { label: t("Download-Link", "Download link"), value: (r) => (inZip.has(r.media_id) ? "" : r.url ?? "") },
    { label: t("Link gültig bis", "Link valid until"), value: (r) => (!inZip.has(r.media_id) && r.url && expiresAt ? d(expiresAt) : ""), numeric: true },
  ];
  return toCsv(cols, items);
}

/** Dieselbe Liste als HTML-Seite zum Anklicken (öffnet jeder Browser, auch ohne Tabellenprogramm). */
export function mediaLinksHtml(items: readonly MediaExportItem[], inZip: ReadonlyMap<string, string>, expiresAt: string | null): string {
  const esc = (v: unknown) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const until = expiresAt ? expiresAt.slice(0, 10).split("-").reverse().join(".") : "";
  const rows = items.map((r) => {
    const where = inZip.get(r.media_id);
    const link = where ? `<a href="../${esc(where)}">${esc(t("im Paket", "in package"))}</a>`
      : r.url && /^https?:|^blob:|^\//.test(r.url) ? `<a href="${esc(r.url)}" download>${esc(t("herunterladen", "download"))}</a>` : esc(t("kein Link", "no link"));
    return `<tr><td>${esc(r.deal_number ?? "")}</td><td>${esc(r.deal_title)}</td><td>${esc(r.creator)}</td><td>${esc(r.month ? r.month.slice(0, 7) : "")}</td>`
      + `<td>${esc(r.file_name)}</td><td>${esc(r.size)}</td><td><code>${esc(r.sha256)}</code></td><td>${link}</td></tr>`;
  }).join("\n");
  const title = t("Content-Ablage: Dateien", "Content library: files");
  return `<!doctype html>\n<html lang="${t("de", "en")}"><head><meta charset="utf-8"><title>${esc(title)}</title>`
    + `<style>body{font:14px/1.4 system-ui,sans-serif;margin:24px;color:#111827}table{border-collapse:collapse;width:100%}`
    + `th,td{border-bottom:1px solid #e5e7eb;padding:6px 8px;text-align:left;vertical-align:top}code{font-size:11px;word-break:break-all}</style></head><body>`
    + `<h1>${esc(title)}</h1>`
    + `<p>${esc(until ? t(`Download-Links gelten bis ${until}. Danach stellt ein neuer Vollexport neue Links aus.`, `Download links are valid until ${until}. After that, a new full export issues new links.`) : t("Alle Dateien liegen im Paket.", "All files are in the package."))}</p>`
    + `<table><thead><tr><th>${esc(t("Nr.", "No."))}</th><th>${esc(t("Kooperation", "Deal"))}</th><th>Creator</th><th>${esc(t("Monat", "Month"))}</th>`
    + `<th>${esc(t("Datei", "File"))}</th><th>${esc(t("Byte", "Bytes"))}</th><th>SHA-256</th><th>${esc(t("Herunterladen", "Download"))}</th></tr></thead>`
    + `<tbody>\n${rows}\n</tbody></table></body></html>\n`;
}

/** Zellenwert einer Exportzeile: Listen und Objekte als JSON, sonst Text. */
function exportCell(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
/** Eine Tabelle des Vollexports als CSV (alle Spalten, die in irgendeiner Zeile vorkommen). */
export function tableCsv(rows: readonly Record<string, unknown>[]): string {
  const cols = [...new Set(rows.flatMap((r) => Object.keys(r)))];
  return toCsv(cols.map((c) => ({ label: c, value: (r: Record<string, unknown>) => exportCell(r[c]) })), rows);
}
/** Dateiname im Paket: nur harmlose Zeichen, Ordner je Art. */
export function exportFilePath(f: { kind: string; name: string; path: string }): string {
  const base = (f.path.split("/").pop() || f.name).replace(/[^A-Za-z0-9._-]+/g, "_");
  const folder = { attachment: "anhaenge", invoice: "rechnungen", contract: "vertraege", profile_image: "profilbilder", logo: "logo" }[f.kind] ?? "dateien";
  return `dateien/${folder}/${base}`;
}
/**
 * Lesbare Listen für den Umzug in ein anderes Werkzeug (Praxisfall „Agentur kündigt und nimmt Daten mit“): Creator und
 * Marken im Importformat (lassen sich hier und in Tabellen wieder einlesen), Kontakte, Kooperationen, Rechnungen mit dem
 * Pfad ihres PDFs im Paket und Verträge. Ohne IDs; Namen statt Verweisen.
 */
export function readableLists(e: FullExport, lists: {
  creator: (talents: Record<string, unknown>[], payouts: Record<string, unknown>[]) => string;
  brands: (brands: Record<string, unknown>[], contacts: Record<string, unknown>[]) => string;
  contacts: (brands: Record<string, unknown>[], contacts: Record<string, unknown>[]) => string;
}): { name: string; text: string }[] {
  const tl = (k: string) => e.tables[k] ?? [];
  const s = (v: unknown) => (v === null || v === undefined ? "" : String(v));
  const talentName = new Map(tl("talents").map((x) => [s(x.id), s(x.display_name) || `${s(x.first_name)} ${s(x.last_name)}`.trim()]));
  const brandName = new Map(tl("brands").map((x) => [s(x.id), s(x.name)]));
  const dealTitle = new Map(tl("deals").map((x) => [s(x.id), s(x.title)]));
  const fileOf = new Map(e.files.map((f) => [f.path, exportFilePath(f)]));
  // Dezimalzeichen wie alle CSV-Exporte der Oberfläche (SAAS-R3-3)
  const cents = (v: unknown) => (v === null || v === undefined || v === "" ? "" : (Number(v) / 100).toFixed(2).replace(".", decimalSeparator()));
  const d = (v: unknown) => (v ? s(v).slice(0, 10).split("-").reverse().join(".") : "");
  // GB-W4: Formelschutz wie alle Exporte (Beträge und Nummern bleiben Zahlen, csvCellGuard)
  const cell = (v: unknown) => { const x = csvCellGuard(s(v)); return /[;"\r\n]/.test(x) ? `"${x.replace(/"/g, '""')}"` : x; };
  const csv = (header: string[], rows: unknown[][]) => "\uFEFF" + [header.join(";"), ...rows.map((r) => r.map(cell).join(";"))].join("\r\n") + "\r\n";
  const byDate = (k: string) => (a: Record<string, unknown>, b: Record<string, unknown>) => s(b[k]).localeCompare(s(a[k]));
  return [
    { name: "listen/creator.csv", text: lists.creator(tl("talents"), tl("talent_payout_details")) },
    { name: "listen/marken.csv", text: lists.brands(tl("brands"), tl("brand_contacts")) },
    { name: "listen/marken-kontakte.csv", text: lists.contacts(tl("brands"), tl("brand_contacts")) },
    {
      name: "listen/kooperationen.csv",
      text: csv([t("Nr.", "No."), t("Kooperation", "Deal"), "Creator", t("Marke", "Brand"), "Status", t("Modell", "Model"), t("Budget netto", "Net budget"), t("Währung", "Currency"), t("Beginn", "Start"), t("Monate", "Months"), t("Archiviert", "Archived")],
        [...tl("deals")].sort(byDate("start_month")).map((x) => [x.deal_number, x.title, talentName.get(s(x.talent_id)), brandName.get(s(x.brand_id)), x.status, x.model,
          cents(x.budget_cents), x.currency, d(x.start_month), x.months, x.archived_at ? t("ja", "yes") : t("nein", "no")])),
    },
    {
      name: "listen/rechnungen.csv",
      text: csv([t("Nummer", "Number"), t("Art", "Type"), "Status", t("Rechnungsdatum", "Invoice date"), t("Fällig", "Due"), "Creator", t("Marke", "Brand"), t("Kooperation", "Deal"), t("Netto", "Net"), t("Steuer", "Tax"), t("Brutto", "Gross"), t("Währung", "Currency"), t("Bezahlt am", "Paid on"), t("PDF im Paket", "PDF in package")],
        [...tl("invoices")].sort(byDate("issue_date")).map((x) => [x.number, INVOICE_KIND_LABEL[s(x.kind)] ?? x.kind, INVOICE_STATUS_LABEL[s(x.status)] ?? x.status,
          d(x.issue_date), d(x.effective_due_date), talentName.get(s(x.talent_id)), brandName.get(s(x.brand_id)), dealTitle.get(s(x.deal_id)),
          cents(x.net_cents), cents(x.tax_cents), cents(x.gross_cents), x.currency, d(x.paid_at), x.pdf_path ? fileOf.get(s(x.pdf_path)) ?? "" : ""])),
    },
    {
      name: "listen/vertraege.csv",
      text: csv([t("Nummer", "Number"), t("Titel", "Title"), "Status", "Creator", t("Marke", "Brand"), t("Kooperation", "Deal"), t("Versendet", "Sent"), t("Unterschrieben", "Signed"), t("Beginn", "Start"), t("Ende", "End"), t("Unterschriebenes PDF im Paket", "Signed PDF in package")],
        [...tl("contracts")].sort(byDate("created_at")).map((x) => [x.contract_number_label || x.contract_number, x.title, x.status, talentName.get(s(x.talent_id)),
          brandName.get(s(x.brand_id)), dealTitle.get(s(x.deal_id)), d(x.sent_at), d(x.completed_at), d(x.starts_on), d(x.ends_on),
          x.signed_pdf_path ? fileOf.get(s(x.signed_pdf_path)) ?? "" : ""])),
    },
  ];
}

export function fullExportName(slug: string, generatedAt: string): string {
  // Dateiname sichtbar im Download-Ordner: Produktname CreatorOffice (Portal einheitlich)
  return `creatoroffice-export_${slug}_${generatedAt.slice(0, 10)}.zip`;
}
export const FLAT_CSV_COLUMNS: CsvColumn<ExportFlatRow>[] = [
  { get label() { return t("Kooperation Nr.", "Deal no."); }, value: (r) => r.deal_number, numeric: true },
  { get label() { return t("Kooperation", "Deal"); }, value: (r) => r.deal_title },
  { label: "Creator", value: (r) => r.talent },
  { get label() { return t("Marke", "Brand"); }, value: (r) => r.brand },
  { get label() { return t("Monat", "Month"); }, value: (r) => (r.month ? r.month.slice(0, 7) : ""), numeric: true },
  { get label() { return t("Honorar", "Fee"); }, value: (r) => centsToDecimal(r.amount_cents), numeric: true },
  { get label() { return t("Warenwert", "Value of goods"); }, value: (r) => centsToDecimal(r.goods_value_cents ?? null), numeric: true },
  { get label() { return t("Produkte", "Products"); }, value: (r) => [r.goods_description, r.products].filter(Boolean).join("; ") },
  { get label() { return t("Produktpositionen", "Product items"); }, value: (r) => centsToDecimal(r.products_cents ?? null), numeric: true },
  { get label() { return t("Bemessungsgrundlage", "Assessment basis"); }, value: (r) => centsToDecimal(r.base_cents ?? null), numeric: true },
  { get label() { return t("Währung", "Currency"); }, value: (r) => r.currency },
];

// ── Profil ─────────────────────────────────────────────────────────────────────

export function suggestInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}
export const INITIALS_PATTERN = /^[A-ZÄÖÜ0-9]{1,4}$/;

// ── Formulare: Prüfen und Umwandeln ────────────────────────────────────────────
// strict = Pflichtangaben verlangen („Änderungen speichern“, „Weiter“); ohne strict nur Formate („Zwischenspeichern“).

export interface FirmaDraft {
  orgName: string;
  legal_name: string;
  street: string;
  postal_code: string;
  city: string;
  country: string;
  timezone: string;
  small_business: boolean;
  vat_id: string;
  tax_number: string;
  legal_form: string;
  representative_name: string;
  representative_position: string;
  register_type: string;
  register_number: string;
  register_court: string;
  phone: string;
  website: string;
  imprint_extra: string;
  privacy_contact_email: string;
  governing_law: string;
  jurisdiction: string;
  /** 0071: weitere vertretungsberechtigte Personen (die erste steht in representative_name/_position) */
  more_representatives?: { name: string; position: string }[];
  /** 0071: Gesamtvertretung – zwei Vertretungen handeln nur gemeinsam */
  joint_representation?: boolean;
}
export interface FirmaValues {
  orgName: string | null;
  settings: {
    legal_name: string | null; street: string | null; postal_code: string | null; city: string | null; country: string;
    timezone: string; small_business: boolean; vat_id: string | null; tax_number: string | null;
  };
  legal: {
    legal_form: string | null; representative_name: string | null; representative_position: string | null; register_type: string | null;
    register_number: string | null; register_court: string | null; phone: string | null; website: string | null; imprint_extra: string | null;
    privacy_contact_email: string | null; governing_law: string | null; jurisdiction: string | null;
    representatives?: { name: string; position: string | null }[]; joint_representation?: boolean;
  };
}

const POSTAL: Record<string, RegExp> = { DE: /^\d{5}$/, AT: /^\d{4}$/, CH: /^\d{4}$/, LI: /^\d{4}$/ };

export function validateFirma(d: FirmaDraft, o: { strict: boolean; withOrgName: boolean }): { values: FirmaValues | null; errors: Record<string, string>; hints: Record<string, string> } {
  const c = new Check();
  const req = o.strict;
  const orgName = o.withOrgName ? c.text("orgName", d.orgName, { label: t("den Agenturnamen", "the agency name"), required: true, min: 2, max: 120 }) : null;
  const country = AGENCY_COUNTRIES.some((x) => x.code === d.country) ? d.country : (c.fail("country", t("Bitte das Land wählen.", "Please choose the country.")), "DE");
  const timezone = (TIMEZONES as readonly string[]).includes(d.timezone) ? d.timezone : countryDefaults(country).timezone;
  const legal_name = c.text("legal_name", d.legal_name, { label: t("den Firmennamen", "the company name"), required: req, max: 200 });
  const street = c.text("street", d.street, { label: t("Straße und Hausnummer", "Street and house number"), required: req, max: 120 });
  const postal_code = c.text("postal_code", d.postal_code, {
    label: t("die Postleitzahl", "the postcode"), required: req, pattern: POSTAL[country] ?? /^[A-Z0-9 -]{3,10}$/, patternText: country === "DE" ? t("Die Postleitzahl hat fünf Ziffern.", "The postcode has five digits.") : t("Die Postleitzahl hat vier Ziffern.", "The postcode has four digits."),
  });
  const city = c.text("city", d.city, { label: t("den Ort", "the city"), required: req, max: 80 });
  const vat_id = c.text("vat_id", d.vat_id, { label: countryDefaults(country).vatLabel, max: 30 });
  const tax_number = c.text("tax_number", d.tax_number, { label: t("Steuernummer", "Tax number"), max: 30 });
  if (req && !d.small_business && !vat_id && !tax_number) c.fail("vat_id", vatIdMissingText(country));
  const legal_form = c.text("legal_form", d.legal_form, { label: t("die Rechtsform", "the legal form"), required: req, max: 60 });
  const representative_name = c.text("representative_name", d.representative_name, { label: t("die vertretungsberechtigte Person", "the authorised representative"), required: req, max: 120 });
  const representative_position = c.text("representative_position", d.representative_position, { label: t("die Funktion der vertretungsberechtigten Person", "the position of the authorised representative"), required: req, max: 80 });
  const needRegister = req && registerRequired(legal_form);
  const register_eingabe = c.text("register_number", d.register_number, { label: t("die Registernummer", "the register number"), required: needRegister, max: 40 });
  const cd = countryDefaults(country);
  const register_court = c.text("register_court", d.register_court, { label: cd.code === "AT" ? t("das Firmenbuchgericht", "the company register court") : cd.code === "CH" ? t("das Handelsregisteramt", "the commercial register office") : t("das Registergericht", "the register court"),
    required: needRegister && cd.registerCourtRequired, max: 120 });
  const register_type = c.text("register_type", d.register_type, { label: t("Registerart", "Register type"), max: 60 }) ?? (legal_form ? suggestRegisterType(country, legal_form) : null);
  // Probelauf 29.10.: „HRB 234567 B“ bei Registerart „HRB“ stand überall als „HRB HRB 234567 B“; die Art steht schon davor
  const register_number = register_eingabe && register_type && register_eingabe.toLowerCase().startsWith(register_type.toLowerCase() + " ")
    ? register_eingabe.slice(register_type.length).trim() : register_eingabe;
  const phone = c.text("phone", d.phone, { label: t("eine Telefonnummer", "a phone number"), required: req, pattern: /^[+0-9 ()/.-]{5,40}$/, patternText: t("Bitte eine Telefonnummer wie +49 40 1234567 eingeben.", "Please enter a phone number such as +49 40 1234567.") });
  const website = c.text("website", d.website, { label: "Website", max: 200, pattern: /^https?:\/\/\S+\.\S+$/, patternText: t("Bitte die Website mit https:// angeben.", "Please enter the website with https://.") });
  const imprint_extra = c.text("imprint_extra", d.imprint_extra, { label: t("Zusatzangaben", "Additional details"), max: 2000 });
  const privacy_contact_email = c.email("privacy_contact_email", d.privacy_contact_email, { label: t("Kontakt für Datenschutz", "Privacy contact") });
  const governing_law = c.text("governing_law", d.governing_law, { label: t("das anwendbare Recht", "the governing law"), required: req, max: 120 });
  const jurisdiction = c.text("jurisdiction", d.jurisdiction, { label: t("den Gerichtsstand", "the place of jurisdiction"), required: req, max: 120 });
  // 0071: weitere Vertretungen; eine leere Zeile zählt nicht, eine halbe schon (Name und Funktion stehen im Vertrag)
  const more: { name: string; position: string | null }[] = [];
  (d.more_representatives ?? []).forEach((r, i) => {
    if (!r.name.trim() && !r.position.trim()) return;
    const name = c.text(`more_representatives.${i}.name`, r.name, { label: t("den Namen der weiteren vertretungsberechtigten Person", "the name of the further authorised representative"), required: true, min: 2, max: 120 });
    const position = c.text(`more_representatives.${i}.position`, r.position, { label: t("ihre Funktion", "their position"), required: req, max: 120 });
    if (name) {
      if (more.some((m) => m.name.toLowerCase() === name.toLowerCase()) || name.toLowerCase() === (representative_name ?? "").toLowerCase()) {
        c.fail(`more_representatives.${i}.name`, t("Diese Person steht schon in der Liste.", "This person is already in the list."));
      } else more.push({ name, position });
    }
  });
  if (more.length && !representative_name) c.fail("representative_name", t("Bitte zuerst die erste vertretungsberechtigte Person eintragen.", "Please enter the first authorised representative first."));
  // K-314: Hinweise sperren das Speichern nicht
  const vatHintText = vatIdHint(d);
  const hints: Record<string, string> = vatHintText ? { vat_id: vatHintText } : {};
  if (!c.ok) return { values: null, errors: c.errors, hints };
  const reps = d.more_representatives === undefined ? {} : {
    representatives: [...(representative_name ? [{ name: representative_name, position: representative_position }] : []), ...more],
    joint_representation: !!d.joint_representation && more.length > 0,
  };
  return {
    values: {
      orgName,
      settings: { legal_name, street, postal_code, city, country, timezone, small_business: d.small_business, vat_id, tax_number },
      legal: { legal_form, representative_name, representative_position, register_type, register_number, register_court, phone, website, imprint_extra, privacy_contact_email, governing_law, jurisdiction, ...reps },
    },
    errors: c.errors,
    hints,
  };
}

export interface BankDraft {
  holder: string;
  iban: string;
  bic: string;
  bank_name: string;
  currency: string;
  qr_iban: string;
  is_default: boolean;
}
/** IBAN in Vierergruppen, groß geschrieben (so steht sie auf Rechnungen). */
export function formatIban(v: string): string {
  return v.replace(/\s+/g, "").toUpperCase().replace(/(.{4})/g, "$1 ").trim();
}
export function validateBank(d: BankDraft): { values: { holder: string; iban: string; bic: string | null; bank_name: string | null; currency: string; qr_iban: string | null; is_default: boolean } | null; errors: Record<string, string> } {
  const c = new Check();
  const holder = c.text("holder", d.holder, { label: t("den Kontoinhaber", "the account holder"), required: true, max: 120 });
  const ibanRaw = d.iban.replace(/\s+/g, "").toUpperCase();
  const iban = c.text("iban", ibanRaw, { label: t("die IBAN", "the IBAN"), required: true, pattern: /^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/, patternText: t("Die IBAN beginnt mit dem Ländercode und zwei Prüfziffern.", "The IBAN starts with the country code and two check digits.") });
  if (iban && !isValidIban(iban)) c.fail("iban", t("Diese IBAN ist ungültig: die Prüfziffer passt nicht. Bitte Zahlendreher prüfen.", "This IBAN is invalid: the check digit does not match. Please check for transposed digits."));
  const bic = c.text("bic", d.bic.replace(/\s+/g, "").toUpperCase(), { label: "BIC", pattern: /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/, patternText: t("Die BIC hat 8 oder 11 Zeichen.", "The BIC has 8 or 11 characters.") });
  if (bic && !isValidBic(bic)) c.fail("bic", t("Diese BIC entspricht nicht dem SWIFT-Muster.", "This BIC does not match the SWIFT pattern."));
  const bank_name = c.text("bank_name", d.bank_name, { label: "Bank", max: 120 });
  const qr = c.text("qr_iban", d.qr_iban.replace(/\s+/g, "").toUpperCase(), { label: "QR-IBAN", pattern: /^(CH|LI)\d{2}[A-Z0-9]{17}$/, patternText: t("Eine QR-IBAN beginnt mit CH oder LI und hat 21 Zeichen.", "A QR-IBAN starts with CH or LI and has 21 characters.") });
  const currency = (BANK_CURRENCIES as readonly string[]).includes(d.currency) ? d.currency : (c.fail("currency", t("Bitte die Kontowährung wählen.", "Please choose the account currency.")), "EUR");
  if (qr && d.currency !== "CHF" && d.currency !== "EUR") c.fail("qr_iban", t("Eine QR-IBAN gibt es nur für Konten in CHF oder EUR.", "A QR-IBAN only exists for accounts in CHF or EUR."));
  else if (qr) {
    const e = qrIbanError(qr, iban);
    if (e) c.fail("qr_iban", e);
  }
  if (!c.ok || !holder || !iban) return { values: null, errors: c.errors };
  return { values: { holder, iban: formatIban(iban), bic, bank_name, currency, qr_iban: qr ? formatIban(qr) : null, is_default: d.is_default }, errors: c.errors };
}

/**
 * QR-IBAN prüfen (0069): Prüfziffern, Institutsidentifikation 30000–31999 (daran erkennt man die QR-IBAN) und ein Konto in der
 * Schweiz oder in Liechtenstein. null = in Ordnung.
 */
export function qrIbanError(qr: string, iban: string | null): string | null {
  const v = qr.replace(/\s+/g, "").toUpperCase();
  if (!/^(CH|LI)\d{2}[A-Z0-9]{17}$/.test(v)) return t("Eine QR-IBAN beginnt mit CH oder LI und hat 21 Zeichen.", "A QR-IBAN starts with CH or LI and has 21 characters.");
  if (!isValidIban(v)) return t("Diese QR-IBAN ist ungültig: die Prüfziffer passt nicht.", "This QR-IBAN is invalid: the check digit does not match.");
  if (!isQrIban(v)) return t("Das ist eine normale IBAN. Eine QR-IBAN hat an Stelle 5 bis 9 eine Nummer von 30000 bis 31999; die Bank nennt sie gesondert.", "This is a normal IBAN. A QR-IBAN has a number from 30000 to 31999 at positions 5 to 9; the bank states it separately.");
  if (iban && !isSwissIban(iban)) return t("Eine QR-IBAN gehört zu einem Konto in der Schweiz oder in Liechtenstein; die IBAN des Kontos beginnt dann mit CH oder LI.", "A QR-IBAN belongs to an account in Switzerland or Liechtenstein; the account's IBAN then starts with CH or LI.");
  return null;
}

export type QrReference = "scor" | "non";
export interface QrBillDraft { enabled: boolean; reference: QrReference; prefix: string }
export function validateQrBill(d: QrBillDraft): { values: { qr_bill_enabled: boolean; qr_bill_reference: QrReference; qr_reference_prefix: string | null } | null; errors: Record<string, string> } {
  const prefix = d.prefix.replace(/\s+/g, "");
  if (!isValidQrReferencePrefix(prefix)) return { values: null, errors: { prefix: t("Die Kundenidentifikation besteht aus 1 bis 12 Ziffern (so, wie die Bank sie nennt).", "The customer identification consists of 1 to 12 digits (as stated by the bank).") } };
  return { values: { qr_bill_enabled: d.enabled, qr_bill_reference: d.reference, qr_reference_prefix: prefix || null }, errors: {} };
}

/** Was eine Rechnung in dieser Kontowährung als Zahlteil bekommt (Anzeige unter Einstellungen · Firma) */
export function qrBillStatus(b: { iban: string; qr_iban: string | null; currency: string }, s: { enabled: boolean; reference: QrReference }): { active: boolean; text: string } {
  if (b.currency !== "CHF" && b.currency !== "EUR") return { active: false, text: t(`Kein QR-Zahlteil: QR-Rechnungen gibt es nur in CHF und EUR.`, "No QR payment part: QR-bills only exist in CHF and EUR.") };
  if (!isSwissIban(b.iban) && !(b.qr_iban && isQrIban(b.qr_iban))) return { active: false, text: t("Kein QR-Zahlteil: das Konto liegt nicht in der Schweiz oder in Liechtenstein.", "No QR payment part: the account is not in Switzerland or Liechtenstein.") };
  if (!s.enabled) return { active: false, text: t("QR-Zahlteil ausgeschaltet.", "QR payment part switched off.") };
  if (b.qr_iban && isQrIban(b.qr_iban)) return { active: true, text: t(`QR-Zahlteil mit QR-IBAN und QR-Referenz aus der Rechnungsnummer (${b.currency}).`, `QR payment part with QR-IBAN and QR reference from the invoice number (${b.currency}).`) };
  return { active: true, text: s.reference === "scor"
    ? t(`QR-Zahlteil mit IBAN und Creditor Reference (RF…) aus der Rechnungsnummer (${b.currency}).`, `QR payment part with IBAN and Creditor Reference (RF…) from the invoice number (${b.currency}).`)
    : t(`QR-Zahlteil mit IBAN, ohne Referenz; die Rechnungsnummer steht in der Mitteilung (${b.currency}).`, `QR payment part with IBAN, without reference; the invoice number appears in the message (${b.currency}).`) };
}

export interface BrandingDraft {
  brand_color: string;
  sender_name: string;
  reply_to_email: string;
  email_footer: string;
  product_name_override: string;
  mascot_enabled: boolean;
  locale: string;
  currency: string;
  currencies: string[];
  default_document_language: string;
  supported_languages: string[];
}
export function validateBranding(d: BrandingDraft, o: { strict: boolean }) {
  const c = new Check();
  const color = d.brand_color.trim() ? normalizeHex(d.brand_color) : null;
  if (d.brand_color.trim() && !color) c.fail("brand_color", t("Bitte eine Farbe als Hexwert wie #176B55 eingeben.", "Please enter a colour as a hex value such as #176B55."));
  const sender_name = c.text("sender_name", d.sender_name, { label: t("den Absendernamen", "the sender name"), required: o.strict, max: 120 });
  const reply_to_email = c.email("reply_to_email", d.reply_to_email, { label: t("die Antwortadresse", "the reply address"), required: o.strict });
  const email_footer = c.text("email_footer", d.email_footer, { label: t("Fußzeile", "Footer"), max: 2000 });
  const product_name_override = c.text("product_name_override", d.product_name_override, { label: t("Portalname", "Portal name"), max: 60 });
  const locale = LOCALES.some((l) => l.key === d.locale) ? d.locale : (c.fail("locale", t("Bitte ein Zahlenformat wählen.", "Please choose a number format.")), "de-DE");
  const currency = (CURRENCIES as readonly string[]).includes(d.currency) ? d.currency : (c.fail("currency", t("Bitte die Hauswährung wählen.", "Please choose the home currency.")), "EUR");
  const currencies = [...new Set([currency, ...d.currencies.filter((x) => (CURRENCIES as readonly string[]).includes(x))])];
  const lang = d.default_document_language;
  if (o.strict && !lang) c.fail("default_document_language", t("Bitte die Sprache der Belege wählen.", "Please choose the document language."));
  const default_document_language = lang === "de" || lang === "en" ? lang : null;
  const supported = [...new Set([...(default_document_language ? [default_document_language] : []), ...d.supported_languages.filter((x) => x === "de" || x === "en")])];
  if (!supported.length) c.fail("supported_languages", t("Bitte mindestens eine Sprache wählen.", "Please choose at least one language."));
  if (!c.ok) return { values: null, errors: c.errors };
  return {
    values: { brand_color: color, sender_name, reply_to_email, email_footer, product_name_override, mascot_enabled: d.mascot_enabled, locale, currency, currencies, default_document_language, supported_languages: supported },
    errors: c.errors,
  };
}

export interface KonditionenDraft {
  commission: string;
  fee_mode: string;
  fee_percent: string;
  fee_fixed: string;
  payment_terms_days: string;
  invoice_route: string;
  invoice_number_prefix: string;
  number_template: string;
  month_check: string;
  brand_payment_terms_days: string;
  talent_payment_terms_days: string;
  due_soon_days: string;
  talent_answer_days: string;
  signing_link_days: string;
  invitation_days: string;
  holiday_region: string;
  budget_deviation_percent: string;
  min_line_description_chars: string;
  invoice_date_workdays_only: boolean;
  small_amount: string;
  expense_keywords: string;
  /** Auslagen-Rückfrage abgeschaltet (leere Liste statt Plattform-Stichworte, SAAS-R3-11) */
  expense_check_off?: boolean;
  deal_models: string[];
  revision_fee: string;
  profile_fields: string[];
  talent_payout_mode: string;
  requested_urgent_days: string;
  stale_draft_days: string;
  flat_tax: string;
}
const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));
export function validateKonditionen(d: KonditionenDraft, o: {
  strict: boolean;
  needsHolidayRegion: boolean;
  defaultModels: string[];
  modelsWereDefault: boolean;
  defaultProfileFields?: string[];
  profileWasDefault?: boolean;
}) {
  const c = new Check();
  const req = o.strict;
  const default_commission_bp = c.percentBp("commission", d.commission, { label: t("die Standardprovision", "the default commission"), required: req });
  const mode = FEE_MODES.some((m) => m.key === d.fee_mode) ? d.fee_mode : null;
  if (req && !mode) c.fail("fee_mode", t("Bitte die Art der Agenturgebühr wählen.", "Please choose the type of agency fee."));
  const agency_fee_bp = mode === "percent" ? c.percentBp("fee_percent", d.fee_percent, { label: t("die Agenturgebühr in Prozent", "the agency fee in percent"), required: true }) : null;
  const agency_fee_cents = mode === "fixed" ? c.cents("fee_fixed", d.fee_fixed, { label: t("die Agenturgebühr als Festbetrag", "the agency fee as a fixed amount"), required: true }) : null;
  const payment_terms_days = c.int("payment_terms_days", d.payment_terms_days, { label: t("das Zahlungsziel", "the payment term"), required: req, min: 0, max: 180 });
  const route = INVOICE_ROUTES.some((r) => r.key === d.invoice_route) ? d.invoice_route : null;
  if (req && !route) c.fail("invoice_route", t("Bitte den Abrechnungsweg wählen.", "Please choose the invoicing route."));
  const template = c.text("number_template", d.number_template, { label: t("Nummernmuster", "Number pattern"), max: 60 });
  const effectiveTemplate = template || DEFAULT_NUMBER_TEMPLATE;
  // Präfix ist Pflicht, sobald das Muster es verwendet: Agenturen stellen in jedem Abrechnungsweg eigene Rechnungen (Provision, Gebühr)
  const prefix = c.text("invoice_number_prefix", d.invoice_number_prefix.toUpperCase(), {
    label: t("das Präfix der Rechnungsnummer", "the invoice number prefix"), required: req && effectiveTemplate.includes("{P}"), pattern: /^[A-Z0-9-]{1,12}$/, patternText: t("Präfix: bis zu 12 Zeichen aus Großbuchstaben, Ziffern und Bindestrich.", "Prefix: up to 12 characters of capital letters, digits and hyphens."),
  });
  const unknown = template ? template.replace(NUMBER_TOKENS, "").match(/\{[^}]*\}/) : null;
  if (template && template.includes("{PREFIX}")) c.fail("number_template", t("Für das Präfix bitte {P} verwenden.", "Please use {P} for the prefix."));
  else if (unknown) c.fail("number_template", t(`Unbekannter Platzhalter ${unknown[0]}. Erlaubt sind {P}, {YYYY}, {YY}, {MM}, {N} und {N:4}.`, `Unknown placeholder ${unknown[0]}. Allowed are {P}, {YYYY}, {YY}, {MM}, {N} and {N:4}.`));
  else if (template && !/\{N(:[1-8])?\}/.test(template)) c.fail("number_template", t("Das Nummernmuster braucht den Platzhalter {N} (oder {N:4} für vier Stellen) für die laufende Nummer.", "The number pattern needs the placeholder {N} (or {N:4} for four digits) for the sequential number."));
  else if (template && (template.match(/\{N(:[1-8])?\}/g) ?? []).length > 1) c.fail("number_template", t("Die laufende Nummer {N} darf nur einmal im Muster stehen.", "The sequential number {N} may only appear once in the pattern."));
  const monthCheck = d.month_check === "off" || d.month_check === "detect" ? d.month_check : null;
  const invoice_number_scheme = template || monthCheck ? { monthCheck: monthCheck ?? "detect", ...(template ? { template } : {}) } : null;
  const days = (f: keyof KonditionenDraft, label: string, min: number, max: number) => c.int(f, d[f] as string, { label, min, max });
  const brand_payment_terms_days = days("brand_payment_terms_days", t("Zahlungsziel für neue Marken", "Payment term for new brands"), 0, 180);
  const talent_payment_terms_days = days("talent_payment_terms_days", t("Zahlungsziel im Rechnungsgenerator der Creator", "Payment term in the creators' invoice generator"), 0, 180);
  const due_soon_days = days("due_soon_days", t("Fenster „bald fällig“", "“Due soon” window"), 0, 60);
  const talent_answer_days = days("talent_answer_days", t("Wartezeit auf die Antwort des Creators", "Waiting time for the creator's reply"), 0, 60);
  const signing_link_days = days("signing_link_days", t("Laufzeit der Signierlinks", "Validity of signing links"), 1, 60);
  const invitation_days = days("invitation_days", t("Gültigkeit von Einladungen", "Validity of invitations"), 1, 60);
  const min_line_description_chars = days("min_line_description_chars", t("Mindestlänge der Leistungsbeschreibung", "Minimum length of the service description"), 0, 200);
  const budget_deviation_percent = c.decimal("budget_deviation_percent", d.budget_deviation_percent, { label: t("die Abweichung", "the deviation"), min: 0, max: 100 });
  const talent_payout_mode = PAYOUT_MODES.some((m) => m.key === d.talent_payout_mode) ? d.talent_payout_mode : null;
  const requested_urgent_days = days("requested_urgent_days", t("Dringend ab", "Urgent from"), 1, 90);
  const stale_draft_days = days("stale_draft_days", t("Liegengeblieben ab", "Stalled from"), 1, 90);
  const flat_tax_enabled = d.flat_tax === "on" ? true : d.flat_tax === "off" ? false : null;
  const region = HOLIDAY_REGIONS.some((r) => r.key === d.holiday_region) ? d.holiday_region : null;
  if (req && !region && (d.invoice_date_workdays_only || o.needsHolidayRegion)) {
    c.fail("holiday_region", t("Werktage werden genutzt: bitte die Feiertagsregion wählen.", "Working days are used: please choose the public holiday region."));
  }
  const models = DEAL_MODELS.map((m) => m.key).filter((k) => d.deal_models.includes(k));
  if (!models.length) c.fail("deal_models", t("Bitte mindestens ein Vergütungsmodell zulassen.", "Please permit at least one remuneration model."));
  const sameAsDefault = sameSet(models, o.defaultModels);
  const keywords = splitKeywords(d.expense_keywords);
  // Land ist immer Pflicht (WS-08); Reihenfolge wie in der Auswahl
  const profile = TALENT_PROFILE_FIELDS.map((f) => f.key as string).filter((k) => k === "country" || d.profile_fields.includes(k));
  const profileDefault = o.defaultProfileFields ?? [];
  const talent_profile_requirements = o.profileWasDefault && sameSet(profile, profileDefault) ? null : profile;
  if (!c.ok) return { values: null, errors: c.errors };
  return {
    values: {
      default_commission_bp, agency_fee_mode: mode, agency_fee_bp, agency_fee_cents, payment_terms_days, invoice_route: route,
      invoice_number_prefix: prefix, invoice_number_scheme, brand_payment_terms_days, talent_payment_terms_days, due_soon_days,
      talent_answer_days, signing_link_days, invitation_days, holiday_region: region, budget_deviation_percent, min_line_description_chars,
      // Aus: leere Liste (keine Rückfrage); leer gelassen: Stichworte der Plattform (SAAS-R3-11)
      invoice_date_workdays_only: d.invoice_date_workdays_only, expense_keywords: d.expense_check_off ? [] : keywords.length ? keywords : null,
      deal_models_enabled: sameAsDefault && o.modelsWereDefault ? null : models, talent_profile_requirements,
      talent_payout_mode, requested_urgent_days, stale_draft_days, flat_tax_enabled,
    },
    errors: c.errors,
  };
}

export interface PolicyDraft {
  fee_mode: string;
  flat_fee: string;
  fee_currency: string;
  interest_enabled: boolean;
  surcharge: string;
  cooldown: string;
  recheck: string;
  /** Mahnung im Auftrag des Creators mit Gebühr, Pauschale und Zinsen (0161); ohne Angabe an */
  creator_claims_costs?: boolean;
  /** CH-2 (0271): „legal“ (Regel des Landes) oder „contract“ (vertraglich vereinbarter fester Satz) */
  rate_mode?: string;
  contract_rate?: string;
  contract_basis?: string;
  /** leer = Vorgabe des Landes, „due“ ab Fälligkeit, „reminder“ ab der ersten Mahnung */
  interest_start?: string;
}
export function validatePolicy(d: PolicyDraft) {
  const c = new Check();
  const fee_mode = d.fee_mode === "cumulative" || d.fee_mode === "highest" ? d.fee_mode : null;
  const flat_fee_cents = c.cents("flat_fee", d.flat_fee, { label: t("Pauschale (einmalig)", "Flat fee (one-off)") });
  const fee_currency = (CURRENCIES as readonly string[]).includes(d.fee_currency) ? d.fee_currency : null;
  const surcharge = c.percentBp("surcharge", d.surcharge, { label: t("den Aufschlag auf den Basiszins", "the surcharge on the base rate"), required: d.interest_enabled, max: 20 });
  const payment_check_cooldown_days = c.int("cooldown", d.cooldown, { label: t("Abstand zwischen zwei Nachfragen", "Interval between two enquiries"), min: 0, max: 60 });
  const still_overdue_recheck_days = c.int("recheck", d.recheck, { label: t("Tage bis „Erneut nachfragen“", "Days until “Ask again”"), min: 0, max: 60 });
  // CH-2 (A1): vertraglich vereinbarter Satz mit Grundlage (die Datenbank prüft dasselbe)
  const contract = d.rate_mode === "contract";
  const contract_rate_bp = contract ? c.percentBp("contract_rate", d.contract_rate ?? "", { label: t("den vereinbarten Zinssatz", "the agreed interest rate"), required: true, max: 50 }) : null;
  const contract_basis = contract ? c.text("contract_basis", d.contract_basis ?? "", { label: t("die Vertragsgrundlage", "the contractual basis"), required: true, min: 3, max: 300 }) : null;
  const interest_start: "due" | "reminder" | null = d.interest_start === "due" || d.interest_start === "reminder" ? d.interest_start : null;
  if (!c.ok) return { values: null, errors: c.errors };
  return {
    values: {
      policy: { fee_mode, flat_fee_cents, fee_currency, interest_enabled: d.interest_enabled, surcharge_bp: surcharge ?? 0, creator_claims_costs: d.creator_claims_costs !== false,
        interest_rate_mode: contract ? "contract" as const : "legal" as const, contract_rate_bp, contract_basis, interest_start },
      settings: { payment_check_cooldown_days, still_overdue_recheck_days },
    },
    errors: c.errors,
  };
}

export function validateInvite(email: string, name = ""): { email: string | null; name: string | null; error: string | null; errors: Record<string, string> } {
  const c = new Check();
  const v = c.email("email", email, { label: t("die E-Mail-Adresse", "the email address"), required: true });
  const n = c.text("name", name, { label: t("den Namen", "the name"), min: 2, max: 80 });
  return { email: v, name: n, error: c.errors.email ?? null, errors: c.errors };
}


export interface ProfilDraft {
  display_name: string;
  job_title: string;
  initials: string;
  language: string;
  desktop_notifications: boolean;
}
export function validateProfil(d: ProfilDraft, o: { canEditMembership: boolean }) {
  const c = new Check();
  const display_name = o.canEditMembership ? c.text("display_name", d.display_name, { label: t("deinen Anzeigenamen", "your display name"), required: true, min: 2, max: 80 }) : null;
  const job_title = o.canEditMembership ? c.text("job_title", d.job_title, { label: t("Funktion", "Job title"), max: 80 }) : null;
  const initials = c.text("initials", d.initials.toUpperCase(), { label: t("dein Kürzel", "your initials"), max: 4, pattern: INITIALS_PATTERN, patternText: t("Kürzel: ein bis vier Buchstaben oder Ziffern.", "Initials: one to four letters or digits.") });
  const language = d.language === "de" || d.language === "en" ? d.language : null;
  if (!c.ok) return { values: null, errors: c.errors };
  return { values: { membership: o.canEditMembership ? { display_name, job_title } : null, prefs: { initials, language, desktop_notifications: d.desktop_notifications } }, errors: c.errors };
}

// ── Protokoll (audit_log) in Klartext ──────────────────────────────────────────

const AUDIT_FIELDS: Record<string, string> = {
  get role() { return t("Rolle", "Role"); }, get active() { return t("Zugang", "Access"); }, get display_name() { return t("Anzeigename", "Display name"); }, get job_title() { return t("Funktion", "Job title"); }, get sees_all_talents() { return t("Creator sehen", "See creators"); },
  get can_approve_invoices() { return t("Rechnungen freigeben", "Approve invoices"); }, get can_send_invoices() { return t("An Marke senden", "Send to brand"); }, get can_dun() { return t("Mahnen", "Send dunning notices"); },
  get can_confirm_payments() { return t("Zahlungen bestätigen", "Confirm payments"); }, get can_trigger_payouts() { return t("Auszahlungen ausführen", "Make payouts"); }, get can_sign_contracts() { return t("Verträge gegenzeichnen", "Countersign contracts"); },
  get can_edit_templates() { return t("Vorlagen bearbeiten", "Edit templates"); }, get can_edit_contract_text() { return t("Vertragstext ändern", "Change contract text"); },
  get can_view_financials() { return t("Kennzahlen und Agenturumsatz sehen", "View metrics and agency revenue"); }, get can_view_amounts() { return t("Beträge sehen", "View amounts"); }, is_owner: "Inhaber",
};
export interface AuditLike {
  table_name: string;
  action: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}
/** Eine Protokollzeile der Team-Tabellen als Satz („Kim Feld: Mahnen an“). */
export function auditText(a: AuditLike, names: { member: (membershipId: string) => string; talent: (talentId: string) => string }): string {
  const row = (a.after ?? a.before ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === "string" ? v : "");
  switch (a.table_name) {
    case "memberships": {
      const who = str(row.display_name) || t("Teammitglied", "Team member");
      if (a.action === "insert") return t(`${who} ist dem Team beigetreten.`, `${who} joined the team.`);
      if (a.action === "delete") return t(`${who} wurde aus dem Team entfernt.`, `${who} was removed from the team.`);
      const b = a.before ?? {}, n = a.after ?? {};
      const parts = Object.keys(AUDIT_FIELDS).filter((k) => JSON.stringify(b[k]) !== JSON.stringify(n[k])).map((k) => {
        const v = n[k];
        if (k === "role") return t(`Rolle ${v === "chef_manager" ? t("Admin", "Admin") : "Manager"}`, `Role ${v === "chef_manager" ? t("Admin", "Admin") : "Manager"}`);
        if (k === "active") return v ? t("Zugang wieder aktiv", "Access active again") : t("Zugang beendet", "Access ended");
        if (k === "sees_all_talents") return v ? t("sieht alle Creator", "sees all creators") : t("sieht nur zugewiesene Creator", "sees only assigned creators");
        if (typeof v === "boolean") return `${AUDIT_FIELDS[k]} ${v ? t("an", "on") : t("aus", "off")}`;
        return t(`${AUDIT_FIELDS[k]} geändert`, `${AUDIT_FIELDS[k]} changed`);
      });
      return parts.length ? `${who}: ${parts.join(", ")}.` : t(`${who}: Angaben geändert.`, `${who}: details changed.`);
    }
    case "manager_talent_assignments": {
      const m = names.member(str(row.membership_id)), tl = names.talent(str(row.talent_id));
      return a.action === "delete" ? t(`${m}: ${tl} nicht mehr zugewiesen.`, `${m}: ${tl} no longer assigned.`) : t(`${m}: ${tl} zugewiesen.`, `${m}: ${tl} assigned.`);
    }
    case "invitations": {
      const email = str(row.email);
      if (a.action === "insert") return t(`Einladung an ${email} erstellt.`, `Invitation to ${email} created.`);
      if (a.after?.accepted_at && !a.before?.accepted_at) return t(`Einladung von ${email} angenommen.`, `Invitation accepted by ${email}.`);
      if (a.after?.revoked_at && !a.before?.revoked_at) return t(`Einladung an ${email} zurückgezogen.`, `Invitation to ${email} withdrawn.`);
      // Neu gesendet heißt: neuer Link (Prüfwert oder Ablauf ändern sich); sonst wurden nur Rechte oder Name angepasst
      if (a.after?.token_hash !== a.before?.token_hash || a.after?.expires_at !== a.before?.expires_at) return t(`Einladung an ${email} erneut gesendet.`, `Invitation to ${email} resent.`);
      return t(`Einladung an ${email}: Rechte oder Name angepasst.`, `Invitation to ${email}: permissions or name adjusted.`);
    }
    case "support_access_grants":
      if (a.action === "support_session_opened") return t("Support-Sitzung begonnen (nur lesend).", "Support session started (read-only).");
      if (a.action === "insert") return t("Support-Zugang eröffnet.", "Support access opened.");
      return a.after?.revoked_at ? t("Support-Zugang beendet.", "Support access ended.") : t("Support-Zugang geändert.", "Support access changed.");
    default:
      return `${a.table_name}: ${a.action}`;
  }
}

export type InvitationState = "offen" | "angenommen" | "zurueckgezogen" | "abgelaufen";
export function invitationState(i: { accepted_at: string | null; revoked_at: string | null; expires_at: string }, nowMs: number): InvitationState {
  if (i.accepted_at) return "angenommen";
  if (i.revoked_at) return "zurueckgezogen";
  return Date.parse(i.expires_at) <= nowMs ? "abgelaufen" : "offen";
}

// ── 0345 (Entscheidung 16.10.2026, Punkt 4): Vorschlag „E-Rechnung ab 1.1.2027“ beim Feld „E-Rechnung ab“ ─────────────────────

/**
 * Hinweis unter „E-Rechnung zur Gutschrift“ (Einstellungen · Konditionen) aus dem Vorschlag der Fachlogik
 * (selfBillingEInvoiceSuggestion): Umsatz 2026 der Agentur laut CreatorOffice, Grenze 800.000 €, Knopf „Übernehmen“ nur mit
 * Vorschlag. Setzt nie selbst etwas. Immer mit dem Satz, dass Umsätze außerhalb von CreatorOffice nicht erfasst sind.
 */
export function gutschriftStichtagHinweis(s: SelfBillingEInvoiceSuggestion | null): { tone: "warning" | "info"; text: string; offer: string | null; outside: string } | null {
  if (!s) return null;
  const umsatz = money(s.turnoverCents, "EUR");
  const stand = s.complete ? t(`Umsatz ${s.year}`, `Turnover ${s.year}`) : t(`Umsatz ${s.year} bisher`, `Turnover ${s.year} so far`);
  const ausland = s.abroadCents > 0
    ? t(` Nicht mitgezählt: ${money(s.abroadCents, "EUR")} mit Leistungsort im Ausland (Reverse Charge, Drittland).`, ` Not counted: ${money(s.abroadCents, "EUR")} with place of supply abroad (reverse charge, non-EU).`) : "";
  const ohneKurs = s.unconverted > 0
    ? t(` ${s.unconverted} Rechnung(en) in anderer Währung ohne Kurs zählen nicht mit.`, ` ${s.unconverted} invoice(s) in another currency without exchange rate are not counted.`) : "";
  const outside = t(
    "Umsätze außerhalb von CreatorOffice sind nicht erfasst. Liegt der Gesamtumsatz der Agentur insgesamt über 800.000 €, gilt der 1. Januar 2027 auch dann; trage ihn in diesem Fall selbst ein.",
    "Turnover outside CreatorOffice is not included. If the agency's total turnover is above €800,000 overall, 1 January 2027 applies as well; in that case enter it yourself.");
  if (s.over && s.suggestFrom) {
    return {
      tone: "warning",
      text: t(`${stand} laut CreatorOffice: ${umsatz} netto, über 800.000 €. Bei Gutschriften zählt der Umsatz der Agentur; damit sind sie schon ab 1. Januar 2027 als E-Rechnung auszustellen. Vorschlag: E-Rechnung ab 1. Januar 2027.${ausland}${ohneKurs}`,
        `${stand} according to CreatorOffice: ${umsatz} net, above €800,000. For self-billing invoices the agency's turnover counts, so they must be issued as e-invoices from 1 January 2027. Suggestion: e-invoice from 1 January 2027.${ausland}${ohneKurs}`),
      offer: s.suggestFrom, outside,
    };
  }
  if (s.over) {
    // K-450: den eingetragenen Tag nennen statt „liegt nicht nach dem 1. Januar 2027“
    const longDate = (lang: "de" | "en") => s.currentFrom
      ? new Date(`${s.currentFrom}T00:00:00Z`).toLocaleDateString(lang === "en" ? "en-GB" : "de-DE", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }) : "";
    return { tone: "info", text: t(`${stand} laut CreatorOffice: ${umsatz} netto, über 800.000 €. Der eingetragene Tag${s.currentFrom ? ` (${longDate("de")})` : ""} passt.${ausland}${ohneKurs}`,
      `${stand} according to CreatorOffice: ${umsatz} net, above €800,000. The date entered${s.currentFrom ? ` (${longDate("en")})` : ""} fits.${ausland}${ohneKurs}`), offer: null, outside };
  }
  return { tone: "info", text: t(`${stand} laut CreatorOffice: ${umsatz} netto, Grenze 800.000 €. Bis dahin genügt die Vorgabe (1. Januar 2028).${ausland}${ohneKurs}`,
    `${stand} according to CreatorOffice: ${umsatz} net, threshold €800,000. Until then the default (1 January 2028) is enough.${ausland}${ohneKurs}`), offer: null, outside };
}

/**
 * Verständlichkeit A2-04: Rangfolge der Provision an einer Stelle, wortgleich auf den Seiten Provision, Staffel und Herkunft
 * (wie zusageSatz und die Datenbank beim Anlegen: Kooperation, Herkunft, Creator mit Staffel oder Satz, Agentur).
 */
export function welcherSatzGilt(): string {
  return t("Welcher Satz gilt? 1. ein eigener Satz an der Kooperation, 2. der Satz nach Herkunft der Kooperation, 3. die Staffel oder der Satz des Creators, 4. dieser Standard. Der Satz wird beim Anlegen der Kooperation festgehalten; spätere Änderungen gelten nur für neue Kooperationen.",
    "Which rate applies? 1. a rate set on the deal itself, 2. the rate for the deal's origin, 3. the creator's tiers or rate, 4. this default. The rate is fixed when the deal is created; later changes only apply to new deals.");
}
